import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import * as XLSX from 'xlsx';
import { Calendar, Users, Clock, Activity, Settings, LogOut, Plus, Mail, CheckCircle, XCircle, Bell, Edit, Trash2, ArrowLeft, BarChart3, TrendingUp, User, Calculator, Save, AlertCircle, Search, Eye, EyeOff, Archive, UserX, Filter, ChevronDown, ChevronUp, FileText, Upload, Download, Scissors, Image, X, CheckSquare, MapPin, PauseCircle, PlayCircle, Home } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format error messages from backend
const formatErrorMessage = (error) => {
  if (!error.response) return 'Errore di connessione';
  
  const errorData = error.response.data;
  
  // Handle validation errors (422) which can be arrays of objects
  if (error.response.status === 422 && errorData.detail) {
    if (Array.isArray(errorData.detail)) {
      // Extract messages from validation error objects
      return errorData.detail.map(err => {
        if (typeof err === 'object' && err.msg) {
          return err.msg;
        }
        return String(err);
      }).join(', ');
    } else if (typeof errorData.detail === 'object') {
      // Handle single validation error object
      return errorData.detail.msg || JSON.stringify(errorData.detail);
    }
    return String(errorData.detail);
  }
  
  // Handle other error types
  if (errorData.detail) {
    return String(errorData.detail);
  }
  
  return `Errore ${error.response.status}`;
};

// Configure axios defaults
axios.defaults.headers.common['Authorization'] = localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null;

// Logout Warning Modal Component with countdown - FIXED VERSION
const LogoutWarningModal = ({ onExtendSession, onLogout }) => {
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else {
      onLogout();
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [countdown, onLogout]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center mb-4">
          <AlertCircle className="h-6 w-6 text-amber-500 mr-2" />
          <h3 className="text-lg font-semibold text-slate-800">⚠️ Sessione in Scadenza</h3>
        </div>
        <p className="text-slate-600 mb-6">
          La tua sessione scadrà tra <strong className="text-red-600">{countdown} secondi</strong> per inattività. 
          Clicca "Rimani Connesso" per continuare.
        </p>
        <div className="flex space-x-3">
          <button
            onClick={onExtendSession}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            ✅ Rimani Connesso
          </button>
          <button
            onClick={onLogout}
            className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors font-medium"
          >
            🚪 Esci Ora
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [logoutTimer, setLogoutTimer] = useState(null);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Set initial activity timestamp
        updateLastActivity();
        
        // Check if user was inactive while app was closed
        checkInactivityOnFocus();
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivity');
      }
    }
  }, []);

  // Setup inactivity timer when user is logged in - SIMPLIFIED VERSION
  useEffect(() => {
    if (!user) return;

    console.log('Setting up inactivity timer for user:', user.username);
    
    let activityTimer = null;
    
    // Start initial timer
    const startTimer = () => {
      if (activityTimer) {
        clearTimeout(activityTimer);
      }
      
      console.log('Starting 14 minute timer');
      activityTimer = setTimeout(() => {
        console.log('14 minutes passed - showing warning');
        setShowLogoutWarning(true);
      }, 14 * 60 * 1000); // 14 minutes
    };
    
    // Reset timer on activity
    const resetTimer = () => {
      console.log('Activity detected - resetting timer');
      updateLastActivity();
      
      // Don't reset if warning is showing or logging out
      if (showLogoutWarning || isLoggingOut) {
        console.log('Timer reset blocked - warning showing or logging out');
        return;
      }
      
      startTimer();
    };

    // Event listeners
    const events = ['click', 'keydown', 'mousemove', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Start initial timer
    startTimer();

    // Cleanup
    return () => {
      console.log('Cleaning up timer for user');
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      if (activityTimer) {
        clearTimeout(activityTimer);
      }
    };
  }, [user]); // Only depend on user, not on state variables

  // Separate useEffect to handle timer cleanup when warning shows
  useEffect(() => {
    if (showLogoutWarning && logoutTimer) {
      console.log('Warning shown - clearing main activity timer');
      clearTimeout(logoutTimer);
      setLogoutTimer(null);
    }
  }, [showLogoutWarning]);

  const logout = () => {
    // Prevent multiple logout calls
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity'); // Remove activity timestamp
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    setCurrentPage('dashboard');
    
    // Clear any existing timers
    if (logoutTimer) {
      clearTimeout(logoutTimer);
      setLogoutTimer(null);
    }
    setShowLogoutWarning(false);
    
    // Reset logout flag after a short delay
    setTimeout(() => setIsLoggingOut(false), 1000);
  };

  const updateLastActivity = () => {
    const now = Date.now();
    localStorage.setItem('lastActivity', now.toString());
  };

  const checkInactivityOnFocus = () => {
    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity || !user) return;
    
    const now = Date.now();
    const timeDiff = now - parseInt(lastActivity);
    const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds
    
    if (timeDiff >= fifteenMinutes) {
      // User has been inactive for 15+ minutes, force logout
      setShowLogoutWarning(false);
      logout();
      return true;
    }
    return false;
  };

  const extendSession = () => {
    console.log('extendSession called - hiding warning and restarting app');
    setShowLogoutWarning(false);
    
    // Force a page reload to restart the timer system cleanly
    window.location.reload();
  };

  const handleAutoLogout = () => {
    console.log('handleAutoLogout called');
    setShowLogoutWarning(false);
    logout();
  };

  // Check if we're on the reset password page (via hash routing)
  const hash = window.location.hash;
  if (hash && hash.startsWith('#reset-password/')) {
    const resetToken = hash.replace('#reset-password/', '');
    return <ResetPasswordScreen token={resetToken} />;
  }

  if (!user) {
    return <LoginScreen setUser={setUser} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b border-slate-200/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 xl:px-8">
          <div className="flex justify-between items-center h-12 sm:h-16">
            <div className="flex items-center space-x-1 sm:space-x-3">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-1 sm:p-2 rounded-lg">
                <Calendar className="h-3 w-3 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-sm sm:text-xl font-bold text-slate-800">Int-REA-Net</h1>
                <p className="text-xs text-slate-600 hidden sm:block">Benvenuto, {user.username}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-1 sm:space-x-4">
              <div className="flex items-center space-x-1 text-xs sm:text-sm text-slate-600 bg-slate-100 px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full">
                <div className={`w-1 h-1 sm:w-2 sm:h-2 rounded-full ${
                  user.role === 'admin' ? 'bg-purple-500' : 
                  user.role === 'consulente' ? 'bg-orange-500' :
                  user.role === 'responsabile_unit' ? 'bg-blue-500' :
                  user.unit === 'Agenti' ? 'bg-red-500' :
                  'bg-green-500'
                }`} />
                <span className="hidden sm:inline">
                  {user.role === 'admin' ? 'Amministratore' : 
                   user.role === 'consulente' ? 'Consulente' :
                   user.role === 'responsabile_unit' ? 'Responsabile Unit' :
                   user.unit === 'Agenti' ? 'Agente' :
                   'Dipendente'}
                </span>
                <span className="sm:hidden">
                  {user.role === 'admin' ? 'Admin' : 
                   user.role === 'consulente' ? 'Cons.' :
                   user.role === 'responsabile_unit' ? 'Resp.' :
                   user.unit === 'Agenti' ? 'Agent.' :
                   'Dip.'}
                </span>
              </div>
              <button
                onClick={logout}
                className="flex items-center space-x-1 text-slate-600 hover:text-red-600 transition-colors"
              >
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline text-sm">Esci</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Auto-logout Warning Modal */}
      {showLogoutWarning && !isLoggingOut && (
        <LogoutWarningModal 
          onExtendSession={extendSession}
          onLogout={handleAutoLogout}
        />
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {user.role === 'admin' ? (
          <AdminDashboard currentPage={currentPage} setCurrentPage={setCurrentPage} user={user} />
        ) : user.role === 'consulente' ? (
          <ConsulenteDashboard currentPage={currentPage} setCurrentPage={setCurrentPage} user={user} />
        ) : user.role === 'responsabile_unit' ? (
          <ResponsabileUnitDashboard currentPage={currentPage} setCurrentPage={setCurrentPage} user={user} />
        ) : user.unit === 'Agenti' ? (
          <AgentiDashboard currentPage={currentPage} setCurrentPage={setCurrentPage} user={user} />
        ) : (
          <EmployeeDashboard currentPage={currentPage} setCurrentPage={setCurrentPage} user={user} />
        )}
      </div>
    </div>
  );
}

// Login Screen Component
const LoginScreen = ({ setUser }) => {
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/login`, loginData);
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('lastActivity', Date.now().toString()); // Set activity timestamp
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(user);
    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      const response = await axios.post(`${API}/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      setForgotPasswordMessage(response.data.message);
      setForgotPasswordEmail('');
    } catch (error) {
      setForgotPasswordMessage(error.response?.data?.detail || 'Errore nell\'invio dell\'email');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center p-3 sm:p-4 lg:p-6">
      <div className="bg-white/95 backdrop-blur-md p-4 sm:p-6 lg:p-8 rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 sm:p-4 rounded-2xl mx-auto w-fit mb-3 sm:mb-4">
            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">Int-REA-Net</h1>
          <p className="text-sm sm:text-base text-slate-600">Accedi al tuo account</p>
        </div>

        {!showForgotPassword ? (
          <>
            <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Username</label>
                <input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base"
                  placeholder="Inserisci username"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base"
                  placeholder="Inserisci password"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all transform hover:scale-[1.02] text-sm sm:text-base"
              >
                {loading ? 'Accesso in corso...' : 'Accedi'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => setShowForgotPassword(true)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
              >
                Password dimenticata?
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-6">
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordMessage('');
                  setForgotPasswordEmail('');
                }}
                className="flex items-center text-slate-600 hover:text-slate-800 text-sm transition-colors mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Torna al login
              </button>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Password dimenticata</h2>
              <p className="text-slate-600 text-sm">
                Inserisci la tua email per ricevere le istruzioni per reimpostare la password
              </p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base"
                  placeholder="Inserisci la tua email"
                  required
                />
              </div>

              {forgotPasswordMessage && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">
                  {forgotPasswordMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={forgotPasswordLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all transform hover:scale-[1.02] text-sm sm:text-base"
              >
                {forgotPasswordLoading ? 'Invio in corso...' : 'Invia email di reset'}
              </button>
            </form>
          </>
        )}

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      </div>
    </div>
  );
};

// Reset Password Screen Component
const ResetPasswordScreen = ({ token }) => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Le password non corrispondono');
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.newPassword.length < 6) {
      setError('La password deve essere almeno 6 caratteri');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/reset-password`, {
        token: token,
        new_password: formData.newPassword
      });
      
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nel reset della password');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    window.location.href = '/';
    // Also clear the hash to ensure clean navigation
    window.location.hash = '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center p-3 sm:p-4 lg:p-6">
      <div className="bg-white/95 backdrop-blur-md p-4 sm:p-6 lg:p-8 rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 sm:p-4 rounded-2xl mx-auto w-fit mb-3 sm:mb-4">
            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">Reset Password</h1>
          <p className="text-sm sm:text-base text-slate-600">Imposta la tua nuova password</p>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nuova Password</label>
              <input
                type="password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base"
                placeholder="Inserisci nuova password"
                required
                minLength="6"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Conferma Password</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base"
                placeholder="Conferma nuova password"
                required
                minLength="6"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 inline mr-2" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all transform hover:scale-[1.02] text-sm sm:text-base"
            >
              {loading ? 'Aggiornamento in corso...' : 'Aggiorna Password'}
            </button>
          </form>
        ) : (
          <div className="text-center space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-6 rounded-lg">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <h3 className="text-lg font-semibold mb-2">Password Aggiornata!</h3>
              <p className="text-sm">
                La tua password è stata aggiornata con successo. Ora puoi effettuare il login con la nuova password.
              </p>
            </div>
            
            <button
              onClick={handleGoToLogin}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-all transform hover:scale-[1.02] text-sm sm:text-base"
            >
              Vai al Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Consulente Dashboard Component
const ConsulenteDashboard = ({ currentPage, setCurrentPage, user }) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Navigation */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-wrap gap-1.5 sm:gap-3">
          <button
            onClick={() => setCurrentPage('payslips')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'payslips' 
                ? 'bg-orange-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Documenti Dipendente</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('conventions')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'conventions' 
                ? 'bg-orange-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Image className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Convenzioni</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('communications')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'communications' 
                ? 'bg-orange-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Comunicazioni</span>
          </button>

          {/* Campaign Reports button only for Digital unit */}
          {user.unit === "Digital" && (
            <button
              onClick={() => setCurrentPage('campaign-reports')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'campaign-reports' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Report Campagne</span>
            </button>
          )}

          {/* Impostazioni button for all users */}
          <button
            onClick={() => setCurrentPage('settings')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'settings' 
                ? 'bg-orange-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Impostazioni</span>
          </button>
        </div>
      </div>

      {/* Content based on current page */}
      {currentPage === 'payslips' && <PayslipManagement currentUser={user} />}
      {currentPage === 'conventions' && user.unit === 'Digital' && <ConventionsManager user={user} />}
      {currentPage === 'conventions' && user.unit !== 'Digital' && <ConventionsGallery user={user} />}
      {currentPage === 'communications' && <CompanyCommunications user={user} />}
      {currentPage === 'settings' && <PasswordSettings user={user} />}
      {currentPage === 'campaign-reports' && user.unit === 'Digital' && <CampaignReports user={user} />}
    </div>
  );
};

// Agenti Dashboard Component (Only Room Booking)
const AgentiDashboard = ({ currentPage, setCurrentPage, user }) => {
  // Set default page to room-booking for Agenti
  useEffect(() => {
    setCurrentPage('room-booking');
  }, [setCurrentPage]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-pink-600 p-4 sm:p-6 rounded-xl text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold mb-2">Dashboard Agenti</h2>
            <p className="text-red-100 text-sm sm:text-base">
              Gestione Account e Impostazioni
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setCurrentPage('settings')}
              className={`p-2 rounded-lg transition-colors ${
                currentPage === 'settings' 
                  ? 'bg-red-700 text-white' 
                  : 'bg-red-400/50 text-red-100 hover:bg-red-400/70'
              }`}
              title="Impostazioni"
            >
              <Settings className="h-5 w-5" />
            </button>
            <User className="h-8 w-8 sm:h-12 sm:w-12 text-red-200" />
          </div>
        </div>
      </div>

      {/* Content - Only Settings available */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="text-center">
          <User className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Benvenuto!</h3>
          <p className="text-slate-600 mb-4">
            Usa il pulsante Impostazioni per gestire il tuo account e cambiare la password.
          </p>
          <button
            onClick={() => setCurrentPage('settings')}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Settings className="h-4 w-4" />
            <span>Vai a Impostazioni</span>
          </button>
        </div>
      </div>

      {/* Settings Modal */}
      {currentPage === 'settings' && (
        <PasswordSettings user={user} />
      )}
    </div>
  );
};

// Responsabile Unit Dashboard Component
const ResponsabileUnitDashboard = ({ currentPage, setCurrentPage, user }) => {
  // Simplified dashboard only for "Agenti" unit
  if (user.unit === "Agenti") {
    // Agenti ResponsabileUnit should have room booking access
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 sm:p-6 rounded-xl text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold mb-2">Dashboard Responsabile Unit - Agenti</h2>
              <p className="text-blue-100 text-sm sm:text-base">
                Gestione Prenotazioni e Impostazioni
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentPage('room-booking')}
                className={`p-2 rounded-lg transition-colors ${
                  currentPage === 'room-booking' 
                    ? 'bg-blue-700 text-white' 
                    : 'bg-blue-400/50 text-blue-100 hover:bg-blue-400/70'
                }`}
                title="Prenotazione Sala"
              >
                <Calendar className="h-5 w-5" />
              </button>
              <button
                onClick={() => setCurrentPage('settings')}
                className={`p-2 rounded-lg transition-colors ${
                  currentPage === 'settings' 
                    ? 'bg-blue-700 text-white' 
                    : 'bg-blue-400/50 text-blue-100 hover:bg-blue-400/70'
                }`}
                title="Impostazioni"
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {currentPage === 'settings' ? (
          <PasswordSettings user={user} />
        ) : (
          <RoomBookingSystem user={user} />
        )}
      </div>
    );
  }

  // Full dashboard for all other units
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Navigation */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-wrap gap-1.5 sm:gap-3">
          <button
            onClick={() => setCurrentPage('unit-approvals')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'unit-approvals' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <CheckSquare className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Approvazioni</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('my-requests')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'my-requests' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Le mie Richieste</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('new-request')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'new-request' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Nuova Richiesta</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('stats')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'stats' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Statistiche</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('payslips')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'payslips' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Documenti Dipendente</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('conventions')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'conventions' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Image className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Convenzioni</span>
          </button>

          {/* Comunicazioni button for all except Agenti unit */}
          {user.unit !== "Agenti" && (
            <button
              onClick={() => setCurrentPage('communications')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'communications' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Comunicazioni</span>
            </button>
          )}

          {/* Campaign Reports button for ResponsabileUnit (read-only, except Agenti) */}
          {user.unit !== "Agenti" && user.role === "responsabile_unit" && (
            <button
              onClick={() => setCurrentPage('campaign-reports')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'campaign-reports' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Report</span>
            </button>
          )}

          {/* Scadenzario button only for Amministrazione unit */}
          {user.unit === "Amministrazione" && (
            <button
              onClick={() => setCurrentPage('deadlines')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'deadlines' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Scadenzario</span>
            </button>
          )}

          {/* CEO Appointments button only for CEO unit */}
          {user.unit === "CEO" && (
            <button
              onClick={() => setCurrentPage('ceo-appointments')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'ceo-appointments' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Appuntamenti</span>
            </button>
          )}

          {/* Solar Installations button for CEO and Fotovoltaico/Fibra units */}
          {(user.unit === "CEO" || user.unit === "Fotovoltaico" || user.unit === "Fibra") && (
            <button
              onClick={() => setCurrentPage('solar-installations')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'solar-installations' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Installazioni</span>
            </button>
          )}
          
          <button
            onClick={() => setCurrentPage('room-booking')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'room-booking' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Prenotazione Sala</span>
          </button>

          {/* Impostazioni button for all users */}
          <button
            onClick={() => setCurrentPage('settings')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'settings' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Impostazioni</span>
          </button>
        </div>
      </div>

      {/* Content based on current page */}
      {currentPage === 'unit-approvals' && <UnitApprovalDashboard user={user} />}
      {currentPage === 'my-requests' && <ResponsabileUnitMyRequests />}
      {currentPage === 'new-request' && <NewRequestForm onSuccess={() => setCurrentPage('my-requests')} />}
      {currentPage === 'stats' && <EmployeeStats />}
      {currentPage === 'payslips' && <EmployeePayslips />}
      {currentPage === 'conventions' && user.unit === 'Digital' && <ConventionsManager user={user} />}
      {currentPage === 'conventions' && user.unit !== 'Digital' && <ConventionsGallery user={user} />}
      {currentPage === 'communications' && user.unit !== "Agenti" && <CompanyCommunications user={user} />}
      {currentPage === 'campaign-reports' && user.unit !== "Agenti" && <CampaignReports user={user} />}
      {currentPage === 'deadlines' && user.unit === "Amministrazione" && <DeadlineScheduler user={user} />}
      {currentPage === 'ceo-appointments' && user.unit === "CEO" && <CEOAppointments user={user} />}
      {currentPage === 'solar-installations' && (user.unit === "CEO" || user.unit === "Fotovoltaico" || user.unit === "Fibra") && <SolarInstallations user={user} />}
      {currentPage === 'room-booking' && <RoomBookingSystem user={user} />}
      {currentPage === 'settings' && <PasswordSettings user={user} />}
    </div>
  );
};

// Unit Approval Dashboard Component
const UnitApprovalDashboard = ({ user }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [processedRequests, setProcessedRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [unitNotes, setUnitNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/requests`);
      const allRequests = response.data;
      
      // Filter for unit-related requests only (backend already filters, but we filter for UI display)
      const unitRequests = allRequests.filter(request => {
        // Show requests from employees of this unit (for approval) or own requests (for viewing)
        return request.user_id !== user.id; // Exclude own requests from approval section
      });

      console.log(`Found ${unitRequests.length} requests for unit ${user.unit} approval`);
      
      setPendingRequests(unitRequests.filter(req => req.status === 'pending'));
      setProcessedRequests(unitRequests.filter(req => req.status !== 'pending'));
      
    } catch (error) {
      console.error('Errore nel caricamento delle richieste:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAction = async (action) => {
    if (!selectedRequest) return;

    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/requests/${selectedRequest.id}`, {
        request_id: selectedRequest.id,
        action: action,
        notes: unitNotes
      });

      alert(`Richiesta ${action === 'approve' ? 'pre-approvata' : 'rifiutata'} con successo`);
      
      // Reset form
      setSelectedRequest(null);
      setUnitNotes('');
      
      // Reload requests
      await loadRequests();
      
    } catch (error) {
      console.error('Errore nell\'azione:', error);
      alert(error.response?.data?.detail || 'Errore nell\'operazione');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'In Attesa', icon: Clock },
      unit_approved: { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'Pre-approvata', icon: CheckCircle },
      approved: { color: 'bg-green-100 text-green-800 border-green-200', text: 'Approvata', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', text: 'Rifiutata', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Unit Info */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 sm:p-6 rounded-xl text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold mb-2">
              Gestione Approvazioni - {user.units && user.units.length > 1 ? 'Unità' : 'Unità'}: {
                user.units && user.units.length > 0 
                  ? user.units.join(', ') 
                  : user.unit || 'N/A'
              }
            </h2>
            <p className="text-blue-100 text-sm sm:text-base">
              Richieste in attesa: {pendingRequests.length}
            </p>
          </div>
          <Users className="h-8 w-8 sm:h-12 sm:w-12 text-blue-200" />
        </div>
      </div>

      {/* Pending Requests */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
          <Bell className="h-5 w-5 mr-2 text-orange-500" />
          Richieste da Approvare ({pendingRequests.length})
        </h3>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-slate-500 mt-2">Caricamento...</p>
          </div>
        ) : pendingRequests.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-slate-500">Nessuna richiesta in attesa nella tua unità</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingRequests.map((request) => (
              <div key={request.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-slate-800 capitalize">{request.type}</h4>
                    <p className="text-slate-600 text-sm">
                      {request.user?.username} ({request.user?.email})
                    </p>
                    <p className="text-slate-500 text-sm">
                      Richiesta del {formatDate(request.created_at)}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Gestisci
                    </button>
                  </div>
                </div>
                
                {/* Request Details */}
                <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded">
                  {request.type === 'ferie' && (
                    <p>Dal {formatDate(request.start_date)} al {formatDate(request.end_date)}</p>
                  )}
                  {request.type === 'permesso' && (
                    <p>{formatDate(request.permit_date)} dalle {request.start_time} alle {request.end_time}</p>
                  )}
                  {request.type === 'malattia' && (
                    <p>Dal {formatDate(request.sick_start_date)} per {request.sick_days} giorni - Protocollo: {request.protocol_code}</p>
                  )}
                  {request.type === 'smartworking' && (
                    <p>Data: {formatDate(request.smartworking_date)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Processed Requests */}
      {processedRequests.length > 0 && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            Richieste Elaborate ({processedRequests.length})
          </h3>
          
          <div className="space-y-3">
            {processedRequests.slice(0, 5).map((request) => (
              <div key={request.id} className="border border-slate-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium capitalize">{request.type}</span>
                    <span className="text-slate-600 ml-2">- {request.user?.username}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(request.status)}
                    <span className="text-slate-500 text-sm">{formatDate(request.updated_at || request.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request Management Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Gestione Richiesta - {selectedRequest.type}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-slate-600">
                <strong>Dipendente:</strong> {selectedRequest.user?.username}
              </p>
              <p className="text-sm text-slate-600">
                <strong>Data richiesta:</strong> {formatDate(selectedRequest.created_at)}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Note (opzionali)
              </label>
              <textarea
                value={unitNotes}
                onChange={(e) => setUnitNotes(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Aggiungi note per il dipendente..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => handleRequestAction('approve')}
                disabled={submitting}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? 'Elaborando...' : 'Pre-Approva'}
              </button>
              
              <button
                onClick={() => handleRequestAction('reject')}
                disabled={submitting}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? 'Elaborando...' : 'Rifiuta'}
              </button>
              
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setUnitNotes('');
                }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Responsabile Unit My Requests Component  
const ResponsabileUnitMyRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await axios.get(`${API}/requests`);
      // Filter only own requests (exclude unit employee requests)
      const ownRequests = response.data.filter(req => req.user_id === JSON.parse(localStorage.getItem('user')).id);
      setRequests(ownRequests);
    } catch (error) {
      console.error('Errore nel caricamento delle richieste:', error);
    }
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/requests/${requestId}`);
      loadRequests(); // Ricarica la lista
    } catch (error) {
      console.error('Errore nella cancellazione:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'unit_approved': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'In attesa';
      case 'unit_approved': return 'Pre-approvata';
      case 'approved': return 'Approvata';
      case 'rejected': return 'Rifiutata';
      default: return status;
    }
  };

  return (
    <div className="space-y-4">
      {editingRequest && (
        <EditRequestForm
          request={editingRequest}
          onSuccess={() => {
            setEditingRequest(null);
            loadRequests();
          }}
          onCancel={() => setEditingRequest(null)}
        />
      )}
      
      {!editingRequest && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6">Le mie richieste</h2>
          
          {requests.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-slate-300 mx-auto mb-3 sm:mb-4" />
              <p className="text-slate-500 text-base sm:text-lg">Nessuna richiesta ancora</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {requests.map((request) => (
                <div key={request.id} className="bg-slate-50 rounded-lg p-3 sm:p-4 border border-slate-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col xs:flex-row xs:items-center gap-1 xs:gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(request.status)}`}>
                          {getStatusText(request.status)}
                        </span>
                        <span className="text-sm font-medium text-slate-700 capitalize">
                          {request.type}
                        </span>
                      </div>
                      
                      <div className="text-xs sm:text-sm text-slate-600 space-y-0.5">
                        {request.type === 'ferie' && (
                          <p>Dal {new Date(request.start_date).toLocaleDateString('it-IT')} al {new Date(request.end_date).toLocaleDateString('it-IT')}</p>
                        )}
                        {request.type === 'permesso' && (
                          <p>{new Date(request.permit_date).toLocaleDateString('it-IT')} dalle {request.start_time} alle {request.end_time}</p>
                        )}
                        {request.type === 'malattia' && (
                          <p>Dal {new Date(request.sick_start_date).toLocaleDateString('it-IT')} - {request.sick_days} giorni</p>
                        )}
                        {request.type === 'smartworking' && (
                          <p>Data: {new Date(request.smartworking_date).toLocaleDateString('it-IT')}</p>
                        )}
                        <p className="text-xs text-slate-500">
                          Creata: {new Date(request.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                    </div>
                    
                    {request.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditRequest(request)}
                          className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Modifica richiesta"
                        >
                          <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRequest(request.id)}
                          className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Cancella richiesta"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Responsabile Unit Stats Component (complete like Employee)
const ResponsabileUnitStats = () => {
  const [stats, setStats] = useState(null);
  const [vacationSummary, setVacationSummary] = useState(null);
  const [permitSummary, setPermitSummary] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadYears();
    loadVacationSummary();
    loadPermitSummary();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      loadStats();
    }
  }, [selectedYear]);

  const loadYears = async () => {
    try {
      const response = await axios.get(`${API}/years`);
      setYears(response.data.years);
    } catch (error) {
      console.error('Errore nel caricamento degli anni:', error);
    }
  };

  const loadVacationSummary = async () => {
    try {
      const response = await axios.get(`${API}/vacation-summary`);
      setVacationSummary(response.data);
    } catch (error) {
      console.error('Errore nel caricamento del riepilogo ferie:', error);
    }
  };

  const loadPermitSummary = async () => {
    try {
      const response = await axios.get(`${API}/permit-summary`);
      setPermitSummary(response.data);
    } catch (error) {
      console.error('Errore nel caricamento del riepilogo permessi:', error);
    }
  };

  const loadStats = async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stats/${selectedYear}`);
      setStats(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle statistiche:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800">Le Mie Statistiche</h2>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-slate-700">Anno:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Vacation Summary */}
      {vacationSummary && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-3 sm:p-6 rounded-xl text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-3 sm:mb-0">
              <h3 className="text-base sm:text-xl font-semibold mb-2">Riepilogo Ferie Totali</h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <p className="text-emerald-200">Ferie Rimanenti Totali</p>
                  <p className="text-lg sm:text-2xl font-bold">{vacationSummary.total_remaining_days}</p>
                  <p className="text-xs text-emerald-200">giorni disponibili</p>
                </div>
                <div>
                  <p className="text-emerald-200">Usate Quest'Anno</p>
                  <p className="text-lg sm:text-2xl font-bold">{vacationSummary.used_this_year}</p>
                  <p className="text-xs text-emerald-200">giorni nel {vacationSummary.current_year}</p>
                </div>
                <div>
                  <p className="text-emerald-200">Anni con Ferie</p>
                  <p className="text-lg sm:text-2xl font-bold">{vacationSummary.total_years}</p>
                  <p className="text-xs text-emerald-200">anni registrati</p>
                </div>
              </div>
            </div>
            <Calculator className="h-8 w-8 sm:h-16 sm:w-16 text-emerald-200 self-center sm:self-auto" />
          </div>
        </div>
      )}

      {/* Vacation Details by Year */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4 flex items-center">
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-emerald-600" />
          Dettaglio Ferie per Anno
        </h3>
        
        {stats && stats.vacation_details && stats.vacation_details.length > 0 ? (
          <div className="space-y-3">
            {stats.vacation_details.map((detail, index) => (
              <div key={index} className="bg-emerald-50 p-3 sm:p-4 rounded-lg border border-emerald-200">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-emerald-800">Anno {detail.year}</h4>
                  <span className="px-2 py-1 bg-emerald-200 text-emerald-800 rounded text-xs font-medium">
                    Ferie Disponibili
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <p className="text-emerald-600">Massime:</p>
                    <p className="font-bold text-emerald-800">{detail.max_days} giorni</p>
                  </div>
                  <div>
                    <p className="text-emerald-600">Utilizzate:</p>
                    <p className="font-bold text-emerald-800">{detail.used_days} giorni</p>
                  </div>
                  <div>
                    <p className="text-emerald-600">Riportate:</p> 
                    <p className="font-bold text-emerald-800">{detail.carried_over} giorni</p>
                  </div>
                  <div>
                    <p className="text-emerald-600">Rimanenti:</p>
                    <p className="font-bold text-emerald-800">{detail.remaining_days} giorni</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4">Nessun dato disponibile per l'anno {selectedYear}</p>
        )}
      </div>

      {/* Permit Summary */}
      {permitSummary && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-600 p-3 sm:p-6 rounded-xl text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-3 sm:mb-0">
              <h3 className="text-base sm:text-xl font-semibold mb-2">Riepilogo Permessi Totali</h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <p className="text-orange-200">Ore Rimanenti Totali</p>
                  <p className="text-lg sm:text-2xl font-bold">{permitSummary.total_remaining_hours}</p>
                  <p className="text-xs text-orange-200">ore disponibili</p>
                </div>
                <div>
                  <p className="text-orange-200">Usate Quest'Anno</p>
                  <p className="text-lg sm:text-2xl font-bold">{permitSummary.used_this_year}</p>
                  <p className="text-xs text-orange-200">ore nel {permitSummary.current_year}</p>
                </div>
                <div>
                  <p className="text-orange-200">Accrual Mensile</p>
                  <p className="text-lg sm:text-2xl font-bold">2.5 <span className="text-sm">ore/mese</span></p>
                  <p className="text-xs text-orange-200">accrual automatico</p>
                </div>
              </div>
            </div>
            <Clock className="h-8 w-8 sm:h-16 sm:w-16 text-orange-200 self-center sm:self-auto" />
          </div>
        </div>
      )}

      {/* Permit Details by Year */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4 flex items-center">
          <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-orange-600" />
          Dettaglio Permessi per Anno
        </h3>
        
        {stats && stats.permit_details && stats.permit_details.length > 0 ? (
          <div className="space-y-3">
            {stats.permit_details.map((detail, index) => (
              <div key={index} className="bg-orange-50 p-3 sm:p-4 rounded-lg border border-orange-200">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-orange-800">Anno {detail.year}</h4>
                  <span className="px-2 py-1 bg-orange-200 text-orange-800 rounded text-xs font-medium">
                    Permessi Orari
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <p className="text-orange-600">Massime:</p>
                    <p className="font-bold text-orange-800">{detail.max_hours} ore</p>
                  </div>
                  <div>
                    <p className="text-orange-600">Utilizzate:</p>
                    <p className="font-bold text-orange-800">{detail.used_hours} ore</p>
                  </div>
                  <div>
                    <p className="text-orange-600">Riportate:</p>
                    <p className="font-bold text-orange-800">{detail.carried_over} ore</p>
                  </div>
                  <div>
                    <p className="text-orange-600">Rimanenti:</p>
                    <p className="font-bold text-orange-800">{detail.remaining_hours} ore</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4">Nessun dato disponibile per l'anno {selectedYear}</p>
        )}
      </div>

      {/* Requests Summary */}
      {stats && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4 flex items-center">
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600" />
            Riepilogo Richieste {selectedYear}
          </h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-blue-50 p-3 rounded-lg text-center border border-blue-200">
              <p className="text-blue-600 text-xs sm:text-sm">Ferie</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-800">{stats.vacation_requests || 0}</p>
              <p className="text-xs text-blue-600">richieste</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center border border-green-200">
              <p className="text-green-600 text-xs sm:text-sm">Permessi</p>
              <p className="text-lg sm:text-2xl font-bold text-green-800">{stats.permit_requests || 0}</p>
              <p className="text-xs text-green-600">richieste</p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg text-center border border-red-200">
              <p className="text-red-600 text-xs sm:text-sm">Malattia</p>
              <p className="text-lg sm:text-2xl font-bold text-red-800">{stats.sick_requests || 0}</p>
              <p className="text-xs text-red-600">richieste</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg text-center border border-slate-200">
              <p className="text-slate-600 text-xs sm:text-sm">Totale</p>
              <p className="text-lg sm:text-2xl font-bold text-slate-800">
                {(stats.vacation_requests || 0) + (stats.permit_requests || 0) + (stats.sick_requests || 0)}
              </p>
              <p className="text-xs text-slate-600">richieste</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Employee Dashboard Component (Only Room Booking)
const EmployeeDashboard = ({ currentPage, setCurrentPage, user }) => {
  // Simplified dashboard only for "Agenti" unit
  if (user.unit === "Agenti") {
    // Set default page to room-booking for Agenti unit
    useEffect(() => {
      setCurrentPage('room-booking');
    }, [setCurrentPage]);

    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-4 sm:p-6 rounded-xl text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold mb-2">Dashboard Dipendente - Agenti</h2>
              <p className="text-emerald-100 text-sm sm:text-base">
                Prenotazione Sala Riunioni
              </p>
            </div>
            <Calendar className="h-8 w-8 sm:h-12 sm:w-12 text-emerald-200" />
          </div>
        </div>

        {/* Room Booking System - Always Displayed */}
        <RoomBookingSystem user={user} />
      </div>
    );
  }

  // Full dashboard for all other units
  const [showRequestForm, setShowRequestForm] = useState(false);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Navigation */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-wrap gap-1.5 sm:gap-3">
          <button
            onClick={() => {setCurrentPage('requests'); setShowRequestForm(false);}}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'requests' && !showRequestForm
                ? 'bg-emerald-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Le mie Richieste</span>
          </button>
          
          <button
            onClick={() => {setCurrentPage('requests'); setShowRequestForm(true);}}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'requests' && showRequestForm
                ? 'bg-emerald-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Nuova Richiesta</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('stats')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'stats' 
                ? 'bg-emerald-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Statistiche</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('payslips')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'payslips' 
                ? 'bg-emerald-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Documenti Dipendente</span>
          </button>

          {/* Comunicazioni button for all except Agenti unit */}
          {user.unit !== "Agenti" && (
            <button
              onClick={() => setCurrentPage('communications')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'communications' 
                  ? 'bg-emerald-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Comunicazioni</span>
            </button>
          )}

          {/* Convenzioni button for all except Agenti unit */}
          {user.unit !== "Agenti" && (
            <button
              onClick={() => setCurrentPage('conventions')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'conventions' 
                  ? 'bg-emerald-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <CheckSquare className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Convenzioni</span>
            </button>
          )}

          {/* Campaign Reports button only for Digital unit employees */}
          {user.unit === "Digital" && (
            <button
              onClick={() => setCurrentPage('campaign-reports')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'campaign-reports' 
                  ? 'bg-emerald-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Report Campagne</span>
            </button>
          )}

          {/* Scadenzario button only for Amministrazione unit */}
          {user.unit === "Amministrazione" && (
            <button
              onClick={() => setCurrentPage('deadlines')}
              className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
                currentPage === 'deadlines' 
                  ? 'bg-emerald-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Scadenzario</span>
            </button>
          )}

          {/* Impostazioni button for all users */}
          <button
            onClick={() => setCurrentPage('settings')}
            className={`flex items-center space-x-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-base ${
              currentPage === 'settings' 
                ? 'bg-emerald-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Impostazioni</span>
          </button>
        </div>
      </div>

      {/* Content based on current page */}
      {currentPage === 'requests' && !showRequestForm && <ResponsabileUnitMyRequests />}
      {currentPage === 'requests' && showRequestForm && <NewRequestForm onSuccess={() => {setCurrentPage('requests'); setShowRequestForm(false);}} />}
      {currentPage === 'stats' && <EmployeeStats />}
      {currentPage === 'payslips' && <EmployeePayslips />}
      {currentPage === 'communications' && user.unit !== "Agenti" && <CompanyCommunications user={user} />}
      {currentPage === 'conventions' && user.unit !== "Agenti" && <ConventionsGallery />}
      {currentPage === 'settings' && <PasswordSettings user={user} />}
      {currentPage === 'campaign-reports' && user.unit !== "Agenti" && <CampaignReports user={user} />}
      {currentPage === 'deadlines' && user.unit === "Amministrazione" && <DeadlineScheduler user={user} />}
    </div>
  );
};

// Employee Stats Component
const EmployeeStats = () => {
  const [stats, setStats] = useState(null);
  const [vacationSummary, setVacationSummary] = useState(null);
  const [permitSummary, setPermitSummary] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadYears();
    loadVacationSummary();
    loadPermitSummary();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      loadStats();
    }
  }, [selectedYear]);

  const loadYears = async () => {
    try {
      const response = await axios.get(`${API}/years`);
      setYears(response.data.years);
    } catch (error) {
      console.error('Errore nel caricamento degli anni:', error);
    }
  };

  const loadVacationSummary = async () => {
    try {
      const response = await axios.get(`${API}/vacation-summary`);
      setVacationSummary(response.data);
    } catch (error) {
      console.error('Errore nel caricamento del riepilogo ferie:', error);
    }
  };

  const loadPermitSummary = async () => {
    try {
      const response = await axios.get(`${API}/permit-summary`);
      setPermitSummary(response.data);
    } catch (error) {
      console.error('Errore nel caricamento del riepilogo permessi:', error);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stats?year=${selectedYear}`);
      setStats(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle statistiche:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-slate-800 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
            Le Mie Statistiche
          </h2>
          
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-slate-700">Anno:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Vacation Summary */}
        {vacationSummary && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 rounded-xl text-white mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Riepilogo Ferie Totali</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-emerald-200 text-sm">Ferie Rimanenti Totali</p>
                      <p className="text-3xl font-bold">{vacationSummary.total_remaining_days}</p>
                      <p className="text-emerald-200 text-xs">giorni disponibili</p>
                    </div>
                    <div>
                      <p className="text-emerald-200 text-sm">Usate Quest'Anno</p>
                      <p className="text-3xl font-bold">{vacationSummary.used_this_year}</p>
                      <p className="text-emerald-200 text-xs">giorni nel {vacationSummary.current_year}</p>
                    </div>
                    <div>
                      <p className="text-emerald-200 text-sm">Anni con Ferie</p>
                      <p className="text-3xl font-bold">{vacationSummary.vacation_by_year.length}</p>
                      <p className="text-emerald-200 text-xs">anni registrati</p>
                    </div>
                  </div>
                </div>
                <Calculator className="h-16 w-16 text-emerald-200" />
              </div>
            </div>

            {/* Vacation Breakdown by Year */}
            <div className="bg-slate-50 border border-slate-200 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                Dettaglio Ferie per Anno
              </h3>
              
              <div className="space-y-3">
                {vacationSummary.vacation_by_year.map((yearData) => (
                  <div key={yearData.year} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-slate-800">Anno {yearData.year}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        yearData.remaining_days > 0 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : yearData.remaining_days === 0
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {yearData.remaining_days > 0 ? 'Ferie Disponibili' : 
                         yearData.remaining_days === 0 ? 'Ferie Esaurite' : 'Ferie in Eccesso'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600">Massime:</span>
                        <p className="font-bold text-slate-800">{yearData.max_days} giorni</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Utilizzate:</span>
                        <p className="font-bold text-slate-800">{yearData.used_days} giorni</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Riportate:</span>
                        <p className="font-bold text-slate-800">{yearData.carried_over_days} giorni</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Rimanenti:</span>
                        <p className={`font-bold ${yearData.remaining_days >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {yearData.remaining_days} giorni
                        </p>
                      </div>
                      {yearData.year < vacationSummary.current_year && (
                        <div>
                          <span className="text-slate-600">Riportabili:</span>
                          <p className="font-bold text-blue-600">{yearData.can_carry_over} giorni</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Permit Summary */}
        {permitSummary && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 p-6 rounded-xl text-white mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Riepilogo Permessi Totali</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-orange-200 text-sm">Ore Rimanenti Totali</p>
                      <p className="text-3xl font-bold">{permitSummary.total_remaining_hours}</p>
                      <p className="text-orange-200 text-xs">ore disponibili</p>
                    </div>
                    <div>
                      <p className="text-orange-200 text-sm">Usate Quest'Anno</p>
                      <p className="text-3xl font-bold">{permitSummary.used_this_year}</p>
                      <p className="text-orange-200 text-xs">ore nel {permitSummary.current_year}</p>
                    </div>
                    <div>
                      <p className="text-orange-200 text-sm">Accrual Mensile</p>
                      <p className="text-3xl font-bold">2.5</p>
                      <p className="text-orange-200 text-xs">ore al mese</p>
                    </div>
                  </div>
                </div>
                <Clock className="h-16 w-16 text-orange-200" />
              </div>
            </div>

            {/* Permit Breakdown by Year */}
            <div className="bg-slate-50 border border-slate-200 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-orange-600" />
                Dettaglio Permessi per Anno
              </h3>
              
              <div className="space-y-3">
                {permitSummary.permit_by_year.map((yearData) => (
                  <div key={yearData.year} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-slate-800">Anno {yearData.year}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        yearData.remaining_hours > 0 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : yearData.remaining_hours === 0
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {yearData.remaining_hours > 0 ? 'Ore Disponibili' : 
                         yearData.remaining_hours === 0 ? 'Ore Esaurite' : 'Ore in Eccesso'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600">Massime:</span>
                        <p className="font-bold text-slate-800">{yearData.max_hours} ore</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Utilizzate:</span>
                        <p className="font-bold text-slate-800">{yearData.used_hours} ore</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Riportate:</span>
                        <p className="font-bold text-slate-800">{yearData.carried_over_hours} ore</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Rimanenti:</span>
                        <p className={`font-bold ${yearData.remaining_hours >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {yearData.remaining_hours} ore
                        </p>
                      </div>
                      {yearData.year < permitSummary.current_year && (
                        <div>
                          <span className="text-slate-600">Riportabili:</span>
                          <p className="font-bold text-blue-600">{yearData.can_carry_over} ore</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : stats ? (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100">Ferie Prese</p>
                    <p className="text-3xl font-bold">{stats.stats.ferie_days}</p>
                    <p className="text-blue-200 text-sm">giorni nel {stats.year}</p>
                  </div>
                  <Calendar className="h-12 w-12 text-blue-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100">Permessi Presi</p>
                    <p className="text-3xl font-bold">{stats.stats.permessi_count}</p>
                    <p className="text-green-200 text-sm">richieste nel {stats.year}</p>
                  </div>
                  <Clock className="h-12 w-12 text-green-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100">Malattie</p>
                    <p className="text-3xl font-bold">{stats.stats.malattie_days}</p>
                    <p className="text-orange-200 text-sm">giorni nel {stats.year}</p>
                  </div>
                  <Activity className="h-12 w-12 text-orange-200" />
                </div>
              </div>
            </div>

            {/* Summary Card */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-xl text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Riepilogo Anno {stats.year}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-purple-200">Totale Richieste</p>
                      <p className="text-xl font-bold">{stats.stats.total_requests}</p>
                    </div>
                    <div>
                      <p className="text-purple-200">Giorni Ferie</p>
                      <p className="text-xl font-bold">{stats.stats.ferie_days}</p>
                    </div>
                    <div>
                      <p className="text-purple-200">Permessi</p>
                      <p className="text-xl font-bold">{stats.stats.permessi_count}</p>
                    </div>
                    <div>
                      <p className="text-purple-200">Giorni Malattia</p>
                      <p className="text-xl font-bold">{stats.stats.malattie_days}</p>
                    </div>
                  </div>
                </div>
                <TrendingUp className="h-16 w-16 text-purple-200" />
              </div>
            </div>

          </div>
        ) : (
          <div className="text-center py-12">
            <BarChart3 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessun dato disponibile per l'anno selezionato</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Edit Request Form Component
const EditRequestForm = ({ request, onSuccess, onCancel }) => {
  const [requestType, setRequestType] = useState(request.type);
  const [formData, setFormData] = useState(() => {
    // Initialize form data based on request type
    const data = {};
    
    if (request.type === 'ferie') {
      data.start_date = request.start_date?.split('T')[0] || '';
      data.end_date = request.end_date?.split('T')[0] || '';
    } else if (request.type === 'permesso') {
      data.permit_date = request.permit_date?.split('T')[0] || '';
      data.start_time = request.start_time || '';
      data.end_time = request.end_time || '';
    } else if (request.type === 'malattia') {
      data.sick_start_date = request.sick_start_date?.split('T')[0] || '';
      data.sick_days = request.sick_days || '';
      data.protocol_code = request.protocol_code || '';
    } else if (request.type === 'smartworking') {
      data.smartworking_date = request.smartworking_date?.split('T')[0] || '';
    }
    
    return data;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const requestData = { type: requestType, ...formData };
      await axios.put(`${API}/requests/${request.id}`, requestData);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore durante la modifica della richiesta');
    } finally {
      setLoading(false);
    }
  };

  const renderEditForm = () => {
    switch (requestType) {
      case 'ferie':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Modifica Richiesta Ferie (max 15 giorni consecutivi)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Data inizio</label>
                <input
                  type="date"
                  value={formData.start_date || ''}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Data fine</label>
                <input
                  type="date"
                  value={formData.end_date || ''}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
          </div>
        );
        
      case 'permesso':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Modifica Richiesta Permesso</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Data</label>
              <input
                type="date"
                value={formData.permit_date || ''}
                onChange={(e) => setFormData({ ...formData, permit_date: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Orario inizio</label>
                <input
                  type="time"
                  value={formData.start_time || ''}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Orario fine</label>
                <input
                  type="time"
                  value={formData.end_time || ''}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
          </div>
        );
        
      case 'malattia':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Modifica Richiesta Malattia</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Data inizio</label>
                <input
                  type="date"
                  value={formData.sick_start_date || ''}
                  onChange={(e) => setFormData({ ...formData, sick_start_date: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Numero giorni</label>
                <input
                  type="number"
                  min="1"
                  value={formData.sick_days || ''}
                  onChange={(e) => setFormData({ ...formData, sick_days: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Codice protocollo certificato</label>
              <input
                type="text"
                value={formData.protocol_code || ''}
                onChange={(e) => setFormData({ ...formData, protocol_code: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Inserisci il codice del certificato medico"
                required
              />
            </div>
          </div>
        );
      
      case 'smartworking':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Modifica Richiesta Smartworking</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Data smartworking</label>
              <input
                type="date"
                value={formData.smartworking_date || ''}
                onChange={(e) => setFormData({ ...formData, smartworking_date: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min={new Date().toISOString().split('T')[0]} // Non permettere date passate
                required
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Home className="h-5 w-5 text-blue-600 mt-0.5" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    <strong>Ricorda:</strong> Puoi richiedere massimo 4 giorni di smartworking al mese.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
      <h2 className="text-xl font-semibold text-slate-800 mb-6">Modifica Richiesta</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <p className="text-blue-800 text-sm">
            <strong>Nota:</strong> Puoi modificare solo le richieste in stato "In attesa". 
            Una volta elaborate dall'amministratore, non potranno più essere modificate.
          </p>
        </div>

        {renderEditForm()}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex space-x-3 pt-6 border-t border-slate-200">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-6 rounded-lg font-medium hover:from-green-700 hover:to-green-800 disabled:opacity-50 transition-all"
          >
            {loading ? 'Modifica in corso...' : 'Salva Modifiche'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all"
          >
            Annulla
          </button>
        </div>
      </form>
    </div>
  );
};

// New Request Form Component
const NewRequestForm = ({ onSuccess }) => {
  const [requestType, setRequestType] = useState('');
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const requestData = { type: requestType, ...formData };
      await axios.post(`${API}/requests`, requestData);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore durante la creazione della richiesta');
    } finally {
      setLoading(false);
    }
  };

  const renderRequestTypeForm = () => {
    switch (requestType) {
      case 'ferie':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Richiesta Ferie (max 15 giorni consecutivi)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Data inizio</label>
                <input
                  type="date"
                  value={formData.start_date || ''}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Data fine</label>
                <input
                  type="date"
                  value={formData.end_date || ''}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          </div>
        );
        
      case 'permesso':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Richiesta Permesso</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Data</label>
              <input
                type="date"
                value={formData.permit_date || ''}
                onChange={(e) => setFormData({ ...formData, permit_date: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Orario inizio</label>
                <input
                  type="time"
                  value={formData.start_time || ''}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Orario fine</label>
                <input
                  type="time"
                  value={formData.end_time || ''}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          </div>
        );
        
      case 'malattia':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Richiesta Malattia</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Data inizio</label>
                <input
                  type="date"
                  value={formData.sick_start_date || ''}
                  onChange={(e) => setFormData({ ...formData, sick_start_date: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Numero giorni</label>
                <input
                  type="number"
                  min="1"
                  value={formData.sick_days || ''}
                  onChange={(e) => setFormData({ ...formData, sick_days: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Codice protocollo certificato</label>
              <input
                type="text"
                value={formData.protocol_code || ''}
                onChange={(e) => setFormData({ ...formData, protocol_code: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Inserisci il codice del certificato medico"
                required
              />
            </div>
          </div>
        );
        
      case 'smartworking':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Richiesta Smartworking (max 4 giorni al mese)</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Data smartworking</label>
              <input
                type="date"
                value={formData.smartworking_date || ''}
                onChange={(e) => setFormData({ ...formData, smartworking_date: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                min={new Date().toISOString().split('T')[0]} // Non permettere date passate
                required
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Home className="h-5 w-5 text-blue-600 mt-0.5" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    <strong>Limite mensile:</strong> Puoi richiedere massimo 4 giorni di smartworking al mese.
                    Ogni richiesta deve essere pre-approvata dal tuo responsabile e poi dall'amministratore.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
      <h2 className="text-xl font-semibold text-slate-800 mb-6">Nuova Richiesta</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">Tipo di richiesta</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { value: 'ferie', label: 'Ferie', icon: Calendar },
              { value: 'permesso', label: 'Permesso', icon: Clock },
              { value: 'malattia', label: 'Malattia', icon: Activity },
              { value: 'smartworking', label: 'Smartworking', icon: Home }
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setRequestType(value);
                  setFormData({});
                }}
                className={`p-4 border rounded-xl text-center transition-all hover:shadow-md ${
                  requestType === value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Icon className="h-6 w-6 mx-auto mb-2" />
                <div className="font-medium">{label}</div>
              </button>
            ))}
          </div>
        </div>

        {requestType && (
          <div className="border-t border-slate-200 pt-6">
            {renderRequestTypeForm()}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {requestType && (
          <div className="flex space-x-3 pt-6 border-t border-slate-200">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
            >
              {loading ? 'Invio in corso...' : 'Invia Richiesta'}
            </button>
            <button
              type="button"
              onClick={() => {
                setRequestType('');
                setFormData({});
                setError('');
              }}
              className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all"
            >
              Annulla
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

// PDF Splitter Tool Component (Admin only)
const PDFSplitterTool = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setUploadStatus('');
      } else {
        setUploadStatus('❌ Solo file PDF sono supportati');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setUploadStatus('');
      } else {
        setUploadStatus('❌ Solo file PDF sono supportati');
        setSelectedFile(null);
      }
    }
  };

  const handleSplitPDF = async () => {
    if (!selectedFile) {
      setUploadStatus('❌ Seleziona un file PDF prima');
      return;
    }

    setUploading(true);
    setUploadStatus('📄 Divisione PDF in corso...');
    
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(`${API}/admin/pdf-split`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob'
      });

      // Create download URL
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setDownloadUrl(url);
      
      // Trigger automatic download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedFile.name.replace('.pdf', '_diviso_in_pagine.zip'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setUploadStatus('✅ PDF diviso con successo! Download avviato automaticamente.');
      
    } catch (error) {
      console.error('Errore durante la divisione del PDF:', error);
      setUploadStatus('❌ Errore durante la divisione del PDF. Riprova.');
    } finally {
      setUploading(false);
    }
  };

  const resetTool = () => {
    setSelectedFile(null);
    setDownloadUrl(null);
    setUploadStatus('');
    setDragActive(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
          <Scissors className="h-5 w-5 mr-2 text-blue-600" />
          PDF Splitter Tool
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-slate-700">1. Seleziona PDF da dividere</h3>
            
            {/* Drag & Drop Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-300 bg-slate-50 hover:border-slate-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">
                Trascina qui il tuo file PDF o clicca per selezionare
              </p>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
                id="pdf-upload"
              />
              <label
                htmlFor="pdf-upload"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-700 transition-colors inline-block"
              >
                Seleziona PDF
              </label>
            </div>

            {/* Selected File Info */}
            {selectedFile && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-800">File selezionato:</p>
                    <p className="text-blue-700 text-sm">{selectedFile.name}</p>
                    <p className="text-blue-600 text-xs">
                      Dimensione: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={handleSplitPDF}
              disabled={!selectedFile || uploading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                selectedFile && !uploading
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-slate-300 text-slate-500 cursor-not-allowed'
              }`}
            >
              {uploading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Dividendo PDF...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Scissors className="h-4 w-4 mr-2" />
                  Dividi PDF in Pagine
                </div>
              )}
            </button>
          </div>

          {/* Info Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-slate-700">2. Come funziona</h3>
            
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg">
              <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 text-blue-600" />
                Processo di divisione:
              </h4>
              <ul className="text-slate-600 text-sm space-y-2">
                <li>• Il PDF viene analizzato pagina per pagina</li>
                <li>• Ogni pagina viene salvata come file PDF separato</li>
                <li>• Tutti i file vengono compressi in un archivio ZIP</li>
                <li>• Il download inizia automaticamente</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                Formato file risultanti:
              </h4>
              <ul className="text-green-700 text-sm space-y-2">
                <li>• <strong>Nome:</strong> documento_pagina_001.pdf</li>
                <li>• <strong>Archivio:</strong> documento_diviso_in_pagine.zip</li>
                <li>• <strong>Qualità:</strong> Mantenuta identica all'originale</li>
              </ul>
            </div>

            {/* Reset Button */}
            {(selectedFile || uploadStatus) && (
              <button
                onClick={resetTool}
                className="w-full py-2 px-4 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Nuovo PDF
              </button>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {uploadStatus && (
          <div className={`mt-6 p-4 rounded-lg ${
            uploadStatus.includes('✅') 
              ? 'bg-green-50 border border-green-200 text-green-800'
              : uploadStatus.includes('❌')
              ? 'bg-red-50 border border-red-200 text-red-800'
              : 'bg-blue-50 border border-blue-200 text-blue-800'
          }`}>
            <p className="font-medium">{uploadStatus}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Room Booking System Component
const RoomBookingSystem = ({ user }) => {
  const [currentView, setCurrentView] = useState('calendar');
  const [bookings, setBookings] = useState([]);
  const [roomInfo, setRoomInfo] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [pendingBookings, setPendingBookings] = useState([]);

  // Unit colors for the calendar
  const unitColors = {
    'CEO': 'bg-purple-600',
    'Amministrazione': 'bg-blue-600', 
    'Presidi': 'bg-green-600',
    'Store': 'bg-emerald-600',
    'Agenti': 'bg-red-600',
    'HR': 'bg-orange-500',
    'Digital': 'bg-cyan-500',
    'TLS': 'bg-indigo-600',
    'Fotovoltaico': 'bg-yellow-500',
    'Fibra': 'bg-yellow-500'
  };

  useEffect(() => {
    loadRoomInfo();
    loadBookings();
    if (user.role === 'admin') {
      loadPendingBookings();
    }
  }, []);

  const loadRoomInfo = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRoomInfo(response.data);
    } catch (error) {
      console.error('Errore nel caricamento info sala:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
      
      const response = await axios.get(`${API}/room-bookings`, {
        params: {
          date_from: today.toISOString().split('T')[0],
          date_to: nextMonth.toISOString().split('T')[0]
        }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle prenotazioni:', error);
    }
  };

  const loadPendingBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/room-bookings/pending`);
      setPendingBookings(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle prenotazioni in attesa:', error);
    }
  };

  const handleBookingResponse = async (bookingId, action, notes = '') => {
    try {
      await axios.put(`${API}/admin/room-booking/${bookingId}`, {
        action,
        notes
      });
      await loadPendingBookings();
      await loadBookings();
    } catch (error) {
      console.error('Errore nella gestione della prenotazione:', error);
    }
  };

  // Get bookings for a specific date
  const getBookingsForDate = (date) => {
    return bookings.filter(booking => 
      booking.booking_date === date &&
      booking.status === 'approved'
    );
  };

  // Generate time slots from 8:00 to 18:00
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 flex items-center mb-2">
              <Calendar className="h-5 w-5 mr-2 text-blue-600" />
              Prenotazione Sala Riunioni
            </h2>
            <p className="text-slate-600 text-sm">
              {user.role === 'admin' ? 'Gestisci tutte le prenotazioni della sala' : 'Prenota la sala per la tua unità'}
            </p>
            {roomInfo && (
              <p className="text-slate-500 text-xs mt-1">
                Dotazioni: {roomInfo.equipment.join(', ')}
              </p>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCurrentView('calendar')}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                currentView === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Calendario
            </button>
            
            <button
              onClick={() => setCurrentView('new-booking')}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                currentView === 'new-booking'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Nuova Prenotazione
            </button>
            
            {user.role === 'admin' && (
              <button
                onClick={() => setCurrentView('pending')}
                className={`px-4 py-2 rounded-lg transition-colors text-sm relative ${
                  currentView === 'pending'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Approvazioni
                {pendingBookings.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingBookings.length}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {currentView === 'calendar' && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <h3 className="text-lg font-semibold text-slate-800">Calendario Occupazione Sala</h3>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700">Data:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Single Room Calendar Grid */}
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-slate-800 flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-blue-600" />
                Sala Riunioni - {new Date(selectedDate).toLocaleDateString('it-IT')}
              </h4>
            </div>
            
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
              {timeSlots.map(time => {
                const dayBookings = getBookingsForDate(selectedDate);
                const booking = dayBookings.find(b => 
                  b.start_time <= time && b.end_time > time
                );
                
                return (
                  <div
                    key={time}
                    className={`p-2 text-xs text-center rounded border min-h-[60px] flex flex-col justify-center ${
                      booking 
                        ? `${unitColors[booking.unit] || 'bg-gray-500'} text-white`
                        : 'bg-green-100 text-green-800 border-green-200'
                    }`}
                    title={booking ? `${booking.unit} - ${booking.purpose} (${booking.start_time}-${booking.end_time})` : `Disponibile alle ${time}`}
                  >
                    <div className="font-medium">{time}</div>
                    {booking && (
                      <div className="mt-1 text-xs opacity-90">
                        {booking.unit}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
              <span className="text-sm text-slate-600">Disponibile</span>
            </div>
            {Object.entries(unitColors).map(([unit, color]) => (
              <div key={unit} className="flex items-center space-x-2">
                <div className={`w-4 h-4 ${color} rounded`}></div>
                <span className="text-sm text-slate-600">{unit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Booking View */}
      {currentView === 'new-booking' && (
        <NewRoomBookingForm 
          roomInfo={roomInfo}
          onSuccess={() => {
            setCurrentView('calendar');
            loadBookings();
          }}
          user={user}
        />
      )}

      {/* Admin Pending Approvals */}
      {currentView === 'pending' && user.role === 'admin' && (
        <PendingBookingApprovals 
          bookings={pendingBookings}
          onAction={handleBookingResponse}
        />
      )}
    </div>
  );
};

// New Room Booking Form Component
const NewRoomBookingForm = ({ roomInfo, onSuccess, user }) => {
  const [formData, setFormData] = useState({
    room_name: 'Sala Riunioni',
    booking_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    purpose: '',
    unit: user.unit || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/room-booking`, formData);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella prenotazione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-800">Nuova Prenotazione Sala</h3>
        {roomInfo && (
          <div className="text-right">
            <p className="text-sm font-medium text-slate-700">Sala Riunioni</p>
            <p className="text-xs text-slate-500">
              Dotazioni: {roomInfo.equipment.join(', ')}
            </p>
          </div>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data</label>
            <input
              type="date"
              name="booking_date"
              value={formData.booking_date}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Unità</label>
            <input
              type="text"
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              required
              placeholder="Es. IT, HR, Finance..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Ora Inizio</label>
            <input
              type="time"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Ora Fine</label>
            <input
              type="time"
              name="end_time"
              value={formData.end_time}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Scopo della Riunione</label>
          <textarea
            name="purpose"
            value={formData.purpose}
            onChange={handleChange}
            required
            rows={3}
            placeholder="Descrivi brevemente lo scopo della riunione..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => onSuccess()}
            className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Annulla
          </button>
          
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Invio...' : 'Richiedi Prenotazione'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Pending Booking Approvals Component (Admin only)
const PendingBookingApprovals = ({ bookings, onAction }) => {
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAction = async (action) => {
    if (!selectedBooking) return;
    
    setSubmitting(true);
    try {
      await onAction(selectedBooking.id, action, adminNotes);
      setSelectedBooking(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Errore nell\'azione:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
        <Bell className="h-5 w-5 mr-2 text-orange-500" />
        Prenotazioni in Attesa di Approvazione ({bookings.length})
      </h3>

      {bookings.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <p className="text-slate-500">Nessuna prenotazione in attesa</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map(booking => (
            <div key={booking.id} className="border border-slate-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-800">
                    {booking.room_name} - {new Date(booking.booking_date).toLocaleDateString('it-IT')}
                  </h4>
                  <p className="text-slate-600 text-sm">
                    {booking.start_time} - {booking.end_time} | Unità: {booking.unit}
                  </p>
                  <p className="text-slate-600 text-sm">
                    Richiesta da: {booking.requested_by_name} ({booking.requested_by_email})
                  </p>
                  <p className="text-slate-600 text-sm mt-1">
                    <strong>Scopo:</strong> {booking.purpose}
                  </p>
                </div>
                
                <button
                  onClick={() => setSelectedBooking(booking)}
                  className="mt-3 sm:mt-0 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Gestisci
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Booking Action Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h4 className="text-lg font-semibold text-slate-800 mb-4">
              Gestisci Prenotazione
            </h4>
            
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <p className="font-medium">{selectedBooking.room_name}</p>
              <p className="text-sm text-slate-600">
                {new Date(selectedBooking.booking_date).toLocaleDateString('it-IT')} | 
                {selectedBooking.start_time} - {selectedBooking.end_time}
              </p>
              <p className="text-sm text-slate-600">Unità: {selectedBooking.unit}</p>
              <p className="text-sm text-slate-600 mt-1">
                <strong>Scopo:</strong> {selectedBooking.purpose}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Note Admin (opzionale)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                placeholder="Aggiungi note per la decisione..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setSelectedBooking(null);
                  setAdminNotes('');
                }}
                className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                disabled={submitting}
              >
                Annulla
              </button>
              
              <button
                onClick={() => handleAction('reject')}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                disabled={submitting}
              >
                {submitting ? 'Invio...' : 'Rifiuta'}
              </button>
              
              <button
                onClick={() => handleAction('approve')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                disabled={submitting}
              >
                {submitting ? 'Invio...' : 'Approva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Company Communications Component (Admin only) - ORIGINAL WORKING VERSION RESTORED
const CompanyCommunications = ({ user }) => {
  const canSendCommunications = user && user.role === 'admin';
  const [currentView, setCurrentView] = useState(canSendCommunications ? 'send' : 'history');
  const [communications, setCommunications] = useState([]);
  
  const availableUnits = [
    "CEO",
    "HR", 
    "Digital",
    "Amministrazione",
    "Presidi",
    "Store",
    "TLS", 
    "Fotovoltaico",
    "Fibra",
    "Agenti"
  ];
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target_units: []
  });
  const [selectAll, setSelectAll] = useState(false);
  const [unitOptions, setUnitOptions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCommunications();
  }, []);

  const loadCommunications = async () => {
    try {
      const endpoint = canSendCommunications 
        ? `${API}/admin/company-communications`
        : `${API}/communications`;
      
      const response = await axios.get(endpoint);
      setCommunications(response.data.communications || []);
    } catch (error) {
      console.error('Errore nel caricamento delle comunicazioni:', error);
    }
  };

  const handleSelectAll = () => {
    console.log('handleSelectAll called, current selectAll:', selectAll);
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      console.log('Selecting all - clearing individual units');
      setFormData(prev => ({ ...prev, target_units: ['all'] }));
      setUnitOptions({});
    } else {
      console.log('Deselecting all - clearing everything');
      setFormData(prev => ({ ...prev, target_units: [] }));
      setUnitOptions({});
    }
  };

  const handleUnitToggle = (unit) => {
    console.log('handleUnitToggle called for unit:', unit);
    console.log('Current target_units before:', formData.target_units);
    
    // Prevent re-render loop by using functional updates
    setFormData(prev => {
      const isSelected = prev.target_units.includes(unit);
      console.log('Unit', unit, 'is currently selected:', isSelected);
      
      if (isSelected) {
        console.log('Removing unit:', unit);
        return {
          ...prev,
          target_units: prev.target_units.filter(u => u !== unit && u !== 'all')
        };
      } else {
        console.log('Adding unit:', unit);
        return {
          ...prev,
          target_units: [...prev.target_units.filter(u => u !== 'all'), unit]
        };
      }
    });
    
    // Update unit options
    setUnitOptions(prev => {
      const isSelected = formData.target_units.includes(unit);
      if (isSelected) {
        const newOpts = { ...prev };
        delete newOpts[unit];
        return newOpts;
      } else {
        return { ...prev, [unit]: 'all' };
      }
    });
    
    // Ensure selectAll is false when selecting individual units
    if (selectAll) {
      setSelectAll(false);
    }
  };

  const handleUnitOptionChange = (unit, option) => {
    setUnitOptions(prev => ({ ...prev, [unit]: option }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const dataToSend = {
        ...formData,
        unit_options: unitOptions
      };
      
      const response = await axios.post(`${API}/admin/company-communication`, dataToSend);
      setSuccess(response.data.message);
      setFormData({ title: '', message: '', target_units: [] });
      setSelectAll(false);
      setUnitOptions({});
      await loadCommunications();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nell\'invio della comunicazione');
    } finally {
      setSubmitting(false);
    }
  };

  // Remove the continuous render log to stop infinite loop
  // console.log('CompanyCommunications render - current state:', {
  //   selectAll,
  //   target_units: formData.target_units,
  //   unitOptions
  // });

  return (
    <div className="space-y-6">
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 flex items-center mb-2">
              <Mail className="h-5 w-5 mr-2 text-blue-600" />
              Comunicazioni Aziendali
            </h2>
            <p className="text-slate-600 text-sm">
              {canSendCommunications 
                ? "Invia comunicazioni alle unità selezionate o a tutti i dipendenti"
                : "Visualizza le comunicazioni aziendali ricevute"
              }
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {canSendCommunications && (
              <button
                onClick={() => setCurrentView('send')}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                  currentView === 'send'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Invia Comunicazione
              </button>
            )}
            
            <button
              onClick={() => setCurrentView('history')}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                currentView === 'history'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Storico ({communications.length})
            </button>
          </div>
        </div>
      </div>

      {currentView === 'send' && canSendCommunications && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Nuova Comunicazione</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Titolo Comunicazione
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Es. Nuove Procedure, Comunicazione Importante..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Messaggio
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Scrivi qui il messaggio della comunicazione..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Destinatari
              </label>
              
              <div className="mb-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Tutti i Dipendenti
                  </span>
                </label>
              </div>

              {!selectAll && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-800 mb-3">
                    Seleziona Unità Destinatarie:
                  </h4>
                  
                  {availableUnits.map(unit => (
                    <div key={unit} className="border border-slate-200 rounded-lg p-3 bg-slate-50 relative">
                      <div className="flex items-center space-x-3 mb-2">
                        <input
                          type="checkbox"
                          checked={formData.target_units.includes(unit)}
                          onChange={() => {
                            console.log('CHECKBOX CHANGED FOR UNIT:', unit);
                            handleUnitToggle(unit);
                          }}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          style={{ 
                            position: 'relative',
                            zIndex: 999,
                            pointerEvents: 'auto',
                            width: '20px',
                            height: '20px'
                          }}
                        />
                        <span 
                          className="font-medium text-slate-800"
                          onClick={() => {
                            console.log('SPAN CLICKED - MANUAL TOGGLE:', unit);
                            handleUnitToggle(unit);
                          }}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          {unit} {formData.target_units.includes(unit) ? '✓' : '○'}
                        </span>
                      </div>
                      
                      {formData.target_units.includes(unit) && (
                        <div className="ml-6 space-y-2">
                          <p className="text-xs text-slate-600 mb-2">Chi riceverà la comunicazione?</p>
                          <div className="space-y-1">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`${unit}-recipient`}
                                checked={(unitOptions[unit] || 'all') === 'all'}
                                onChange={() => {
                                  console.log('RADIO CHANGED - ALL for unit:', unit);
                                  handleUnitOptionChange(unit, 'all');
                                }}
                                className="text-blue-600 focus:ring-blue-500"
                                style={{ 
                                  position: 'relative',
                                  zIndex: 999,
                                  pointerEvents: 'auto',
                                  width: '16px',
                                  height: '16px'
                                }}
                              />
                              <span 
                                className="text-sm"
                                onClick={() => {
                                  console.log('SPAN CLICKED - ALL for unit:', unit);
                                  handleUnitOptionChange(unit, 'all');
                                }}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                              >
                                Tutta la Unit {unit} {(unitOptions[unit] || 'all') === 'all' ? '●' : '○'}
                              </span>
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`${unit}-recipient`}
                                checked={(unitOptions[unit] || 'all') === 'manager'}
                                onChange={() => {
                                  console.log('RADIO CHANGED - MANAGER for unit:', unit);
                                  handleUnitOptionChange(unit, 'manager');
                                }}
                                className="text-blue-600 focus:ring-blue-500"
                                style={{ 
                                  position: 'relative',
                                  zIndex: 999,
                                  pointerEvents: 'auto',
                                  width: '16px',
                                  height: '16px'
                                }}
                              />
                              <span 
                                className="text-sm"
                                onClick={() => {
                                  console.log('SPAN CLICKED - MANAGER for unit:', unit);
                                  handleUnitOptionChange(unit, 'manager');
                                }}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                              >
                                Solo Responsabile {unit} {(unitOptions[unit] || 'all') === 'manager' ? '●' : '○'}
                              </span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {formData.target_units.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium mb-2">
                    Riepilogo Destinatari:
                  </p>
                  {selectAll ? (
                    <p className="text-sm text-blue-700">• Tutti i dipendenti dell'azienda</p>
                  ) : (
                    <div className="space-y-1">
                      {formData.target_units.map(unit => (
                        <p key={unit} className="text-sm text-blue-700">
                          • {unit}: {
                            unitOptions[unit] === 'manager' 
                              ? 'Solo Responsabile' 
                              : 'Tutta la Unit'
                          }
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                {success}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setFormData({ title: '', message: '', target_units: [] });
                  setSelectAll(false);
                  setUnitOptions({});
                  setError('');
                  setSuccess('');
                }}
                className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Annulla
              </button>
              
              <button
                type="submit"
                disabled={submitting || formData.target_units.length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Invio...' : 'Invia Comunicazione'}
              </button>
            </div>
          </form>
        </div>
      )}

      {currentView === 'history' && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            Storico Comunicazioni ({communications.length})
          </h3>

          {communications.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessuna comunicazione inviata ancora</p>
            </div>
          ) : (
            <div className="space-y-4">
              {communications.map(comm => (
                <div key={comm.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2">
                    <h4 className="font-semibold text-slate-800">{comm.title}</h4>
                    <span className="text-xs text-slate-500 mt-1 sm:mt-0">
                      {new Date(comm.created_at).toLocaleString('it-IT')}
                    </span>
                  </div>
                  
                  <p className="text-slate-600 text-sm mb-3">{comm.message}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-slate-500">Inviata a:</span>
                    {comm.target_units.includes('all') ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        Tutti i dipendenti
                      </span>
                    ) : (
                      comm.target_units.map(unit => (
                        <span key={unit} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                          {unit}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Deadline Scheduler Component (Admin and Amministrazione only)
const DeadlineScheduler = ({ user }) => {
  const [currentView, setCurrentView] = useState('list');
  const [deadlines, setDeadlines] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    company: '',
    deadline_type: '',
    amount: '',
    due_date: '',
    notes: '',
    is_recurring: false,
    recurring_day: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendingAlerts, setSendingAlerts] = useState(false);

  useEffect(() => {
    loadDeadlines();
  }, [selectedMonth, selectedYear]);

  const loadDeadlines = async () => {
    try {
      const response = await axios.get(`${API}/admin/deadlines`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      setDeadlines(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle scadenze:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      await axios.post(`${API}/admin/deadline`, submitData);
      setSuccess('Scadenza creata con successo');
      setFormData({
        company: '',
        deadline_type: '',
        amount: '',
        due_date: '',
        notes: '',
        is_recurring: false,
        recurring_day: null
      });
      await loadDeadlines();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella creazione della scadenza');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (deadlineId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa scadenza?')) return;
    
    try {
      await axios.delete(`${API}/admin/deadline/${deadlineId}`);
      await loadDeadlines();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
    }
  };

  const handleSendAlerts = async () => {
    if (!window.confirm('Sei sicuro di voler inviare gli alert per le scadenze?')) return;
    
    setSendingAlerts(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${API}/admin/send-deadline-alerts`);
      setSuccess(response.data.message);
      console.log('Alert inviati:', response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nell\'invio degli alert');
    } finally {
      setSendingAlerts(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Calculate days until deadline
  const getDaysUntil = (dueDate) => {
    const today = new Date();
    const deadline = new Date(dueDate);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get status color based on days remaining
  const getStatusColor = (daysUntil) => {
    if (daysUntil < 0) return 'bg-gray-100 text-gray-800'; // Passed
    if (daysUntil === 0) return 'bg-red-100 text-red-800'; // Today
    if (daysUntil <= 2) return 'bg-orange-100 text-orange-800'; // Urgent
    if (daysUntil <= 7) return 'bg-yellow-100 text-yellow-800'; // Soon
    return 'bg-green-100 text-green-800'; // Future
  };

  const getStatusText = (daysUntil) => {
    if (daysUntil < 0) return `Scaduta ${Math.abs(daysUntil)} giorni fa`;
    if (daysUntil === 0) return 'OGGI';
    if (daysUntil === 1) return 'Domani';
    return `${daysUntil} giorni`;
  };

  // Generate month options
  const months = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const years = [];
  for (let i = new Date().getFullYear() - 2; i <= new Date().getFullYear() + 5; i++) {
    years.push(i);
  }

  // Generate calendar days
  const generateCalendar = () => {
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDay = new Date(selectedYear, selectedMonth, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    const calendar = [];
    
    // Add empty cells for days before first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      calendar.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      // Create date string without timezone conversion
      const dateString = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayDeadlines = deadlines.filter(d => {
        // Compare raw date strings without time conversions
        const deadlineDate = d.due_date.includes('T') ? d.due_date.split('T')[0] : d.due_date;
        return deadlineDate === dateString;
      });
      
      // Check if today without timezone conversion
      const today = new Date();
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      calendar.push({
        day,
        date: dateString,
        deadlines: dayDeadlines,
        isToday: dateString === todayString
      });
    }
    
    return calendar;
  };

  const calendarData = generateCalendar();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 flex items-center mb-2">
              <Bell className="h-5 w-5 mr-2 text-blue-600" />
              Scadenzario Amministrazione
            </h2>
            <p className="text-slate-600 text-sm">
              Gestisci scadenze aziendali con alert automatici
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCurrentView('list')}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                currentView === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Lista ({deadlines.length})
            </button>
            
            <button
              onClick={() => setCurrentView('calendar')}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                currentView === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Calendario
            </button>
            
            <button
              onClick={() => setCurrentView('add')}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                currentView === 'add'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Nuova Scadenza
            </button>
            
            <button
              onClick={handleSendAlerts}
              disabled={sendingAlerts}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors text-sm flex items-center space-x-2"
              title="Invia alert email per scadenze urgenti"
            >
              <Bell className="h-4 w-4" />
              <span>{sendingAlerts ? 'Invio...' : 'Invia Alert'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* List View */}
      {currentView === 'list' && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Scadenze {months[selectedMonth - 1]} {selectedYear}
            </h3>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700">Mese:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700">Anno:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {deadlines.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessuna scadenza per {months[selectedMonth - 1]} {selectedYear}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {deadlines.map(deadline => {
                const daysUntil = getDaysUntil(deadline.due_date);
                
                return (
                  <div key={deadline.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-slate-800">{deadline.company}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(daysUntil)}`}>
                            {getStatusText(daysUntil)}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-slate-600">
                          <div>
                            <span className="font-medium">Tipo:</span> {deadline.deadline_type}
                          </div>
                          <div>
                            <span className="font-medium">Importo:</span> €{deadline.amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </div>
                          <div>
                            <span className="font-medium">Scadenza:</span> {deadline.due_date.split('-').reverse().join('/')}
                          </div>
                        </div>
                        
                        {deadline.notes && (
                          <div className="mt-2 text-sm text-slate-600">
                            <span className="font-medium">Note:</span> {deadline.notes}
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={() => handleDelete(deadline.id)}
                        className="mt-3 sm:mt-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Elimina scadenza"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Calendar View */}
      {currentView === 'calendar' && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Calendario {months[selectedMonth - 1]} {selectedYear}
            </h3>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700">Mese:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700">Anno:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Calendar Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 bg-slate-50 rounded">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Body */}
          <div className="grid grid-cols-7 gap-1">
            {calendarData.map((cell, index) => (
              <div key={index} className="min-h-[100px] border border-slate-200 rounded-lg p-1">
                {cell && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${
                      cell.isToday 
                        ? 'text-blue-600 font-bold' 
                        : 'text-slate-700'
                    }`}>
                      {cell.day}
                    </div>
                    
                    {cell.deadlines.length > 0 && (
                      <div className="space-y-1">
                        {cell.deadlines.slice(0, 2).map(deadline => {
                          const daysUntil = getDaysUntil(deadline.due_date);
                          return (
                            <div
                              key={deadline.id}
                              className={`text-xs p-1 rounded cursor-pointer ${getStatusColor(daysUntil)}`}
                              title={`${deadline.company} - ${deadline.deadline_type} - €${deadline.amount.toLocaleString('it-IT')}`}
                            >
                              <div className="truncate font-medium">
                                {deadline.company}
                              </div>
                              <div className="truncate">
                                {deadline.deadline_type}
                              </div>
                            </div>
                          );
                        })}
                        {cell.deadlines.length > 2 && (
                          <div className="text-xs text-slate-600 font-medium">
                            +{cell.deadlines.length - 2} altre
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {deadlines.length === 0 && (
            <div className="text-center py-8 mt-4">
              <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessuna scadenza per {months[selectedMonth - 1]} {selectedYear}</p>
            </div>
          )}
        </div>
      )}

      {/* Add Deadline View */}
      {currentView === 'add' && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Nuova Scadenza</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Società *
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nome società"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tipo Scadenza *
                </label>
                <input
                  type="text"
                  name="deadline_type"
                  value={formData.deadline_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Es. Tasse, Contributi, Affitto..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Importo (€) *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Data Scadenza *
                </label>
                <input
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* Recurring Deadline Options */}
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="is_recurring"
                  name="is_recurring"
                  checked={formData.is_recurring || false}
                  onChange={(e) => {
                    const isRecurring = e.target.checked;
                    setFormData(prev => ({ 
                      ...prev, 
                      is_recurring: isRecurring,
                      recurring_day: isRecurring ? (formData.due_date ? parseInt(formData.due_date.split('-')[2]) : 1) : null
                    }));
                  }}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="is_recurring" className="text-sm font-medium text-slate-700">
                  🔄 Scadenza Ricorrente (stesso giorno ogni mese)
                </label>
              </div>
              
              {formData.is_recurring && (
                <div className="ml-6 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Giorno del mese per la ricorrenza
                    </label>
                    <select
                      name="recurring_day"
                      value={formData.recurring_day || 1}
                      onChange={handleChange}
                      className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      La scadenza si ripeterà ogni mese il giorno {formData.recurring_day || 1}
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>ℹ️ Scadenze ricorrenti:</strong> Ogni mese verrà automaticamente creata una nuova scadenza per lo stesso cliente e tipo, nello stesso giorno del mese.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Note
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Note aggiuntive sulla scadenza..."
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                {success}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm text-blue-800">
              <h4 className="font-semibold mb-2">📧 Sistema Alert Automatici:</h4>
              <ul className="space-y-1 text-xs">
                <li>• Alert 2 giorni prima della scadenza (ore 09:00 fuso Roma)</li>  
                <li>• Alert urgente il giorno di scadenza (ore 09:00 fuso Roma)</li>
                <li>• Email inviate a Admin e unità Amministrazione</li>
              </ul>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    company: '',
                    deadline_type: '',
                    amount: '',
                    due_date: '',
                    notes: ''
                  });
                  setError('');
                  setSuccess('');
                }}
                className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Annulla
              </button>
              
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Salvataggio...' : 'Crea Scadenza'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = ({ currentPage, setCurrentPage, user }) => {
  const [stats, setStats] = useState({ pending_ferie: 0, pending_permessi: 0, pending_malattie: 0, pending_smartworking: 0, total_pending: 0 });
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // New state for processed requests section
  const [processedRequestsCollapsed, setProcessedRequestsCollapsed] = useState(true);
  const [processedRequestsFilter, setProcessedRequestsFilter] = useState('all'); // 'all', 'ferie', 'permesso', 'malattia', 'smartworking'
  
  // WebDAV configuration states
  const [cloudConfig, setCloudConfig] = useState(null);
  const [cloudConfigForm, setCloudConfigForm] = useState({
    provider: 'aruba_drive',  // Aruba Drive Standard funziona meglio
    webdav_url: 'https://vkbu5u.arubadrive.com/',
    username: '',
    password: '',
    base_path: '/'
  });
  const [availableFolders, setAvailableFolders] = useState([]);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudError, setCloudError] = useState('');
  const [cloudSuccess, setCloudSuccess] = useState('');

  useEffect(() => {
    loadDashboardData();
    loadCloudConfig();
  }, []);

  const loadCloudConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/cloud-storage-config`);
      if (response.data.configured) {
        setCloudConfig(response.data.config);
      }
    } catch (error) {
      console.error('Errore caricamento config cloud:', error);
    }
  };

  const saveCloudConfig = async (e) => {
    e.preventDefault();
    setCloudLoading(true);
    setCloudError('');
    setCloudSuccess('');

    try {
      const providerName = cloudConfigForm.provider === 'aruba_drive' 
        ? 'Aruba Drive' 
        : cloudConfigForm.provider === 'aruba_nextcloud_driver'
        ? 'Aruba Drive Avanzato'
        : cloudConfigForm.provider === 'nextcloud'
        ? 'Nextcloud'
        : 'WebDAV Generico';
      
      await axios.post(`${API}/admin/cloud-storage-config`, {
        ...cloudConfigForm,
        name: providerName,
        storage_type: 'webdav',
        enabled: true
      });

      setCloudSuccess(`Configurazione ${providerName} salvata e testata con successo!`);
      await loadCloudConfig();
      
      // Reset form
      setCloudConfigForm({
        provider: 'aruba_drive',  // Default ad Aruba Drive standard
        webdav_url: 'https://vkbu5u.arubadrive.com/',
        username: '',
        password: '',
        base_path: '/'
      });

    } catch (error) {
      setCloudError(formatErrorMessage(error));
    } finally {
      setCloudLoading(false);
    }
  };

  const testCloudConnection = async () => {
    setCloudLoading(true);
    setCloudError('');
    setCloudSuccess('');

    try {
      const response = await axios.post(`${API}/admin/cloud-storage-test`);
      
      if (response.data.status === 'connected') {
        setCloudSuccess(`Connessione riuscita! ${response.data.message}`);
        if (response.data.folder_structure === 'ok') {
          setCloudSuccess(prev => prev + ' Struttura cartelle verificata.');
        }
      } else {
        setCloudError(`Test connessione fallito: ${response.data.message}`);
      }

    } catch (error) {
      setCloudError(formatErrorMessage(error));
    } finally {
      setCloudLoading(false);
    }
  };

  const loadAvailableFolders = async () => {
    setCloudLoading(true);
    try {
      const response = await axios.get(`${API}/admin/nextcloud-folders`);
      setAvailableFolders(response.data.folders || []);
    } catch (error) {
      console.error('Errore caricamento cartelle:', error);
      setAvailableFolders([]);
    } finally {
      setCloudLoading(false);
    }
  };

  const deleteCloudConfig = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare la configurazione Nextcloud?')) {
      return;
    }

    setCloudLoading(true);
    try {
      await axios.delete(`${API}/admin/cloud-storage-config/${cloudConfig.id}`);
      setCloudConfig(null);
      setAvailableFolders([]);
      setCloudSuccess('Configurazione eliminata con successo');
    } catch (error) {
      setCloudError(formatErrorMessage(error));
    } finally {
      setCloudLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [statsResponse, requestsResponse, employeesResponse] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/requests`),
        axios.get(`${API}/admin/employees`)
      ]);
      
      setStats(statsResponse.data);
      setRequests(requestsResponse.data);
      setEmployees(employeesResponse.data);
    } catch (error) {
      console.error('Errore nel caricamento dei dati:', error);
    }
  };

  const handleRequestAction = async (requestId, action, notes = '') => {
    try {
      console.log('Sending request action:', { requestId, action, notes });
      
      const response = await axios.put(`${API}/admin/requests/${requestId}`, {
        request_id: requestId,
        action,
        notes: notes || ""
      });
      
      console.log('Request action successful:', response.data);
      await loadDashboardData(); // Reload data
    } catch (error) {
      console.error('Errore nell\'azione sulla richiesta:', error);
      console.error('Error details:', error.response?.data);
      alert(`Errore: ${error.response?.data?.detail || 'Errore sconosciuto'}`);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/admin/requests/${requestId}`);
      await loadDashboardData(); // Reload data
      alert('Richiesta eliminata con successo');
    } catch (error) {
      console.error('Errore nell\'eliminazione della richiesta:', error);
      alert(error.response?.data?.detail || 'Errore nell\'eliminazione della richiesta');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Navigation */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <button
            onClick={() => setCurrentPage('dashboard')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'dashboard' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Dashboard</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('employees')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'employees' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Users className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Dipendenti</span>
            <span className="sm:hidden">Dipendenti</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('settings')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'settings' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Impostazioni</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('payslips')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'payslips' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Documenti Dipendente</span>
            <span className="sm:hidden">Doc</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('pdf-splitter')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'pdf-splitter' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Scissors className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">PDF Splitter</span>
            <span className="sm:hidden">PDF</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('room-booking')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'room-booking' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Prenotazione Sale</span>
            <span className="sm:hidden">Sale</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('communications')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'communications' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Comunicazioni</span>
            <span className="sm:hidden">Comm</span>
          </button>
          
          {user.unit === 'Amministrazione' && (
            <button
              onClick={() => setCurrentPage('deadlines')}
              className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
                currentPage === 'deadlines' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Bell className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Scadenzario</span>
              <span className="sm:hidden">Scad</span>
            </button>
          )}
          
          <button
            onClick={() => setCurrentPage('conventions')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'conventions' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Image className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Convenzioni</span>
            <span className="sm:hidden">Conv</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('campaign-reports')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'campaign-reports' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Report Campagne</span>
            <span className="sm:hidden">Report</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('solar-installations')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'solar-installations' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Installazioni</span>
            <span className="sm:hidden">Install</span>
          </button>
          
          <button
            onClick={() => setCurrentPage('cloud-settings')}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
              currentPage === 'cloud-settings' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Archive className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Cloud Storage</span>
            <span className="sm:hidden">Cloud</span>
          </button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {currentPage === 'dashboard' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 sm:p-6 rounded-xl text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm sm:text-base">Ferie in attesa</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.pending_ferie}</p>
                </div>
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-blue-200" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 sm:p-6 rounded-xl text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm sm:text-base">Permessi in attesa</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.pending_permessi}</p>
                </div>
                <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-green-200" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 sm:p-6 rounded-xl text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm sm:text-base">Malattie in attesa</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.pending_malattie}</p>
                </div>
                <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-orange-200" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-4 sm:p-6 rounded-xl text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-sm sm:text-base">Smartworking in attesa</p>
                  <p className="text-2xl sm:text-3xl font-bold">{stats.pending_smartworking}</p>
                </div>
                <Home className="h-6 w-6 sm:h-8 sm:w-8 text-cyan-200" />
              </div>
            </div>
          </div>
          
          {/* Total pending card */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 sm:p-6 rounded-xl text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm sm:text-base">Totale richieste in attesa</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.total_pending}</p>
              </div>
              <Bell className="h-6 w-6 sm:h-8 sm:w-8 text-purple-200" />
            </div>
          </div>

          {/* Requests List */}
          <RequestsList 
            requests={requests} 
            onAction={handleRequestAction}
            onDeleteRequest={handleDeleteRequest}
            processedRequestsCollapsed={processedRequestsCollapsed}
            setProcessedRequestsCollapsed={setProcessedRequestsCollapsed}
            processedRequestsFilter={processedRequestsFilter}
            setProcessedRequestsFilter={setProcessedRequestsFilter}
          />
        </>
      )}

      {currentPage === 'employees' && (
        <EmployeeManagement employees={employees} onRefresh={loadDashboardData} onViewEmployee={(employee) => {
          setSelectedEmployee(employee);
          setCurrentPage('employee-details');
        }} />
      )}

      {currentPage === 'employee-details' && selectedEmployee && (
        <EmployeeDetails 
          employee={selectedEmployee} 
          onBack={() => {
            setCurrentPage('employees');
            setSelectedEmployee(null);
          }} 
        />
      )}

      {currentPage === 'settings' && (
        <PasswordSettings user={user} />
      )}
      
      {currentPage === 'payslips' && (
        <PayslipManagement currentUser={user} />
      )}
      
      {currentPage === 'pdf-splitter' && (
        <PDFSplitterTool />
      )}
      
      {currentPage === 'room-booking' && (
        <RoomBookingSystem user={user} />
      )}
      
      {currentPage === 'communications' && (
        <CompanyCommunications user={user} />
      )}
      
      {currentPage === 'deadlines' && (
        <DeadlineScheduler user={user} />
      )}
      
      {currentPage === 'conventions' && (
        <ConventionsManager user={user} />
      )}
      
      {currentPage === 'campaign-reports' && (
        <CampaignReports user={user} />
      )}
      
      {currentPage === 'solar-installations' && (
        <SolarInstallations user={user} />
      )}
      
      {currentPage === 'cloud-settings' && (
        <div className="space-y-6">
          <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
            <h2 className="text-xl font-semibold text-slate-800 mb-6">Configurazione Cloud Storage</h2>
            <p className="text-slate-600 mb-6">
              Configura la connessione al tuo cloud storage per salvare le buste paga direttamente nel cloud.
              Supportiamo Aruba Drive e Nextcloud via WebDAV.
            </p>
            
            {cloudConfig ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-green-800">✅ {cloudConfig.name} Configurato</h3>
                      <div className="text-sm text-green-700 mt-2 space-y-1">
                        <p><strong>URL:</strong> {cloudConfig.webdav_url}</p>
                        <p><strong>Username:</strong> {cloudConfig.username}</p>
                        <p><strong>Cartella Buste Paga:</strong> {cloudConfig.base_path}</p>
                        <p><strong>Stato:</strong> <span className={`font-medium ${
                          cloudConfig.connection_status === 'connected' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {cloudConfig.connection_status === 'connected' ? 'Connesso' : 'Errore Connessione'}
                        </span></p>
                        {cloudConfig.available_folders && cloudConfig.available_folders.length > 0 && (
                          <p><strong>Cartelle Disponibili:</strong> {cloudConfig.available_folders.length}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={testCloudConnection}
                        disabled={cloudLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {cloudLoading ? 'Test...' : 'Testa Connessione'}
                      </button>
                      <button
                        onClick={loadAvailableFolders}
                        disabled={cloudLoading}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {cloudLoading ? 'Caricamento...' : 'Mostra Cartelle'}
                      </button>
                      <button
                        onClick={deleteCloudConfig}
                        disabled={cloudLoading}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={saveCloudConfig} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Provider Cloud Storage *
                  </label>
                  <select
                    value={cloudConfigForm.provider}
                    onChange={(e) => {
                      const provider = e.target.value;
                      setCloudConfigForm({
                        ...cloudConfigForm, 
                        provider,
                        webdav_url: provider === 'aruba_nextcloud_driver'
                          ? 'https://vkbu5u.arubadrive.com/'
                          : provider === 'aruba_drive' 
                          ? 'https://vkbu5u.arubadrive.com/' 
                          : 'https://yourserver.com/remote.php/dav/files/username/',
                        base_path: '/'
                      });
                    }}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="aruba_drive">✅ Aruba Drive (Consigliato)</option>
                    <option value="aruba_nextcloud_driver">🔧 Aruba Drive Avanzato (Sperimentale)</option>
                    <option value="nextcloud">☁️ Nextcloud</option>
                    <option value="generic">🔗 Altri WebDAV</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {cloudConfigForm.provider === 'aruba_drive' 
                      ? 'URL Aruba Drive *' 
                      : cloudConfigForm.provider === 'aruba_nextcloud_driver' 
                      ? 'URL Aruba Drive Avanzato *' 
                      : cloudConfigForm.provider === 'nextcloud'
                      ? 'URL Nextcloud WebDAV *'
                      : 'URL WebDAV *'}
                  </label>
                  <input
                    type="url"
                    value={cloudConfigForm.webdav_url}
                    onChange={(e) => setCloudConfigForm({...cloudConfigForm, webdav_url: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={
                      cloudConfigForm.provider === 'aruba_drive'
                        ? 'https://vkbu5u.arubadrive.com/' 
                        : cloudConfigForm.provider === 'aruba_nextcloud_driver'
                        ? 'https://vkbu5u.arubadrive.com/'
                        : cloudConfigForm.provider === 'nextcloud'
                        ? 'https://tuonextcloud.com/remote.php/dav/files/username/'
                        : 'https://yourserver.com/dav/'
                    }
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {cloudConfigForm.provider === 'aruba_drive'
                      ? '✅ URL Aruba Drive - configurazione standard e testata (es: https://vkbu5u.arubadrive.com/)'
                      : cloudConfigForm.provider === 'aruba_nextcloud_driver'
                      ? '🔧 URL Aruba Drive con connettore avanzato - può richiedere configurazione aggiuntiva'
                      : cloudConfigForm.provider === 'nextcloud'
                      ? '☁️ URL WebDAV Nextcloud (termina con /remote.php/dav/files/username/)'
                      : '🔗 URL WebDAV del tuo server di storage'
                    }
                  </p>
                  {cloudConfigForm.provider === 'aruba_drive' && (
                    <div className="bg-green-50 border border-green-200 rounded p-3 mt-2">
                      <p className="text-green-800 text-sm font-medium mb-2">✅ Aruba Drive - Configurazione Consigliata</p>
                      <div className="text-green-700 text-xs space-y-1">
                        <p><strong>✅</strong> Connessione WebDAV standard testata e funzionante</p>
                        <p><strong>✅</strong> Compatibilità ottimale con Aruba Drive</p>
                        <p><strong>✅</strong> Salvataggio automatico delle buste paga nel cloud</p>
                        <p><strong>✅</strong> Inserisci URL, username e password del tuo account Aruba Drive</p>
                      </div>
                    </div>
                  )}
                  {cloudConfigForm.provider === 'aruba_nextcloud_driver' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-2">
                      <p className="text-yellow-800 text-sm font-medium mb-2">⚠️ Modalità Avanzata (Sperimentale)</p>
                      <div className="text-yellow-700 text-xs space-y-1">
                        <p><strong>⚠️</strong> Questa modalità usa tecniche avanzate di connessione</p>
                        <p><strong>⚠️</strong> Potrebbe richiedere configurazioni aggiuntive</p>
                        <p><strong>✅</strong> Raccomandiamo di usare "Aruba Drive (Consigliato)" per stabilità ottimale</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Username *
                    </label>
                    <input
                      type="text"
                      value={cloudConfigForm.username}
                      onChange={(e) => setCloudConfigForm({...cloudConfigForm, username: e.target.value})}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={
                        cloudConfigForm.provider === 'aruba_nextcloud_driver' || cloudConfigForm.provider === 'aruba_drive'
                          ? 'Username Aruba Drive'
                          : 'tuo_username'
                      }
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {cloudConfigForm.provider === 'aruba_nextcloud_driver' || cloudConfigForm.provider === 'aruba_drive'
                        ? 'Username del tuo account Aruba Drive'
                        : 'Username per accedere al server WebDAV'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={cloudConfigForm.password}
                      onChange={(e) => setCloudConfigForm({...cloudConfigForm, password: e.target.value})}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="••••••••"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {cloudConfigForm.provider === 'aruba_nextcloud_driver' || cloudConfigForm.provider === 'aruba_drive'
                        ? 'Password del tuo account Aruba Drive'
                        : 'Password per accedere al server WebDAV'
                      }
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Cartella per Buste Paga
                  </label>
                  
                  {cloudConfigForm.provider === 'aruba_drive' && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                      <p className="text-blue-800 text-sm font-medium mb-2">📁 Configurazione Cartella Aruba Drive</p>
                      <div className="text-blue-700 text-xs space-y-1">
                        <p><strong>Opzione 1:</strong> Lascia "/" per salvare nella cartella principale</p>
                        <p><strong>Opzione 2:</strong> Inserisci il nome della tua cartella (es: /BustePaga, /Documenti/BustePaga)</p>
                        <p className="italic">Nota: Per motivi di sicurezza, Aruba Drive potrebbe non consentire la visualizzazione automatica delle cartelle.</p>
                      </div>
                    </div>
                  )}
                  
                  {availableFolders.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm text-slate-600 mb-2">Seleziona una cartella esistente:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-3 max-h-32 overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => setCloudConfigForm({...cloudConfigForm, base_path: '/'})}
                          className={`p-2 text-left rounded border text-sm ${
                            cloudConfigForm.base_path === '/' 
                              ? 'bg-blue-100 border-blue-300 text-blue-800' 
                              : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                          }`}
                        >
                          📁 / (Root)
                        </button>
                        {availableFolders.map((folder, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setCloudConfigForm({...cloudConfigForm, base_path: folder.path})}
                            className={`p-2 text-left rounded border text-sm ${
                              cloudConfigForm.base_path === folder.path 
                                ? 'bg-blue-100 border-blue-300 text-blue-800' 
                                : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            📁 {folder.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <input
                    type="text"
                    value={cloudConfigForm.base_path}
                    onChange={(e) => setCloudConfigForm({...cloudConfigForm, base_path: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="/"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Cartella dove verranno salvate le buste paga. Usa "/" per la cartella principale o seleziona una cartella esistente sopra.
                  </p>
                  
                  {cloudConfigForm.provider === 'nextcloud_aruba' && (
                    <div className="bg-green-50 border border-green-200 rounded p-3 mt-3">
                      <p className="text-green-800 text-sm font-medium mb-2">✅ Perfetto! Hai già configurato Nextcloud Desktop con Aruba Drive</p>
                      <div className="text-green-700 text-xs space-y-1">
                        <p><strong>Ora inserisci:</strong></p>
                        <p>• <strong>URL:</strong> L'indirizzo del tuo server Nextcloud personale</p>
                        <p>• <strong>Username:</strong> Il tuo username Nextcloud (non Aruba)</p>
                        <p>• <strong>Password:</strong> La tua password Nextcloud (non Aruba)</p>
                        <p><strong>Il sistema si collegherà a Nextcloud → Nextcloud mostrerà le cartelle Aruba Drive!</strong></p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2 mt-2">
                    {cloudConfigForm.webdav_url && cloudConfigForm.username && cloudConfigForm.password && (
                      <button
                        type="button"
                        onClick={loadAvailableFolders}
                        disabled={cloudLoading}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm flex items-center gap-2"
                      >
                        <span>📁</span>
                        {cloudLoading ? 'Caricamento...' : 'Carica Cartelle'}
                      </button>
                    )}
                  </div>
                </div>
                
                {cloudError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {cloudError}
                  </div>
                )}
                
                {cloudSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    {cloudSuccess}
                  </div>
                )}
                
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={cloudLoading}
                    className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-all"
                  >
                    {cloudLoading ? 'Configurazione...' : 'Salva e Testa Configurazione'}
                  </button>
                </div>
              </form>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <h4 className="font-medium text-blue-800 mb-2">💡 Come Funziona</h4>
              <div className="text-sm text-blue-700 space-y-2">
                <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
                  <p className="text-green-800 font-medium mb-1">🎯 Configurazione Consigliata: Nextcloud Desktop + Aruba Drive</p>
                  <p className="text-green-700 text-xs">
                    Usa l'app desktop Nextcloud con il connettore Aruba Drive per accedere alle cartelle tramite Nextcloud.
                  </p>
                </div>
                
                <p><strong>1. Nextcloud Desktop:</strong> Installa l'app desktop Nextcloud sul tuo computer</p>
                <p><strong>2. Connettore Aruba:</strong> Configura Aruba Drive come "External Storage" in Nextcloud</p>
                <p><strong>3. Sincronizzazione:</strong> Nextcloud mostra le cartelle Aruba Drive come cartelle locali</p>
                <p><strong>4. Nostro Sistema:</strong> Si collega a Nextcloud per accedere alle cartelle Aruba</p>
                <p><strong>5. Fallback:</strong> Se il cloud non è disponibile, salva nel database interno</p>
                
                <div className="bg-blue-100 p-3 rounded mt-3">
                  <p className="font-medium mb-1">Esempio configurazione (le TUE credenziali):</p>
                  <div className="font-mono text-xs space-y-1">
                    <p>URL: https://tuo-nextcloud.com/remote.php/dav/files/tuo_user_nextcloud/</p>
                    <p>Username: tuo_username_nextcloud</p>
                    <p>Password: tua_password_nextcloud</p>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-3">
                  <p className="text-yellow-800 text-xs">
                    <strong>Client Desktop Nextcloud:</strong> Questa configurazione usa il client desktop Nextcloud che si collega ad Aruba Drive, permettendo un accesso stabile alle cartelle cloud.
                  </p>
                </div>
                
                {cloudError && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mt-3">
                    <p className="text-red-800 font-medium text-sm mb-2">🚨 Problemi di Connessione?</p>
                    <div className="text-red-700 text-xs space-y-1">
                      <p><strong>1. URL non trovato:</strong> Prova gli URL suggeriti sopra o contatta Aruba</p>
                      <p><strong>2. Accesso negato:</strong> Il tuo account Aruba potrebbe non avere WebDAV abilitato</p>
                      <p><strong>3. Credenziali errate:</strong> Usa esattamente username/password del pannello Aruba</p>
                      <p><strong>4. Alternative:</strong> Seleziona "Aruba Drive Diretto" o usa il database interno (sempre funzionante)</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Requests List Component
const RequestsList = ({ 
  requests, 
  onAction, 
  processedRequestsCollapsed, 
  setProcessedRequestsCollapsed, 
  processedRequestsFilter, 
  setProcessedRequestsFilter,
  onDeleteRequest 
}) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  const pendingRequests = requests.filter(r => r.status === 'pending' || r.status === 'unit_approved');
  const processedRequests = requests.filter(r => r.status !== 'pending' && r.status !== 'unit_approved');

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const handleAction = async (request, action) => {
    await onAction(request.id, action, adminNotes);
    setSelectedRequest(null);
    setAdminNotes('');
  };

  const handleDeleteRequest = async (requestId) => {
    if (onDeleteRequest) {
      await onDeleteRequest(requestId);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Pending Requests */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6 flex items-center">
          <Bell className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-orange-500" />
          Richieste in attesa ({pendingRequests.length})
        </h2>
        
        {pendingRequests.length === 0 ? (
          <div className="text-center py-6 sm:py-8 text-slate-500 text-sm sm:text-base">
            Nessuna richiesta in attesa
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {pendingRequests.map((request) => (
              <div key={request.id} className="border border-slate-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-all">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 sm:mb-4 space-y-2 sm:space-y-0">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800 text-base sm:text-lg capitalize flex items-center gap-2">
                      {request.type}
                      {request.status === 'unit_approved' && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-200">
                          Pre-approvata dal Responsabile
                        </span>
                      )}
                    </h3>
                    <p className="text-slate-600 text-sm sm:text-base">
                      <span className="font-medium">{request.username}</span>
                      <span className="hidden sm:inline"> ({request.user_email})</span>
                      <span> - {formatDate(request.created_at)}</span>
                    </p>
                  </div>
                  <div className="flex space-x-2 justify-end">
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                    >
                      Gestisci
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 text-sm sm:text-base">
                  {request.type === 'ferie' && (
                    <>
                      <div>
                        <span className="font-medium text-slate-600">Dal:</span>
                        <span className="ml-2">{formatDate(request.start_date)}</span>
                      </div>
                      <div>
                        <span className="font-medium text-slate-600">Al:</span>
                        <span className="ml-2">{formatDate(request.end_date)}</span>
                      </div>
                    </>
                  )}
                  
                  {request.type === 'permesso' && (
                    <>
                      <div>
                        <span className="font-medium text-slate-600">Data:</span>
                        <span className="ml-2">{formatDate(request.permit_date)}</span>
                      </div>
                      <div>
                        <span className="font-medium text-slate-600">Orario:</span>
                        <span className="ml-2">{formatTime(request.start_time)} - {formatTime(request.end_time)}</span>
                      </div>
                    </>
                  )}
                  
                  {request.type === 'malattia' && (
                    <>
                      <div>
                        <span className="font-medium text-slate-600">Inizio:</span>
                        <span className="ml-2">{formatDate(request.sick_start_date)}</span>
                      </div>
                      <div>
                        <span className="font-medium text-slate-600">Giorni:</span>
                        <span className="ml-2">{request.sick_days}</span>
                      </div>
                      <div className="md:col-span-2">
                        <span className="font-medium text-slate-600">Protocollo:</span>
                        <span className="ml-2 font-mono text-xs bg-slate-100 px-2 py-1 rounded">{request.protocol_code}</span>
                      </div>
                    </>
                  )}
                  
                  {request.type === 'smartworking' && (
                    <div>
                      <span className="font-medium text-slate-600">Data:</span>
                      <span className="ml-2">{formatDate(request.smartworking_date)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request Action Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              Gestisci richiesta {selectedRequest.type} - {selectedRequest.username}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Note (opzionale)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows="3"
                placeholder="Aggiungi note..."
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => handleAction(selectedRequest, 'approve')}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700"
              >
                Approva
              </button>
              <button
                onClick={() => handleAction(selectedRequest, 'reject')}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700"
              >
                Rifiuta
              </button>
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setAdminNotes('');
                }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processed Requests - Collapsible Section */}
      {processedRequests.length > 0 && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
          {/* Header with collapse button */}
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-800">
              Richieste elaborate ({processedRequests.filter(r => 
                processedRequestsFilter === 'all' || r.type === processedRequestsFilter
              ).length})
            </h2>
            <button
              onClick={() => setProcessedRequestsCollapsed(!processedRequestsCollapsed)}
              className="flex items-center space-x-1 sm:space-x-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <span className="text-xs sm:text-sm font-medium">
                {processedRequestsCollapsed ? 'Espandi' : 'Comprimi'}
              </span>
              {processedRequestsCollapsed ? (
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
              ) : (
                <ChevronUp className="h-3 w-3 sm:h-4 sm:w-4" />
              )}
            </button>
          </div>

          {/* Content - shown when not collapsed */}
          {!processedRequestsCollapsed && (
            <>
              {/* Filter buttons */}
              <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
                <button
                  onClick={() => setProcessedRequestsFilter('all')}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    processedRequestsFilter === 'all'
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Filter className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  <span className="hidden sm:inline">Tutte</span> ({processedRequests.length})
                </button>
                <button
                  onClick={() => setProcessedRequestsFilter('ferie')}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    processedRequestsFilter === 'ferie'
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  <span className="hidden sm:inline">Ferie</span> ({processedRequests.filter(r => r.type === 'ferie').length})
                </button>
                <button
                  onClick={() => setProcessedRequestsFilter('permesso')}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    processedRequestsFilter === 'permesso'
                      ? 'bg-orange-100 text-orange-800 border border-orange-200'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Clock className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  <span className="hidden sm:inline">Permessi</span> ({processedRequests.filter(r => r.type === 'permesso').length})
                </button>
                <button
                  onClick={() => setProcessedRequestsFilter('malattia')}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    processedRequestsFilter === 'malattia'
                      ? 'bg-red-100 text-red-800 border border-red-200'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Activity className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  <span className="hidden sm:inline">Malattia</span> ({processedRequests.filter(r => r.type === 'malattia').length})
                </button>
                <button
                  onClick={() => setProcessedRequestsFilter('smartworking')}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    processedRequestsFilter === 'smartworking'
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Home className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  <span className="hidden sm:inline">Smartworking</span> ({processedRequests.filter(r => r.type === 'smartworking').length})
                </button>
              </div>

              {/* Filtered requests list */}
              <div className="space-y-2 sm:space-y-3">
                {processedRequests
                  .filter(request => processedRequestsFilter === 'all' || request.type === processedRequestsFilter)
                  .slice(0, 15)
                  .map((request) => (
                  <div key={request.id} className="border border-slate-200 rounded-lg p-3 sm:p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        {/* Type icon */}
                        <div className={`p-1.5 sm:p-2 rounded-lg ${
                          request.type === 'ferie' 
                            ? 'bg-green-100 text-green-600' 
                            : request.type === 'permesso'
                            ? 'bg-orange-100 text-orange-600'
                            : request.type === 'smartworking'
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {request.type === 'ferie' ? (
                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : request.type === 'permesso' ? (
                            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : request.type === 'smartworking' ? (
                            <Home className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="font-medium capitalize text-sm sm:text-base">{request.type}</span>
                          <span className="text-slate-600 ml-1 sm:ml-2 text-sm sm:text-base">- {request.username}</span>
                          {request.admin_notes && (
                            <p className="text-xs sm:text-sm text-slate-500 mt-1">Note: {request.admin_notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${
                          request.status === 'approved' 
                            ? 'bg-green-100 text-green-800 border-green-200' 
                            : 'bg-red-100 text-red-800 border-red-200'
                        }`}>
                          {request.status === 'approved' ? (
                            <>
                              <CheckCircle className="h-3 w-3 inline mr-1" />
                              <span className="hidden sm:inline">Approvata</span>
                              <span className="sm:hidden">OK</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3 inline mr-1" />
                              <span className="hidden sm:inline">Rifiutata</span>
                              <span className="sm:hidden">NO</span>
                            </>
                          )}
                        </span>
                        <span className="text-slate-500 text-xs sm:text-sm">
                          {formatDate(request.updated_at || request.created_at)}
                        </span>
                        <button
                          onClick={() => {
                            if (window.confirm(`Sei sicuro di voler eliminare questa richiesta di ${request.type}?`)) {
                              handleDeleteRequest(request.id);
                            }
                          }}
                          className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Elimina richiesta"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Show more indicator */}
              {processedRequests.filter(r => 
                processedRequestsFilter === 'all' || r.type === processedRequestsFilter
              ).length > 15 && (
                <div className="text-center pt-3 sm:pt-4">
                  <p className="text-xs sm:text-sm text-slate-500">
                    Mostrando prime 15 richieste di {processedRequests.filter(r => 
                      processedRequestsFilter === 'all' || r.type === processedRequestsFilter
                    ).length}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Employee Management Component
const EmployeeManagement = ({ employees, onRefresh, onViewEmployee }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ 
    username: '', 
    email: '', 
    password: '', 
    role: 'employee', 
    unit: '',
    units: []
  });
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [employeeToAction, setEmployeeToAction] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // New states for top-page notifications
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    loadEmployees();
  }, [statusFilter, searchTerm]);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      
      const response = await axios.get(`${API}/admin/employees?${params}`);
      setFilteredEmployees(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei dipendenti:', error);
      setError('Errore nel caricamento dei dipendenti');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await axios.post(`${API}/admin/employees`, {
        ...newEmployee,
        units: newEmployee.units.length > 0 ? newEmployee.units : (newEmployee.unit ? [newEmployee.unit] : [])
      });
      setNewEmployee({ username: '', email: '', password: '', role: 'employee', unit: '', units: [] });
      setShowCreateForm(false);
      setSuccessMessage(`✅ Dipendente ${newEmployee.username} creato con successo`);
      loadEmployees();
      onRefresh();
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella creazione del dipendente');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await axios.put(`${API}/admin/employees/${editingEmployee.id}`, {
        username: editingEmployee.username,
        email: editingEmployee.email,
        password: editingEmployee.password || undefined,
        role: editingEmployee.role,
        units: editingEmployee.units || []
      });
      setSuccessMessage(`✅ Dipendente ${editingEmployee.username} modificato con successo`);
      setEditingEmployee(null);
      loadEmployees();
      onRefresh();
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella modifica del dipendente');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeAction = async (employee, action) => {
    setEmployeeToAction(employee);
    setActionType(action);
    setShowConfirmation(true);
    setError('');
    setSuccessMessage('');
    
    // Scroll to top to show confirmation message
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmEmployeeAction = async () => {
    if (!employeeToAction || !actionType) return;
    
    setActionLoading(true);
    setError('');
    
    try {
      if (actionType === 'delete') {
        await axios.delete(`${API}/admin/employees/${employeeToAction.id}`);
        setSuccessMessage(`✅ Dipendente ${employeeToAction.username} cancellato con successo`);
      } else {
        await axios.put(`${API}/admin/employees/${employeeToAction.id}/status`, {
          status: actionType
        });
        
        const actionMessages = {
          disabled: `✅ Dipendente ${employeeToAction.username} disabilitato con successo`,
          active: `✅ Dipendente ${employeeToAction.username} riattivato con successo`,
          archived: `✅ Dipendente ${employeeToAction.username} archiviato con successo`
        };
        
        setSuccessMessage(actionMessages[actionType] || `✅ Stato dipendente aggiornato con successo`);
      }
      
      setEmployeeToAction(null);
      setActionType('');
      setShowConfirmation(false);
      loadEmployees();
      onRefresh();
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      console.error('Errore nell\'azione:', error);
      setError(error.response?.data?.detail || `Errore nell'${actionType === 'delete' ? 'eliminazione' : 'aggiornamento'} del dipendente`);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelEmployeeAction = () => {
    setEmployeeToAction(null);
    setActionType('');
    setShowConfirmation(false);
    setError('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800 border-green-200', text: 'Attivo', icon: CheckCircle },
      disabled: { color: 'bg-red-100 text-red-800 border-red-200', text: 'Disabilitato', icon: XCircle },
      archived: { color: 'bg-gray-100 text-gray-800 border-gray-200', text: 'Archiviato', icon: Archive }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium border ${config.color}`}>
        <Icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
        <span className="hidden xs:inline">{config.text}</span>
      </span>
    );
  };

  const getActionButtons = (employee) => {
    const buttons = [];
    
    // Edit button always available - more compact for mobile
    buttons.push(
      <button
        key="edit"
        onClick={() => setEditingEmployee({...employee, password: '', units: employee.units || (employee.unit ? [employee.unit] : [])})}
        className="flex items-center justify-center bg-blue-600 text-white px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm hover:bg-blue-700 transition-colors min-w-0 flex-1 sm:flex-none"
        title="Modifica dipendente"
      >
        <Edit className="h-3 w-3 sm:mr-1" />
        <span className="hidden sm:inline ml-1">Modifica</span>
      </button>
    );
    
    if (employee.status === 'active') {
      buttons.push(
        <button
          key="disable"
          onClick={() => handleEmployeeAction(employee, 'disabled')}
          className="flex items-center justify-center bg-orange-600 text-white px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm hover:bg-orange-700 transition-colors min-w-0 flex-1 sm:flex-none"
          title="Disabilita dipendente"
        >
          <EyeOff className="h-3 w-3 sm:mr-1" />
          <span className="hidden sm:inline ml-1">Disabilita</span>
        </button>
      );
    } else if (employee.status === 'disabled') {
      buttons.push(
        <button
          key="activate"
          onClick={() => handleEmployeeAction(employee, 'active')}
          className="flex items-center justify-center bg-green-600 text-white px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm hover:bg-green-700 transition-colors min-w-0 flex-1 sm:flex-none"
          title="Riattiva dipendente"
        >
          <Eye className="h-3 w-3 sm:mr-1" />
          <span className="hidden sm:inline ml-1">Riattiva</span>
        </button>
      );
    }
    
    if (employee.status !== 'archived') {
      buttons.push(
        <button
          key="archive"
          onClick={() => handleEmployeeAction(employee, 'archived')}
          className="flex items-center justify-center bg-gray-600 text-white px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm hover:bg-gray-700 transition-colors min-w-0 flex-1 sm:flex-none"
          title="Archivia dipendente"
        >
          <Archive className="h-3 w-3 sm:mr-1" />
          <span className="hidden sm:inline ml-1">Archivia</span>
        </button>
      );
    } else {
      buttons.push(
        <button
          key="unarchive"
          onClick={() => handleEmployeeAction(employee, 'active')}
          className="flex items-center justify-center bg-blue-600 text-white px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm hover:bg-blue-700 transition-colors min-w-0 flex-1 sm:flex-none"
          title="Rimuovi dall'archivio"
        >
          <Eye className="h-3 w-3 sm:mr-1" />
          <span className="hidden sm:inline ml-1">Ripristina</span>
        </button>
      );
    }
    
    // Delete button only for employees without data - less prominent on mobile
    buttons.push(
      <button
        key="delete"
        onClick={() => handleEmployeeAction(employee, 'delete')}
        className="flex items-center justify-center bg-red-600 text-white px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm hover:bg-red-700 transition-colors min-w-0 flex-1 sm:flex-none"
        title="Cancella dipendente (solo se senza dati)"
      >
        <Trash2 className="h-3 w-3 sm:mr-1" />
        <span className="hidden sm:inline ml-1">Cancella</span>
      </button>
    );
    
    return buttons;
  };

  const getActionMessage = () => {
    if (!employeeToAction || !actionType) return '';
    
    const messages = {
      delete: `Sei sicuro di voler cancellare definitivamente ${employeeToAction.username}? Questa azione non può essere annullata e funziona solo se il dipendente non ha richieste o dati ferie associati.`,
      disabled: `Sei sicuro di voler disabilitare ${employeeToAction.username}? Non potrà più accedere al sistema.`,
      active: `Sei sicuro di voler riattivare ${employeeToAction.username}? Potrà nuovamente accedere al sistema.`,
      archived: `Sei sicuro di voler archiviare ${employeeToAction.username}? Sarà nascosto dalla vista normale ma i dati saranno conservati.`
    };
    
    return messages[actionType] || '';
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
      {/* Top Notifications Area */}
      {(successMessage || error || showConfirmation) && (
        <div className="mb-6">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                  <p className="text-green-800 font-medium">{successMessage}</p>
                </div>
                <button
                  onClick={() => setSuccessMessage('')}
                  className="text-green-600 hover:text-green-800 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-3" />
                  <p className="text-red-800 font-medium">{error}</p>
                </div>
                <button
                  onClick={() => setError('')}
                  className="text-red-600 hover:text-red-800 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          
          {/* Confirmation Message */}
          {showConfirmation && employeeToAction && (
            <div className={`border rounded-lg p-4 mb-4 ${
              actionType === 'delete' 
                ? 'bg-red-50 border-red-200' 
                : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <AlertCircle className={`h-5 w-5 mr-3 ${
                    actionType === 'delete' ? 'text-red-600' : 'text-orange-600'
                  }`} />
                  <div>
                    <h4 className={`font-semibold ${
                      actionType === 'delete' ? 'text-red-800' : 'text-orange-800'
                    }`}>
                      Conferma {actionType === 'delete' ? 'Cancellazione' : 'Modifica'}
                    </h4>
                    <p className={`text-sm mt-1 ${
                      actionType === 'delete' ? 'text-red-700' : 'text-orange-700'
                    }`}>
                      {getActionMessage()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={cancelEmployeeAction}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              {actionType === 'delete' && (
                <div className="bg-red-100 border border-red-300 p-3 rounded-lg mb-4">
                  <p className="text-red-800 text-sm font-medium">
                    ⚠️ Attenzione: La cancellazione è irreversibile!
                  </p>
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  onClick={confirmEmployeeAction}
                  disabled={actionLoading}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                    actionType === 'delete' 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  {actionLoading ? 'Elaborazione...' : 'Conferma'}
                </button>
                <button
                  onClick={cancelEmployeeAction}
                  disabled={actionLoading}
                  className="flex-1 bg-slate-200 text-slate-700 py-2 px-4 rounded-lg hover:bg-slate-300 transition-colors disabled:opacity-50"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800">Gestione Dipendenti</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center space-x-1 sm:space-x-2 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base w-fit"
        >
          <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">Nuovo Dipendente</span>
          <span className="sm:hidden">Nuovo</span>
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="mb-4 sm:mb-6 bg-slate-50 p-3 sm:p-4 rounded-lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-3 w-3 sm:h-4 sm:w-4" />
            <input
              type="text"
              placeholder="Cerca per username o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            />
          </div>
          
          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-3 w-3 sm:h-4 sm:w-4" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            >
              <option value="all">Tutti gli stati</option>
              <option value="active">Attivi</option>
              <option value="disabled">Disabilitati</option>
              <option value="archived">Archiviati</option>
            </select>
          </div>
        </div>
      </div>

      {showCreateForm && (
        <div className="mb-6 border border-slate-200 rounded-xl p-6 bg-slate-50">
          <h3 className="text-lg font-semibold mb-4">Crea Nuovo Dipendente</h3>
          <form onSubmit={handleCreateEmployee} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                <input
                  type="text"
                  value={newEmployee.username}
                  onChange={(e) => setNewEmployee({ ...newEmployee, username: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  value={newEmployee.password}
                  onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ruolo</label>
                <select
                  value={newEmployee.role}
                  onChange={(e) => setNewEmployee({ ...newEmployee, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="employee">Dipendente</option>
                  <option value="responsabile_unit">Responsabile Unit</option>
                  <option value="consulente">Consulente</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Unità {(newEmployee.role === 'responsabile_unit' || newEmployee.role === 'employee') && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <div className="border border-slate-300 rounded-lg p-3 bg-white space-y-2 max-h-48 overflow-y-auto">
                  {['CEO', 'Amministrazione', 'Presidi', 'Store', 'Agenti', 'HR', 'Digital', 'TLS', 'Fotovoltaico', 'Fibra'].map((unitOption) => (
                    <label key={unitOption} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={(newEmployee.units || []).includes(unitOption)}
                        onChange={(e) => {
                          const currentUnits = newEmployee.units || [];
                          if (e.target.checked) {
                            setNewEmployee({ 
                              ...newEmployee, 
                              units: [...currentUnits, unitOption],
                              unit: currentUnits.length === 0 ? unitOption : newEmployee.unit
                            });
                          } else {
                            const newUnits = currentUnits.filter(u => u !== unitOption);
                            setNewEmployee({ 
                              ...newEmployee, 
                              units: newUnits,
                              unit: newUnits[0] || ''
                            });
                          }
                        }}
                        className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">{unitOption}</span>
                    </label>
                  ))}
                </div>
                {(newEmployee.units || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(newEmployee.units || []).map((u) => (
                      <span key={u} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center gap-1">
                        {u}
                        <button
                          type="button"
                          onClick={() => {
                            const newUnits = (newEmployee.units || []).filter(unit => unit !== u);
                            setNewEmployee({
                              ...newEmployee,
                              units: newUnits,
                              unit: newUnits[0] || ''
                            });
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  {newEmployee.role === 'responsabile_unit' && 'Seleziona le unità che gestirà come responsabile'}
                  {newEmployee.role === 'employee' && 'Seleziona le unità di appartenenza'}
                  {(newEmployee.role === 'admin' || newEmployee.role === 'consulente') && 'Opzionale per admin e consulenti. Seleziona una o più unità.'}
                </p>
              </div>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Creazione...' : 'Crea Dipendente'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setError('');
                  setNewEmployee({ username: '', email: '', password: '', role: 'employee', unit: '', units: [] });
                }}
                className="px-6 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Employee List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="spinner" />
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            {searchTerm || statusFilter !== 'active' 
              ? 'Nessun dipendente trovato con i filtri attuali' 
              : 'Nessun dipendente ancora creato'
            }
          </div>
        ) : (
          filteredEmployees.map((employee) => (
            <div key={employee.id} className="border border-slate-200 rounded-lg p-3 sm:p-4 hover:shadow-sm transition-all">
              <div className="flex flex-col space-y-3">
                {/* Employee Info */}
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2">
                      <h3 className="font-semibold text-slate-800 text-sm sm:text-base truncate">{employee.username}</h3>
                      <div className="mt-1 sm:mt-0">
                        {getStatusBadge(employee.status)}
                      </div>
                    </div>
                    <p className="text-slate-600 text-xs sm:text-sm mb-1 truncate">{employee.email}</p>
                    
                    {/* Role and Unit Info */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-1">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-slate-500">Ruolo:</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          employee.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                          employee.role === 'consulente' ? 'bg-orange-100 text-orange-800' :
                          employee.role === 'responsabile_unit' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {employee.role === 'admin' ? 'Amministratore' :
                           employee.role === 'consulente' ? 'Consulente' :
                           employee.role === 'responsabile_unit' ? 'Responsabile Unit' :
                           'Dipendente'}
                        </span>
                      </div>
                      {(employee.units && employee.units.length > 0) ? (
                        <div className="flex items-center space-x-1 mt-1 sm:mt-0">
                          <span className="text-xs text-slate-500">Unità:</span>
                          <div className="flex flex-wrap gap-1">
                            {employee.units.map((u) => (
                              <span key={u} className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                                {u}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : employee.unit && (
                        <div className="flex items-center space-x-1 mt-1 sm:mt-0">
                          <span className="text-xs text-slate-500">Unità:</span>
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                            {employee.unit}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-xs text-slate-500">
                      Creato: {new Date(employee.created_at).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                </div>
                
                {/* Actions - Mobile Optimized */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-1">
                  {/* Primary action */}
                  <button
                    onClick={() => onViewEmployee(employee)}
                    className="flex items-center justify-center space-x-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm w-full sm:w-auto"
                  >
                    <User className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Dettagli</span>
                  </button>
                  
                  {/* Secondary actions in a row for mobile */}
                  <div className="flex space-x-1 sm:space-x-1">
                    {getActionButtons(employee)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Employee Modal */}
      {editingEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 text-slate-800">
              Modifica Dipendente: {editingEmployee.username}
            </h3>
            
            <form onSubmit={handleEditEmployee} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                <input
                  type="text"
                  value={editingEmployee.username}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, username: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editingEmployee.email}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nuova Password (lascia vuoto per non modificare)
                </label>
                <input
                  type="password"
                  value={editingEmployee.password}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Lascia vuoto per non modificare"
                />
              </div>
              
              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ruolo</label>
                <select
                  value={editingEmployee.role || 'employee'}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="employee">Dipendente</option>
                  <option value="responsabile_unit">Responsabile Unit</option>
                  <option value="consulente">Consulente</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>
              
              {/* Multi-Unit Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Unità Assegnate
                  {(editingEmployee.role === 'responsabile_unit' || editingEmployee.role === 'employee') && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <div className="border border-slate-300 rounded-lg p-3 bg-slate-50 space-y-2 max-h-48 overflow-y-auto">
                  {['CEO', 'Amministrazione', 'Presidi', 'Store', 'Agenti', 'HR', 'Digital', 'TLS', 'Fotovoltaico', 'Fibra'].map((unitOption) => (
                    <label key={unitOption} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-100 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={(editingEmployee.units || []).includes(unitOption)}
                        onChange={(e) => {
                          const currentUnits = editingEmployee.units || [];
                          if (e.target.checked) {
                            setEditingEmployee({ 
                              ...editingEmployee, 
                              units: [...currentUnits, unitOption] 
                            });
                          } else {
                            setEditingEmployee({ 
                              ...editingEmployee, 
                              units: currentUnits.filter(u => u !== unitOption) 
                            });
                          }
                        }}
                        className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">{unitOption}</span>
                    </label>
                  ))}
                </div>
                {(editingEmployee.units || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(editingEmployee.units || []).map((u) => (
                      <span key={u} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center gap-1">
                        {u}
                        <button
                          type="button"
                          onClick={() => setEditingEmployee({
                            ...editingEmployee,
                            units: (editingEmployee.units || []).filter(unit => unit !== u)
                          })}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Seleziona una o più unità. Un dipendente può appartenere a più unità.
                </p>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : 'Salva Modifiche'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingEmployee(null);
                    setError('');
                  }}
                  disabled={loading}
                  className="flex-1 bg-slate-200 text-slate-700 py-2 px-4 rounded-lg hover:bg-slate-300 transition-colors disabled:opacity-50"
                >
                  Annulla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Results Summary */}
      <div className="mt-4 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
        <p>
          Mostrando {filteredEmployees.length} dipendente{filteredEmployees.length !== 1 ? 'i' : ''}
          {statusFilter !== 'all' && (
            <span> con stato: <strong>{statusFilter === 'active' ? 'Attivo' : statusFilter === 'disabled' ? 'Disabilitato' : 'Archiviato'}</strong></span>
          )}
          {searchTerm && (
            <span> che corrispondono a: <strong>"{searchTerm}"</strong></span>
          )}
        </p>
      </div>
    </div>
  );
};

// Employee Details Component (Admin)
const EmployeeDetails = ({ employee, onBack }) => {
  const [stats, setStats] = useState(null);
  const [vacationSummary, setVacationSummary] = useState(null);
  const [permitSummary, setPermitSummary] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [editingDays, setEditingDays] = useState('');
  const [editingPermitYear, setEditingPermitYear] = useState(null);
  const [editingPermitHours, setEditingPermitHours] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadYears();
    loadVacationSummary();
    loadPermitSummary();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      loadStats();
    }
  }, [selectedYear]);

  const loadYears = async () => {
    try {
      const response = await axios.get(`${API}/admin/employees/${employee.id}/years`);
      setYears(response.data.years);
    } catch (error) {
      console.error('Errore nel caricamento degli anni:', error);
    }
  };

  const loadVacationSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/employees/${employee.id}/vacation-summary`);
      setVacationSummary(response.data);
    } catch (error) {
      console.error('Errore nel caricamento del riepilogo ferie:', error);
    }
  };

  const loadPermitSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/employees/${employee.id}/permit-summary`);
      setPermitSummary(response.data);
    } catch (error) {
      console.error('Errore nel caricamento del riepilogo permessi:', error);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/employees/${employee.id}/stats?year=${selectedYear}`);
      setStats(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle statistiche:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditVacationDays = (year, currentMaxDays) => {
    setEditingYear(year);
    setEditingDays(currentMaxDays.toString());
  };

  const handleSaveVacationDays = async (year) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/employees/${employee.id}/vacation-allowance/${year}`, {
        max_days: parseInt(editingDays)
      });
      
      setEditingYear(null);
      setEditingDays('');
      
      // Reload data
      await loadVacationSummary();
      if (selectedYear === year) {
        await loadStats();
      }
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      alert('Errore nel salvataggio dei giorni ferie');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPermitHours = (year, currentMaxHours) => {
    setEditingPermitYear(year);
    setEditingPermitHours(currentMaxHours.toString());
  };

  const handleSavePermitHours = async (year) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/employees/${employee.id}/permit-allowance/${year}`, {
        max_hours: parseFloat(editingPermitHours)
      });
      
      setEditingPermitYear(null);
      setEditingPermitHours('');
      
      // Reload data
      await loadPermitSummary();
      if (selectedYear === year) {
        await loadStats();
      }
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      alert('Errore nel salvataggio delle ore permessi');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingYear(null);
    setEditingDays('');
    setEditingPermitYear(null);
    setEditingPermitHours('');
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Torna alla lista</span>
            </button>
            <div className="h-6 w-px bg-slate-300" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center">
                <User className="h-6 w-6 mr-2 text-blue-600" />
                {employee.username}
              </h1>
              <p className="text-slate-600">{employee.email}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-slate-700">Anno:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Vacation Management */}
      {vacationSummary && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
            <Calculator className="h-5 w-5 mr-2 text-green-600" />
            Gestione Ferie - {employee.username}
          </h2>

          {/* Total Summary */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 rounded-xl text-white mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold mb-2">Riepilogo Ferie Totali</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-emerald-200 text-sm">Ferie Rimanenti Totali</p>
                    <p className="text-3xl font-bold">{vacationSummary.total_remaining_days}</p>
                    <p className="text-emerald-200 text-xs">giorni disponibili</p>
                  </div>
                  <div>
                    <p className="text-emerald-200 text-sm">Usate Quest'Anno</p>
                    <p className="text-3xl font-bold">{vacationSummary.used_this_year}</p>
                    <p className="text-emerald-200 text-xs">giorni nel {vacationSummary.current_year}</p>
                  </div>
                  <div>
                    <p className="text-emerald-200 text-sm">Anni Registrati</p>
                    <p className="text-3xl font-bold">{vacationSummary.vacation_by_year.length}</p>
                    <p className="text-emerald-200 text-xs">anni con ferie</p>
                  </div>
                </div>
              </div>
              <Calculator className="h-16 w-16 text-emerald-200" />
            </div>
          </div>

          {/* Vacation Breakdown by Year - Editable */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-blue-600" />
              Gestione Ferie per Anno (Modificabile)
            </h3>
            
            {vacationSummary.vacation_by_year.map((yearData) => (
              <div key={yearData.year} className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-4">
                    <h4 className="text-lg font-semibold text-slate-800">Anno {yearData.year}</h4>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      yearData.remaining_days > 0 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : yearData.remaining_days === 0
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {yearData.remaining_days > 0 ? 'Ferie Disponibili' : 
                       yearData.remaining_days === 0 ? 'Ferie Esaurite' : 'Ferie in Eccesso'}
                    </span>
                  </div>
                  
                  {editingYear === yearData.year ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={editingDays}
                        onChange={(e) => setEditingDays(e.target.value)}
                        className="w-20 px-2 py-1 border border-slate-300 rounded text-sm"
                        min="0"
                        max="50"
                      />
                      <span className="text-sm text-slate-600">giorni</span>
                      <button
                        onClick={() => handleSaveVacationDays(yearData.year)}
                        disabled={saving}
                        className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        <Save className="h-3 w-3" />
                        <span>Salva</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="bg-slate-300 text-slate-700 px-3 py-1 rounded text-sm hover:bg-slate-400"
                      >
                        Annulla
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEditVacationDays(yearData.year, yearData.max_days)}
                      className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <Edit className="h-4 w-4" />
                      <span>Modifica Ferie</span>
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <span className="text-slate-600 text-sm">Ferie Massime</span>
                    <p className="font-bold text-slate-800 text-lg">{yearData.max_days} giorni</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <span className="text-slate-600 text-sm">Utilizzate</span>
                    <p className="font-bold text-slate-800 text-lg">{yearData.used_days} giorni</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <span className="text-slate-600 text-sm">Riportate</span>
                    <p className="font-bold text-blue-600 text-lg">{yearData.carried_over_days} giorni</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <span className="text-slate-600 text-sm">Rimanenti</span>
                    <p className={`font-bold text-lg ${yearData.remaining_days >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {yearData.remaining_days} giorni
                    </p>
                  </div>
                  {yearData.year < vacationSummary.current_year && (
                    <div className="bg-white p-3 rounded border">
                      <span className="text-slate-600 text-sm">Riportabili</span>
                      <p className="font-bold text-emerald-600 text-lg">{yearData.can_carry_over} giorni</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Permit Management */}
      {permitSummary && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6 flex items-center">
            <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-orange-600" />
            Gestione Permessi - {employee.username}
          </h2>

          {/* Total Summary */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-600 p-4 sm:p-6 rounded-xl text-white mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-3 sm:mb-0">
                <h3 className="text-base sm:text-xl font-semibold mb-2">Riepilogo Permessi Totali</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <p className="text-orange-200">Ore Rimanenti Totali</p>
                    <p className="text-xl sm:text-3xl font-bold">{permitSummary.total_remaining_hours}</p>
                    <p className="text-orange-200 text-xs">ore disponibili</p>
                  </div>
                  <div>
                    <p className="text-orange-200">Usate Quest'Anno</p>
                    <p className="text-xl sm:text-3xl font-bold">{permitSummary.used_this_year}</p>
                    <p className="text-orange-200 text-xs">ore nel {permitSummary.current_year}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-orange-200">Accrual Mensile</p>
                    <p className="text-xl sm:text-3xl font-bold">2.5 <span className="text-sm">ore/mese</span></p>
                  </div>
                </div>
              </div>
              <Clock className="h-12 w-12 sm:h-16 sm:w-16 text-orange-200 self-center" />
            </div>
          </div>

          {/* Permit Breakdown by Year - Editable */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg font-semibold text-slate-800 flex items-center">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-orange-600" />
              Gestione Permessi per Anno (Modificabile)
            </h3>
            
            {permitSummary.permit_by_year.map((yearData) => (
              <div key={yearData.year} className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4 space-y-2 sm:space-y-0">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    <h4 className="text-base sm:text-lg font-semibold text-slate-800">Anno {yearData.year}</h4>
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium w-fit ${
                      yearData.remaining_hours > 0 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : yearData.remaining_hours === 0
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {yearData.remaining_hours > 0 ? 'Ore Disponibili' : 
                       yearData.remaining_hours === 0 ? 'Ore Esaurite' : 'Ore in Eccesso'}
                    </span>
                  </div>
                  
                  {editingPermitYear === yearData.year ? (
                    <div className="flex items-center space-x-2 w-full sm:w-auto">
                      <input
                        type="number"
                        value={editingPermitHours}
                        onChange={(e) => setEditingPermitHours(e.target.value)}
                        className="w-16 sm:w-20 px-2 py-1 border border-slate-300 rounded text-sm"
                        min="0"
                        max="100"
                        step="0.5"
                      />
                      <span className="text-sm text-slate-600">ore</span>
                      <button
                        onClick={() => handleSavePermitHours(yearData.year)}
                        disabled={saving}
                        className="flex items-center space-x-1 bg-green-600 text-white px-2 sm:px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        <Save className="h-3 w-3" />
                        <span className="hidden sm:inline">Salva</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center space-x-1 bg-gray-500 text-white px-2 sm:px-3 py-1 rounded text-sm hover:bg-gray-600"
                      >
                        <span className="hidden sm:inline">Annulla</span>
                        <span className="sm:hidden">✕</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEditPermitHours(yearData.year, yearData.max_hours)}
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium w-fit"
                    >
                      <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Modifica Ore Max</span>
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 text-sm">
                  <div>
                    <span className="text-slate-600 font-medium">Massime:</span>
                    <p className="font-bold text-slate-800">{yearData.max_hours} ore</p>
                  </div>
                  <div>
                    <span className="text-slate-600 font-medium">Utilizzate:</span>
                    <p className="font-bold text-slate-800">{yearData.used_hours} ore</p>
                  </div>
                  <div>
                    <span className="text-slate-600 font-medium">Riportate:</span>
                    <p className="font-bold text-slate-800">{yearData.carried_over_hours} ore</p>
                  </div>
                  <div>
                    <span className="text-slate-600 font-medium">Rimanenti:</span>
                    <p className={`font-bold ${yearData.remaining_hours >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {yearData.remaining_hours} ore
                    </p>
                  </div>
                  {yearData.year < permitSummary.current_year && (
                    <div>
                      <span className="text-slate-600 font-medium">Riportabili:</span>
                      <p className="font-bold text-blue-600">{yearData.can_carry_over} ore</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
          Statistiche Dettagliate - Anno {selectedYear}
        </h2>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : stats ? (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100">Ferie Prese</p>
                    <p className="text-3xl font-bold">{stats.stats.ferie_days}</p>
                    <p className="text-blue-200 text-sm">giorni nel {stats.year}</p>
                  </div>
                  <Calendar className="h-12 w-12 text-blue-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100">Permessi Presi</p>
                    <p className="text-3xl font-bold">{stats.stats.permessi_count}</p>
                    <p className="text-green-200 text-sm">richieste nel {stats.year}</p>
                  </div>
                  <Clock className="h-12 w-12 text-green-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100">Malattie</p>
                    <p className="text-3xl font-bold">{stats.stats.malattie_days}</p>
                    <p className="text-orange-200 text-sm">giorni nel {stats.year}</p>
                  </div>
                  <Activity className="h-12 w-12 text-orange-200" />
                </div>
              </div>
            </div>

            {/* Monthly Calendar Breakdown */}
            {stats.monthly_breakdown && (
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="monthly-breakdown-grid">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                    Calendario Mensile {stats.year}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="export-monthly-csv-btn"
                      onClick={async () => {
                        try {
                          const res = await axios.get(
                            `${API}/admin/employees/${employee.id}/stats/export?year=${selectedYear}&format=csv`,
                            { responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `calendario_mensile_${employee.username || employee.id}_${selectedYear}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error('Errore export CSV:', err);
                          alert("Errore durante l'export CSV");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <FileText className="h-4 w-4" />
                      CSV
                    </button>
                    <button
                      type="button"
                      data-testid="export-monthly-xlsx-btn"
                      onClick={async () => {
                        try {
                          const res = await axios.get(
                            `${API}/admin/employees/${employee.id}/stats/export?year=${selectedYear}&format=xlsx`,
                            { responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `calendario_mensile_${employee.username || employee.id}_${selectedYear}.xlsx`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error('Errore export Excel:', err);
                          alert("Errore durante l'export Excel");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      Excel
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((monthName, idx) => {
                    const m = stats.monthly_breakdown[idx] || { ferie_days: 0, permessi_count: 0, smartworking_days: 0, malattie_days: 0 };
                    const isEmpty = !m.ferie_days && !m.permessi_count && !m.smartworking_days && !m.malattie_days;
                    return (
                      <div
                        key={monthName}
                        data-testid={`month-cell-${idx + 1}`}
                        className={`rounded-lg border p-3 ${isEmpty ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-300 shadow-sm'}`}
                      >
                        <div className="font-semibold text-slate-800 text-sm mb-2 border-b border-slate-200 pb-1">
                          {monthName}
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-blue-700">Ferie</span>
                            <span className="font-bold text-slate-800">{m.ferie_days} gg</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-700">Permessi</span>
                            <span className="font-bold text-slate-800">{m.permessi_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-purple-700">Smartworking</span>
                            <span className="font-bold text-slate-800">{m.smartworking_days} gg</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-orange-700">Malattia</span>
                            <span className="font-bold text-slate-800">{m.malattie_days} gg</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Detailed Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Summary Card */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-xl text-white">
                <h3 className="text-lg font-semibold mb-4">Riepilogo {stats.year}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-purple-200">Totale Richieste:</span>
                    <span className="font-bold">{stats.stats.total_requests}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-200">Giorni Ferie:</span>
                    <span className="font-bold">{stats.stats.ferie_days}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-200">Permessi:</span>
                    <span className="font-bold">{stats.stats.permessi_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-200">Giorni Malattia:</span>
                    <span className="font-bold">{stats.stats.malattie_days}</span>
                  </div>
                </div>
              </div>

              {/* Employee Info Card */}
              <div className="bg-slate-50 border border-slate-200 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Informazioni Dipendente</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Username:</span>
                    <span className="font-medium text-slate-800">{stats.employee.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Email:</span>
                    <span className="font-medium text-slate-800">{stats.employee.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Stato:</span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                      <span className="font-medium text-green-700">Attivo</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Creato:</span>
                    <span className="font-medium text-slate-800">
                      {new Date(employee.created_at).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <BarChart3 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessun dato disponibile per l'anno selezionato</p>
            <p className="text-slate-400 text-sm">Il dipendente non ha richieste approvate nel {selectedYear}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Payslips Component for Employees
const EmployeePayslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');

  useEffect(() => {
    loadPayslips();
  }, [yearFilter, monthFilter, documentTypeFilter]);

  const loadPayslips = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (yearFilter) params.append('year', yearFilter);
      if (monthFilter) params.append('month', monthFilter);
      if (documentTypeFilter) params.append('document_type', documentTypeFilter);
      
      const response = await axios.get(`${API}/payslips?${params}`);
      setPayslips(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei documenti:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (payslipId, filename) => {
    try {
      const response = await axios.get(`${API}/payslips/${payslipId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore nel download:', error);
      alert('Errore nel download del documento');
    }
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 10; year--) {
      years.push(year);
    }
    return years;
  };

  const getMonthOptions = () => [
    { value: '', label: 'Tutti i mesi' },
    { value: 1, label: 'Gennaio' },
    { value: 2, label: 'Febbraio' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Aprile' },
    { value: 5, label: 'Maggio' },
    { value: 6, label: 'Giugno' },
    { value: 7, label: 'Luglio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Settembre' },
    { value: 10, label: 'Ottobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Dicembre' }
  ];

  const getDocumentTypeOptions = () => [
    { value: '', label: 'Tutti i documenti' },
    { value: 'busta_paga', label: 'Buste Paga' },
    { value: 'contratto_assunzione', label: 'Contratti Assunzione' },
    { value: 'cu', label: 'CU (Certificazione Unica)' }
  ];

  const getDocumentTypeName = (type) => {
    const names = {
      'busta_paga': 'Busta Paga',
      'contratto_assunzione': 'Contratto Assunzione',
      'cu': 'CU'
    };
    return names[type] || 'Documento';
  };

  const getDocumentIcon = (type) => {
    if (type === 'contratto_assunzione') return '📜';
    if (type === 'cu') return '📋';
    return '📄';
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800 flex items-center">
          <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600" />
          I miei Documenti
        </h2>
      </div>

      {/* Filters */}
      <div className="mb-4 sm:mb-6 bg-slate-50 p-3 sm:p-4 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tipo Documento</label>
            <select
              value={documentTypeFilter}
              onChange={(e) => setDocumentTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              {getDocumentTypeOptions().map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Anno</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              {getYearOptions().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Mese</label>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              {getMonthOptions().map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={loadPayslips}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
            >
              {loading ? 'Filtrando...' : 'Filtra'}
            </button>
          </div>
        </div>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-slate-500 mt-2">Caricamento documenti...</p>
        </div>
      ) : payslips.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <FileText className="h-12 w-12 sm:h-16 sm:w-16 text-slate-300 mx-auto mb-3 sm:mb-4" />
          <p className="text-slate-500 text-base sm:text-lg">Nessun documento trovato</p>
          <p className="text-slate-400 text-sm sm:text-base">
            {documentTypeFilter ? `di tipo ${getDocumentTypeName(documentTypeFilter)}` : ''} 
            {monthFilter ? ` per ${getMonthOptions().find(m => m.value == monthFilter)?.label}` : ''} 
            {yearFilter ? ` ${yearFilter}` : ''}
          </p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {payslips.map((payslip) => (
            <div key={payslip.id} className="border border-slate-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-all">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 sm:p-3 rounded-lg ${
                    payslip.document_type === 'contratto_assunzione' ? 'bg-purple-100' :
                    payslip.document_type === 'cu' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <span className="text-xl sm:text-2xl">{getDocumentIcon(payslip.document_type)}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 text-base sm:text-lg">
                      {getDocumentTypeName(payslip.document_type || 'busta_paga')} 
                      {payslip.month_name ? ` - ${payslip.month_name} ${payslip.year}` : ` - ${payslip.year}`}
                    </h3>
                    <div className="text-xs sm:text-sm text-slate-600 space-y-1">
                      <p>Caricato il: {new Date(payslip.uploaded_at).toLocaleDateString('it-IT')}</p>
                      <p>Dimensione: {(payslip.file_size / 1024 / 1024).toFixed(2)} MB</p>
                      {payslip.download_count > 0 && (
                        <p>Scaricato {payslip.download_count} volte</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleDownload(payslip.id, payslip.original_filename)}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                  >
                    <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin/Consulente Payslip Management Component
const PayslipManagement = ({ currentUser }) => {
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDocumentType, setSelectedDocumentType] = useState('busta_paga');
  const [selectedFile, setSelectedFile] = useState(null);
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');
  const [selectedUnitForUpload, setSelectedUnitForUpload] = useState('');
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    loadUnits();
    loadEmployees();
    loadPayslips();
  }, []);

  useEffect(() => {
    loadPayslips();
  }, [yearFilter, monthFilter, employeeFilter, unitFilter, documentTypeFilter]);

  useEffect(() => {
    loadEmployees();
  }, [selectedUnitForUpload]);

  useEffect(() => {
    // Reload employees when unit filter changes for history section
    loadEmployees();
  }, [unitFilter]);

  const loadUnits = async () => {
    try {
      const response = await axios.get(`${API}/payslips/units`);
      setUnits(response.data.units || []);
    } catch (error) {
      console.error('Errore nel caricamento unità:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      console.log('Loading employees for payslip management...');
      const params = new URLSearchParams();
      // Use selectedUnitForUpload for upload section, unitFilter for history section
      const unitToFilter = selectedUnitForUpload || unitFilter;
      if (unitToFilter) {
        params.append('unit', unitToFilter);
      }
      
      const response = await axios.get(`${API}/admin/employees?${params}`);
      console.log('Raw response from /admin/employees:', response.data);
      
      // Filtra tutti gli user che possono ricevere buste paga (non admin e non consulenti)
      const employeeList = response.data.filter(emp => 
        emp.role === 'employee' || emp.role === 'responsabile_unit'
      );
      
      console.log('Filtered employees for payslips:', employeeList);
      setEmployees(employeeList);
      
      if (employeeList.length === 0) {
        console.log('⚠️ No employees found for payslip management');
      } else {
        console.log(`✅ Loaded ${employeeList.length} employees for payslip management`);
      }
    } catch (error) {
      console.error('Errore nel caricamento dipendenti:', error);
      // Show error to user
      alert('Errore nel caricamento dei dipendenti. Controlla la console per dettagli.');
    }
  };

  const loadPayslips = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (yearFilter) params.append('year', yearFilter);
      if (monthFilter) params.append('month', monthFilter);
      if (employeeFilter) params.append('employee_id', employeeFilter);
      if (unitFilter) params.append('unit', unitFilter);
      if (documentTypeFilter) params.append('document_type', documentTypeFilter);
      
      const response = await axios.get(`${API}/admin/payslips?${params}`);
      setPayslips(response.data);
    } catch (error) {
      console.error('Errore nel caricamento documenti:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    setUploadError('');
    
    if (!selectedEmployee || !selectedFile) {
      setUploadError('Seleziona un dipendente e un file');
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      setUploadError('Solo file PDF sono ammessi');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setUploadError('File troppo grande (max 10MB)');
      return;
    }

    // Validate month for busta_paga
    if (selectedDocumentType === 'busta_paga' && !selectedMonth) {
      setUploadError('Il mese è obbligatorio per le buste paga');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const params = new URLSearchParams({
        employee_id: selectedEmployee,
        year: selectedYear,
        document_type: selectedDocumentType
      });
      
      // Add month only for busta_paga
      if (selectedDocumentType === 'busta_paga') {
        params.append('month', selectedMonth);
      }

      const response = await axios.post(`${API}/payslips/upload?${params}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const docTypeNames = {
        'busta_paga': 'Busta Paga',
        'contratto_assunzione': 'Contratto Assunzione',
        'cu': 'CU'
      };
      
      alert(`${docTypeNames[selectedDocumentType]} caricato con successo su Aruba Drive!`);
      
      // Reset form
      setSelectedEmployee('');
      setSelectedFile(null);
      setSelectedYear(new Date().getFullYear());
      setSelectedMonth(new Date().getMonth() + 1);
      setSelectedDocumentType('busta_paga');
      setUploadError('');
      
      // Reload documents
      await loadPayslips();
      
    } catch (error) {
      console.error('Errore upload:', error);
      const errorMessage = error.response?.data?.detail || 'Errore nel caricamento del documento';
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (payslipId, filename) => {
    try {
      const response = await axios.get(`${API}/payslips/${payslipId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore download:', error);
      alert('Errore nel download del documento');
    }
  };

  const handleDelete = async (payslipId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/payslips/${payslipId}`);
      alert('Documento eliminato con successo');
      await loadPayslips();
    } catch (error) {
      console.error('Errore eliminazione:', error);
      alert('Errore nell\'eliminazione della busta paga');
    }
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const getMonthOptions = () => [
    { value: 1, label: 'Gennaio' },
    { value: 2, label: 'Febbraio' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Aprile' },
    { value: 5, label: 'Maggio' },
    { value: 6, label: 'Giugno' },
    { value: 7, label: 'Luglio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Settembre' },
    { value: 10, label: 'Ottobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Dicembre' }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Upload Section */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6 flex items-center">
          <Upload className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600" />
          Carica Documento Dipendente
        </h2>

        {/* Error Message */}
        {uploadError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Errore nel caricamento</p>
              <p className="text-sm">{uploadError}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleFileUpload} className="space-y-4 sm:space-y-6">
          {/* Unit Filter for Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Filter className="h-4 w-4 inline mr-1" />
              Filtra per Unità (opzionale)
            </label>
            <select
              value={selectedUnitForUpload}
              onChange={(e) => {
                setSelectedUnitForUpload(e.target.value);
                setSelectedEmployee(''); // Reset employee selection when unit changes
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Tutte le unità</option>
              {units.map(unit => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Dipendente
                {selectedUnitForUpload && (
                  <span className="text-xs text-blue-600 ml-1">({selectedUnitForUpload})</span>
                )}
              </label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              >
                <option value="">Seleziona dipendente</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.username} ({employee.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tipo Documento</label>
              <select
                value={selectedDocumentType}
                onChange={(e) => setSelectedDocumentType(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              >
                <option value="busta_paga">📄 Busta Paga</option>
                <option value="contratto_assunzione">📜 Contratto Assunzione</option>
                <option value="cu">📋 CU (Certificazione Unica)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Anno</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              >
                {getYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {selectedDocumentType === 'busta_paga' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Mese <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  {getMonthOptions().map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">File PDF (max 10MB)</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
            {selectedFile && (
              <p className="text-sm text-slate-600 mt-2">
                File selezionato: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>⚠️ Importante:</strong> I documenti vengono salvati esclusivamente su Aruba Drive. 
              Assicurati che la connessione cloud sia configurata correttamente nelle Impostazioni.
            </p>
          </div>

          <button
            type="submit"
            disabled={uploading}
            className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm sm:text-base"
          >
            {uploading ? 'Caricamento su Aruba Drive...' : 'Carica Documento'}
          </button>
        </form>
      </div>

      {/* Filters and List */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 sm:mb-6 flex items-center">
          <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-green-600" />
          Storico Documenti Dipendenti
        </h2>

        {/* Filters */}
        <div className="mb-4 sm:mb-6 bg-slate-50 p-3 sm:p-4 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tipo Documento</label>
              <select
                value={documentTypeFilter}
                onChange={(e) => setDocumentTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Tutti i tipi</option>
                <option value="busta_paga">Buste Paga</option>
                <option value="contratto_assunzione">Contratti</option>
                <option value="cu">CU</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Filter className="h-4 w-4 inline mr-1" />
                Unità
              </label>
              <select
                value={unitFilter}
                onChange={(e) => setUnitFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Tutte le unità</option>
                {units.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Dipendente</label>
              <select
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Tutti i dipendenti</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.username}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Anno</label>
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Tutti gli anni</option>
                {getYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mese</label>
              <select
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Tutti i mesi</option>
                {getMonthOptions().map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={loadPayslips}
                disabled={loading}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
              >
                {loading ? 'Filtrando...' : 'Filtra'}
              </button>
            </div>
          </div>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-slate-500 mt-2">Caricamento...</p>
          </div>
        ) : payslips.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <FileText className="h-12 w-12 sm:h-16 sm:w-16 text-slate-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-slate-500 text-base sm:text-lg">Nessun documento trovato</p>
            <p className="text-slate-400 text-sm sm:text-base">Applica filtri diversi o carica nuovi documenti</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {payslips.map((payslip) => {
              const docTypeNames = {
                'busta_paga': 'Busta Paga',
                'contratto_assunzione': 'Contratto',
                'cu': 'CU'
              };
              const docTypeColors = {
                'busta_paga': 'bg-red-100 text-red-600',
                'contratto_assunzione': 'bg-purple-100 text-purple-600',
                'cu': 'bg-green-100 text-green-600'
              };
              const docType = payslip.document_type || 'busta_paga';
              
              return (
                <div key={payslip.id} className="border border-slate-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-all">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 sm:p-3 rounded-lg ${docTypeColors[docType]?.split(' ')[0] || 'bg-slate-100'}`}>
                        <FileText className={`h-5 w-5 sm:h-6 sm:w-6 ${docTypeColors[docType]?.split(' ')[1] || 'text-slate-600'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-800 text-base sm:text-lg">
                            {payslip.employee_username}
                          </h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${docTypeColors[docType] || 'bg-slate-100 text-slate-600'}`}>
                            {docTypeNames[docType] || 'Documento'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 font-medium">
                          {payslip.month_name ? `${payslip.month_name} ${payslip.year}` : payslip.year}
                        </p>
                        <div className="text-xs sm:text-sm text-slate-600 space-y-1">
                          <p>Email: {payslip.employee_email}</p>
                          <p>Caricato il: {new Date(payslip.uploaded_at).toLocaleDateString('it-IT')}</p>
                          <p>Dimensione: {(payslip.file_size / 1024 / 1024).toFixed(2)} MB</p>
                          {payslip.aruba_path && (
                            <p className="text-green-600">☁️ Su Aruba Drive</p>
                          )}
                          {payslip.download_count > 0 && (
                            <p>Scaricato {payslip.download_count} volte</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDownload(payslip.id, payslip.original_filename)}
                        className="flex items-center space-x-2 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                      >
                        <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Download</span>
                      </button>
                      
                      {currentUser.role === 'admin' && (
                        <button
                          onClick={() => handleDelete(payslip.id)}
                          className="flex items-center space-x-2 bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">Elimina</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Settings Component
const AdminSettings = () => {
  const [settings, setSettings] = useState({ email: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      await axios.put(`${API}/admin/settings`, settings);
      setMessage('Email aggiornata con successo');
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Le password non corrispondono');
      return;
    }

    setLoading(true);
    setMessage('');
    setError('');

    try {
      await axios.put(`${API}/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setMessage('Password cambiata con successo');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Configuration */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
          <Mail className="h-5 w-5 mr-2" />
          Configurazione Email
        </h2>
        
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
          <h3 className="font-semibold text-blue-800 mb-2">Configurazione SMTP Gmail richiesta</h3>
          <p className="text-blue-700 text-sm mb-3">
            Per abilitare l'invio automatico delle email, configura le seguenti variabili d'ambiente nel file backend/.env:
          </p>
          <div className="bg-white p-3 rounded border font-mono text-sm">
            <div>ADMIN_EMAIL=tua-email@gmail.com</div>
            <div>ADMIN_APP_PASSWORD=tua-app-password-gmail</div>
          </div>
          <p className="text-blue-600 text-xs mt-2">
            Ricorda di riavviare il backend dopo aver modificato le variabili d'ambiente.
          </p>
        </div>

        <form onSubmit={handleEmailUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email amministratore (per ricevere notifiche)
            </label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) => setSettings({ ...settings, email: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="admin@company.com"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Aggiornamento...' : 'Aggiorna Email'}
          </button>
        </form>
      </div>

      {/* Password Change */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h2 className="text-xl font-semibold text-slate-800 mb-6">Cambia Password</h2>
        
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Password corrente</label>
            <input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Nuova password</label>
            <input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Conferma nuova password</label>
            <input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Cambiando...' : 'Cambia Password'}
          </button>
        </form>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {message}
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
};

// ===== CONVENTIONS COMPONENTS =====

// Conventions Gallery Component (Visible to all users)
const ConventionsGallery = ({ user }) => {
  const [conventions, setConventions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    loadConventions();
  }, []);

  const loadConventions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/conventions`);
      setConventions(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle convenzioni:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 sm:p-6 rounded-xl text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold mb-2">Convenzioni Aziendali</h2>
            <p className="text-purple-100 text-sm sm:text-base">
              Scopri tutte le convenzioni e i vantaggi disponibili per te
            </p>
          </div>
          <Image className="h-8 w-8 sm:h-12 sm:w-12 text-purple-200" />
        </div>
      </div>

      {/* Conventions List */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-slate-500 mt-2">Caricamento convenzioni...</p>
          </div>
        ) : conventions.length === 0 ? (
          <div className="text-center py-8">
            <Image className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessuna convenzione disponibile al momento</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {conventions.map((convention) => (
              <div key={convention.id} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all">
                {/* Convention Header */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{convention.title}</h3>
                  <p className="text-purple-600 font-medium text-sm mb-2">{convention.company}</p>
                  {convention.discount && (
                    <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium inline-block">
                      {convention.discount}
                    </div>
                  )}
                </div>

                {/* Convention Description */}
                <p className="text-slate-600 text-sm mb-4">{convention.description}</p>

                {/* Convention Images */}
                {convention.image_urls && convention.image_urls.length > 0 && (
                  <div className="mb-4">
                    <div className="grid grid-cols-2 gap-2">
                      {convention.image_urls.slice(0, 4).map((imageUrl, index) => (
                        <div 
                          key={index}
                          className="relative aspect-square cursor-pointer group"
                          onClick={() => setSelectedImage(imageUrl)}
                        >
                          <img 
                            src={imageUrl} 
                            alt={`${convention.title} - Immagine ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg group-hover:opacity-90 transition-opacity"
                          />
                          {index === 3 && convention.image_urls.length > 4 && (
                            <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                              <span className="text-white font-medium text-sm">+{convention.image_urls.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Convention Details */}
                <div className="space-y-2 text-xs text-slate-500">
                  {convention.contact_info && (
                    <p><span className="font-medium">Contatti:</span> {convention.contact_info}</p>
                  )}
                  {convention.valid_until && (
                    <p><span className="font-medium">Valida fino al:</span> {formatDate(convention.valid_until)}</p>
                  )}
                  <p><span className="font-medium">Pubblicata il:</span> {formatDate(convention.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setSelectedImage(null)}>
          <div className="max-w-4xl max-h-full">
            <img 
              src={selectedImage} 
              alt="Immagine convenzione"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Conventions Manager Component (Admin and Responsabile Digital only)
const ConventionsManager = ({ user }) => {
  const [conventions, setConventions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingConvention, setEditingConvention] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    discount: '',
    contact_info: '',
    valid_until: ''
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render key

  // Check if user can edit conventions (Admin or Responsabile Digital)
  const canEdit = user.role === 'admin' || (user.role === 'responsabile_unit' && user.unit === 'Digital');

  useEffect(() => {
    loadConventions();
  }, []);

  const loadConventions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/conventions`);
      setConventions(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle convenzioni:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      // Process form data to handle empty strings for optional fields
      const processedData = {
        ...formData,
        // Convert empty strings to null for optional fields
        discount: formData.discount.trim() || null,
        contact_info: formData.contact_info.trim() || null,
        valid_until: formData.valid_until.trim() || null
      };

      const response = editingConvention 
        ? await axios.put(`${API}/admin/convention/${editingConvention.id}`, processedData)
        : await axios.post(`${API}/admin/convention`, processedData);

      const conventionId = editingConvention ? editingConvention.id : response.data.convention_id;

      // Upload images if any selected
      if (selectedImages.length > 0) {
        for (const image of selectedImages) {
          const imageFormData = new FormData();
          imageFormData.append('file', image);
          await axios.post(`${API}/admin/convention/${conventionId}/upload-image`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        // Reload conventions after image upload to show new images
        await loadConventions();
        setRefreshKey(prev => prev + 1); // Force ConventionsGallery refresh
      }

      alert(editingConvention ? 'Convenzione aggiornata con successo!' : 'Convenzione creata con successo!');
      resetForm();
      await loadConventions(); // Wait for conventions to load
      setRefreshKey(prev => prev + 1); // Force ConventionsGallery refresh
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      alert('Errore nel salvataggio della convenzione');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (convention) => {
    setEditingConvention(convention);
    setFormData({
      title: convention.title,
      company: convention.company,
      description: convention.description,
      discount: convention.discount || '',
      contact_info: convention.contact_info || '',
      valid_until: convention.valid_until ? convention.valid_until.split('T')[0] : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (conventionId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa convenzione?')) return;
    
    try {
      await axios.delete(`${API}/admin/convention/${conventionId}`);
      alert('Convenzione eliminata con successo!');
      await loadConventions();
      setRefreshKey(prev => prev + 1); // Force ConventionsGallery refresh
    } catch (error) {
      console.error('Errore nella cancellazione:', error);
      alert('Errore nella cancellazione della convenzione');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      company: '',
      description: '',
      discount: '',
      contact_info: '',
      valid_until: ''
    });
    setSelectedImages([]);
    setEditingConvention(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 sm:p-6 rounded-xl text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold mb-2">Gestione Convenzioni</h2>
            <p className="text-purple-100 text-sm sm:text-base">
              Gestisci le convenzioni aziendali e carica immagini
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {canEdit && (
              <button
                onClick={() => setShowForm(!showForm)}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Nuova Convenzione</span>
                <span className="sm:hidden">Nuova</span>
              </button>
            )}
            <Image className="h-8 w-8 sm:h-12 sm:w-12 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Convention Form */}
      {showForm && canEdit && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            {editingConvention ? 'Modifica Convenzione' : 'Nuova Convenzione'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Titolo</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Azienda</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Descrizione</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                rows="3"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sconto (opzionale)</label>
                <input
                  type="text"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="es. 10% di sconto"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Contatti (opzionale)</label>
                <input
                  type="text"
                  value={formData.contact_info}
                  onChange={(e) => setFormData({...formData, contact_info: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="info@azienda.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Valida fino al (opzionale)</label>
                <input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Immagini (opzionale)</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => setSelectedImages(Array.from(e.target.files))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              {selectedImages.length > 0 && (
                <p className="text-sm text-slate-600 mt-1">{selectedImages.length} file selezionati</p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={uploading}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>{editingConvention ? 'Aggiorna' : 'Salva'}</span>
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={resetForm}
                className="border border-slate-300 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50"
              >
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Conventions Gallery with Management */}
      <ConventionsGallery key={refreshKey} user={user} />

      {/* Management Controls */}
      {canEdit && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Controlli di Gestione</h3>
          
          {conventions.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Nessuna convenzione da gestire</p>
          ) : (
            <div className="space-y-3">
              {conventions.map((convention) => (
                <div key={convention.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-800">{convention.title}</h4>
                    <p className="text-sm text-slate-600">{convention.company}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(convention)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Modifica convenzione"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(convention.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Elimina convenzione"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Campaign Reports System Component (Digital Unit Only)
const CampaignReports = ({ user }) => {
  const [currentView, setCurrentView] = useState('clients');
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check user permissions
  const canManage = user && user.unit === 'Digital'; // Digital unit (ResponsabileUnit + Employee) can manage
  const isAdmin = user && user.role === 'admin'; // Admin can see all but not manage
  const canView = canManage || isAdmin || (user && user.role === 'responsabile_unit' && user.unit !== 'Agenti');

  // Load clients based on user permissions
  const loadClients = async () => {
    try {
      setLoading(true);
      let endpoint = `${API}/digital/clients`;
      
      // Admin should see all clients - use different endpoint if needed
      if (isAdmin) {
        // Admin can access the same endpoint but backend should return all clients for admin
        endpoint = `${API}/digital/clients`;
      }
      
      const response = await axios.get(endpoint);
      setClients(response.data);
    } catch (error) {
      setError('Errore nel caricamento dei clienti');
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    loadClients();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 flex items-center mb-2">
              <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
              {canManage ? 'Gestione Report Campagne' : 'Report Campagne'}
            </h2>
            <p className="text-slate-600 text-sm">
              {canManage 
                ? "Gestisci clienti, campagne e monitora performance con report dettagliati"
                : isAdmin
                ? "Visualizza tutti i report e export dei dati di campagne"
                : "Visualizza report e statistiche delle campagne autorizzate"
              }
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {setCurrentView('clients'); clearMessages();}}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                currentView === 'clients'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {canManage ? 'Clienti' : 'Report'}
            </button>
            
            {/* Show Export button for all authorized users */}
            {canView && (
              <button
                onClick={() => {setCurrentView('reports'); clearMessages();}}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                  currentView === 'reports'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Export Excel
              </button>
            )}

            {/* Show Calendar for Admin and ResponsabileUnit authorized */}
            {canView && !canManage && (
              <button
                onClick={() => {setCurrentView('calendar'); clearMessages();}}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                  currentView === 'calendar'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <Calendar className="h-4 w-4 inline mr-1" />
                Calendario
              </button>
            )}
            
            {/* Management buttons only for Digital unit */}
            {canManage && (
              <>
                <button
                  onClick={() => {setCurrentView('campaigns'); clearMessages();}}
                  className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentView === 'campaigns'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Campagne
                </button>
                <button
                  onClick={() => {setCurrentView('data'); clearMessages();}}
                  className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentView === 'data'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Dati Giornalieri
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-green-800">{success}</span>
            </div>
            <button onClick={clearMessages} className="text-green-600 hover:text-green-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-red-800">{error}</span>
            </div>
            <button onClick={clearMessages} className="text-red-600 hover:text-red-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Content based on current view */}
      {loading && (
        <div className="text-center py-8">
          <div className="text-slate-600">Caricamento in corso...</div>
        </div>
      )}

      {!loading && (
        <>
          {currentView === 'clients' && (
            <ClientsManagement 
              clients={clients} 
              setClients={setClients}
              setSuccess={setSuccess}
              setError={setError}
              canManage={canManage}
            />
          )}
          
          {/* Export reports - Available for all authorized users */}
          {canView && currentView === 'reports' && (
            <MonthlyReports 
              clients={clients}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}
          
          {/* Management tools - Only for Digital unit */}
          {canManage && currentView === 'campaigns' && (
            <CampaignsManagement 
              clients={clients}
              campaigns={campaigns}
              setCampaigns={setCampaigns}
              setSuccess={setSuccess}
              setError={setError}
              canManage={canManage}
            />
          )}
          
          {canManage && currentView === 'data' && (
            <DailyDataManagement 
              clients={clients}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {/* Calendar View - Available for Admin and ResponsabileUnit authorized (read-only) */}
          {canView && !canManage && currentView === 'calendar' && (
            <CalendarView 
              clients={clients}
              user={user}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}
        </>
      )}
    </div>
  );
};

// Clients Management Component
const ClientsManagement = ({ clients, setClients, setSuccess, setError, canManage }) => {
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authUnits, setAuthUnits] = useState([]);

  // Available units for authorization (except Digital and Agenti)
  const availableUnits = ["CEO", "HR", "Amministrazione", "Presidi", "Store", "TLS", "Fotovoltaico", "Fibra"];

  const createClient = async () => {
    if (!newClientName.trim()) {
      setError('Nome cliente obbligatorio');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/digital/clients?client_name=${encodeURIComponent(newClientName)}`);
      setClients(prev => [...prev, response.data]);
      setSuccess(`Cliente "${newClientName}" creato con successo`);
      setNewClientName('');
      setShowNewClientForm(false);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella creazione del cliente');
    } finally {
      setLoading(false);
    }
  };

  const updateClientAuthorization = async () => {
    if (!selectedClient) return;

    try {
      setLoading(true);
      await axios.put(`${API}/digital/client-authorization`, {
        client_id: selectedClient.id,
        authorized_units: authUnits
      });
      
      // Update local state
      setClients(prev => prev.map(client => 
        client.id === selectedClient.id 
          ? { ...client, authorized_units: authUnits }
          : client
      ));
      
      setSuccess(`Autorizzazioni aggiornate per ${selectedClient.name}`);
      setShowAuthModal(false);
      setSelectedClient(null);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore aggiornamento autorizzazioni');
    } finally {
      setLoading(false);
    }
  };

  const openAuthModal = (client) => {
    setSelectedClient(client);
    setAuthUnits(client.authorized_units || []);
    setShowAuthModal(true);
  };

  const deleteClient = async (client) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il cliente "${client.name}"? Questa azione eliminerà anche tutte le campagne e i dati giornalieri associati.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`${API}/digital/clients/${client.id}`);
      
      // Remove client from local state
      setClients(prev => prev.filter(c => c.id !== client.id));
      
      setSuccess(`Cliente "${client.name}" eliminato con successo. Eliminate ${response.data.deleted_campaigns} campagne e ${response.data.deleted_daily_data} dati giornalieri.`);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore durante l\'eliminazione del cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add New Client Section - Only for Digital unit */}
      {canManage && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Gestione Clienti</h3>
            <button
              onClick={() => setShowNewClientForm(!showNewClientForm)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Nuovo Cliente</span>
            </button>
          </div>

          {showNewClientForm && (
            <div className="border-t pt-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nome Cliente
                  </label>
                  <input
                    type="text"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. Azienda XYZ S.r.l."
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={createClient}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-slate-400 transition-colors"
                  >
                    {loading ? 'Creazione...' : 'Crea'}
                  </button>
                  <button
                    onClick={() => {setShowNewClientForm(false); setNewClientName('');}}
                    className="px-4 py-2 bg-slate-400 text-white rounded-lg hover:bg-slate-500 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clients List */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          {canManage ? 'Lista Clienti' : 'Clienti Autorizzati'} ({clients.length})
        </h3>

        {clients.length === 0 ? (
          <div className="text-center py-8 text-slate-600">
            {canManage ? 'Nessun cliente presente. Creane uno nuovo!' : 'Nessun cliente autorizzato per la tua unit.'}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {clients.map((client) => (
              <div key={client.id} className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-slate-800">{client.name}</h4>
                  {canManage && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openAuthModal(client)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Gestisci autorizzazioni"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteClient(client)}
                        className="text-red-600 hover:text-red-800"
                        title="Elimina cliente"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="text-xs text-slate-600 mb-2">
                  Creato il: {new Date(client.created_at).toLocaleDateString('it-IT')}
                </div>
                
                <div className="flex flex-wrap gap-1">
                  {client.authorized_units && client.authorized_units.length > 0 ? (
                    client.authorized_units.map(unit => (
                      <span key={unit} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {unit}
                      </span>
                    ))
                  ) : (
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                      Nessuna autorizzazione
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Authorization Modal - Only for Digital unit */}
      {canManage && showAuthModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-slate-800">
                Autorizzazioni per {selectedClient.name}
              </h4>
              <button
                onClick={() => setShowAuthModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <p className="text-sm text-slate-600">
                Seleziona le unit che possono visualizzare i report di questo cliente:
              </p>
              
              {availableUnits.map(unit => (
                <label key={unit} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={authUnits.includes(unit)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setAuthUnits(prev => [...prev, unit]);
                      } else {
                        setAuthUnits(prev => prev.filter(u => u !== unit));
                      }
                    }}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                  />
                  <span className="text-sm text-slate-700">{unit}</span>
                </label>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={updateClientAuthorization}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 transition-colors"
              >
                {loading ? 'Salvando...' : 'Salva Autorizzazioni'}
              </button>
              <button
                onClick={() => setShowAuthModal(false)}
                className="px-4 py-2 bg-slate-400 text-white rounded-lg hover:bg-slate-500 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Campaigns Management Component
const CampaignsManagement = ({ clients, campaigns, setCampaigns, setSuccess, setError, canManage }) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [clientCampaigns, setClientCampaigns] = useState([]);
  const [showNewCampaignForm, setShowNewCampaignForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    campaign_type: 'Lead Brand'
    // month e year rimossi: le campagne sono ora indipendenti dal mese
  });

  const campaignTypes = ['Lead Brand', 'Lead White', 'Like Brand', 'Like White'];

  const loadClientCampaigns = async (clientId) => {
    if (!clientId) {
      setClientCampaigns([]);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/digital/campaigns/${clientId}`);
      setClientCampaigns(response.data);
    } catch (error) {
      setError('Errore nel caricamento delle campagne');
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!selectedClient || !campaignForm.name.trim()) {
      setError('Seleziona un cliente e inserisci il nome della campagna');
      return;
    }

    try {
      setLoading(true);
      // Month e year ora sono opzionali, il backend usa la data corrente se non specificati
      const response = await axios.post(`${API}/digital/campaigns`, {
        client_id: selectedClient,
        name: campaignForm.name,
        campaign_type: campaignForm.campaign_type
        // month e year rimossi: le campagne sono ora indipendenti dal mese
      });

      setSuccess('Campagna creata con successo');
      setCampaignForm({
        name: '',
        campaign_type: 'Lead Brand'
      });
      setShowNewCampaignForm(false);
      loadClientCampaigns(selectedClient);
      
      // Update the main campaigns list
      setCampaigns(prev => [...prev, response.data]);
    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const toggleCampaignStatus = async (campaignId, isActive) => {
    try {
      setLoading(true);
      const response = await axios.put(`${API}/digital/campaigns/${campaignId}/status`, {
        is_active: isActive
      });
      
      setSuccess(response.data.message);
      
      // Update local state
      setClientCampaigns(prev => prev.map(campaign => 
        campaign.id === campaignId 
          ? { ...campaign, is_active: isActive }
          : campaign
      ));
      
      // Update main campaigns list
      setCampaigns(prev => prev.map(campaign => 
        campaign.id === campaignId 
          ? { ...campaign, is_active: isActive }
          : campaign
      ));
      
    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const deleteCampaign = async (campaignId, campaignName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la campagna "${campaignName}"? Questa azione eliminerà anche tutti i dati associati e non può essere annullata.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`${API}/digital/campaigns/${campaignId}`);
      
      setSuccess(response.data.message);
      
      // Update local state
      setClientCampaigns(prev => prev.filter(campaign => campaign.id !== campaignId));
      
      // Update main campaigns list
      setCampaigns(prev => prev.filter(campaign => campaign.id !== campaignId));
      
    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'Lead Brand': 'bg-blue-100 text-blue-800 border-blue-200',
      'Lead White': 'bg-cyan-100 text-cyan-800 border-cyan-200',
      'Like Brand': 'bg-green-100 text-green-800 border-green-200',
      'Like White': 'bg-emerald-100 text-emerald-800 border-emerald-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  useEffect(() => {
    if (selectedClient) {
      loadClientCampaigns(selectedClient);
    }
  }, [selectedClient]);

  return (
    <div className="space-y-6">
      {/* Client Selection */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Gestione Campagne</h3>
        
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Seleziona Cliente
            </label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Seleziona un cliente --</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          
          {selectedClient && (
            <button
              onClick={() => setShowNewCampaignForm(!showNewCampaignForm)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nuova Campagna
            </button>
          )}
        </div>
      </div>

      {/* New Campaign Form */}
      {showNewCampaignForm && selectedClient && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <h4 className="text-lg font-semibold text-slate-800 mb-4">Crea Nuova Campagna</h4>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nome Campagna
              </label>
              <input
                type="text"
                value={campaignForm.name}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Es. Campagna Natale 2025"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tipologia
              </label>
              <select
                value={campaignForm.campaign_type}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, campaign_type: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {campaignTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-blue-800">
              ℹ️ <strong>Campagne permanenti:</strong> Le campagne sono ora indipendenti dal mese. 
              Puoi inserire dati per qualsiasi mese e generare report mensili separati.
            </p>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={createCampaign}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-slate-400 transition-colors"
            >
              {loading ? 'Creazione...' : 'Crea Campagna'}
            </button>
            <button
              onClick={() => setShowNewCampaignForm(false)}
              className="px-4 py-2 bg-slate-400 text-white rounded-lg hover:bg-slate-500 transition-colors"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      {selectedClient && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <h4 className="text-lg font-semibold text-slate-800 mb-4">
            Campagne di {clients.find(c => c.id === selectedClient)?.name} ({clientCampaigns.length})
          </h4>

          {loading && (
            <div className="text-center py-4 text-slate-600">
              Caricamento campagne...
            </div>
          )}

          {!loading && clientCampaigns.length === 0 && (
            <div className="text-center py-8 text-slate-600">
              Nessuna campagna presente per questo cliente. Creane una nuova!
            </div>
          )}

          {!loading && clientCampaigns.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {clientCampaigns.map((campaign) => (
                <div key={campaign.id} className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-slate-800">{campaign.name}</h5>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded border ${getTypeColor(campaign.campaign_type)}`}>
                        {campaign.campaign_type}
                      </span>
                      {canManage && (
                        <div className="flex gap-1">
                          {/* Toggle Active/Inactive */}
                          <button
                            onClick={() => toggleCampaignStatus(campaign.id, !campaign.is_active)}
                            className={`p-1 rounded text-xs transition-colors ${
                              campaign.is_active 
                                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                            title={campaign.is_active ? 'Disattiva campagna' : 'Attiva campagna'}
                          >
                            {campaign.is_active ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                          </button>
                          
                          {/* Delete Campaign */}
                          <button
                            onClick={() => deleteCampaign(campaign.id, campaign.name)}
                            className="p-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs transition-colors"
                            title="Elimina campagna"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-sm text-slate-600 space-y-1">
                    <div>🗓️ Creata il: {new Date(campaign.created_at).toLocaleDateString('it-IT')}</div>
                    {campaign.updated_at && campaign.updated_at !== campaign.created_at && (
                      <div>🔄 Aggiornata: {new Date(campaign.updated_at).toLocaleDateString('it-IT')}</div>
                    )}
                    <div className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${campaign.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      {campaign.is_active ? 'Attiva' : 'Chiusa'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Daily Data Management Component with Calendar
const DailyDataManagement = ({ clients, setSuccess, setError }) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [showDataForm, setShowDataForm] = useState(false);
  const [formData, setFormData] = useState({
    lead: '',
    like: '',
    copertura: '',
    impression: '',
    click: '',
    spending: ''
  });

  const loadCampaigns = async (clientId) => {
    if (!clientId) {
      setCampaigns([]);
      return;
    }

    try {
      // Per la sezione Dati Giornalieri, carica solo le campagne attive
      const response = await axios.get(`${API}/digital/campaigns/${clientId}?active_only=true`);
      setCampaigns(response.data);
    } catch (error) {
      setError('Errore nel caricamento delle campagne');
    }
  };

  const loadDailyData = async (campaignId) => {
    if (!campaignId) {
      setDailyData([]);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/digital/daily-data/${campaignId}`);
      setDailyData(response.data);
    } catch (error) {
      setError('Errore nel caricamento dei dati giornalieri');
    } finally {
      setLoading(false);
    }
  };

  const submitDailyData = async () => {
    if (!selectedCampaign || !selectedDate) {
      setError('Seleziona campagna e data');
      return;
    }

    const campaign = campaigns.find(c => c.id === selectedCampaign);
    if (!campaign) return;

    // Prepare data based on campaign type
    const dataPayload = {
      campaign_id: selectedCampaign,
      date: selectedDate,
      copertura: parseInt(formData.copertura) || 0,
      impression: parseInt(formData.impression) || 0,
      click: parseInt(formData.click) || 0,
      spending: parseFloat(formData.spending) || 0
    };

    // Add lead or like based on campaign type
    if (campaign.campaign_type.includes('Lead')) {
      dataPayload.lead = parseInt(formData.lead) || 0;
    } else if (campaign.campaign_type.includes('Like')) {
      dataPayload.like = parseInt(formData.like) || 0;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/digital/daily-data`, dataPayload);
      
      // Update daily data list
      setDailyData(prev => {
        const existing = prev.find(d => d.date === selectedDate);
        if (existing) {
          return prev.map(d => d.date === selectedDate ? response.data : d);
        } else {
          return [...prev, response.data].sort((a, b) => new Date(b.date) - new Date(a.date));
        }
      });
      
      setSuccess(`Dati salvati per ${new Date(selectedDate).toLocaleDateString('it-IT')}`);
      setFormData({
        lead: '', like: '', copertura: '', impression: '', click: '', spending: ''
      });
      setShowDataForm(false);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nel salvare i dati');
    } finally {
      setLoading(false);
    }
  };

  const getDataForDate = (date) => {
    return dailyData.find(d => d.date === date);
  };

  const openDataForm = (date) => {
    setSelectedDate(date);
    const existingData = getDataForDate(date);
    if (existingData) {
      setFormData({
        lead: existingData.lead || '',
        like: existingData.like || '',
        copertura: existingData.copertura || '',
        impression: existingData.impression || '',
        click: existingData.click || '',
        spending: existingData.spending || ''
      });
    } else {
      setFormData({
        lead: '', like: '', copertura: '', impression: '', click: '', spending: ''
      });
    }
    setShowDataForm(true);
  };

  useEffect(() => {
    if (selectedClient) {
      loadCampaigns(selectedClient);
    }
  }, [selectedClient]);

  useEffect(() => {
    if (selectedCampaign) {
      loadDailyData(selectedCampaign);
    }
  }, [selectedCampaign]);

  const selectedCampaignData = campaigns.find(c => c.id === selectedCampaign);
  const isLeadCampaign = selectedCampaignData?.campaign_type.includes('Lead');
  const isLikeCampaign = selectedCampaignData?.campaign_type.includes('Like');

  return (
    <div className="space-y-6">
      {/* Client and Campaign Selection */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Inserimento Dati Giornalieri</h3>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Cliente</label>
            <select
              value={selectedClient}
              onChange={(e) => {
                setSelectedClient(e.target.value);
                setSelectedCampaign('');
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Seleziona Cliente --</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Campagna</label>
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              disabled={!selectedClient}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
            >
              <option value="">-- Seleziona Campagna --</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name} ({campaign.campaign_type})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Calendar and Data Entry */}
      {selectedCampaign && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-slate-800">
              Calendario Dati - {selectedCampaignData?.name}
            </h4>
            <span className={`px-3 py-1 text-sm rounded ${
              isLeadCampaign ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
            }`}>
              {selectedCampaignData?.campaign_type}
            </span>
          </div>

          {/* Simple Calendar - Current Month */}
          <SimpleCalendar 
            dailyData={dailyData}
            onDateClick={openDataForm}
            selectedDate={selectedDate}
          />

          {/* Quick Add Button */}
          <div className="mt-4 text-center">
            <button
              onClick={() => openDataForm(new Date().toISOString().split('T')[0])}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Aggiungi Dati per Oggi
            </button>
          </div>
        </div>
      )}

      {/* Data Entry Form Modal */}
      {showDataForm && selectedCampaignData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-90vh overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-slate-800">
                Dati per {new Date(selectedDate).toLocaleDateString('it-IT')}
              </h4>
              <button
                onClick={() => setShowDataForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Lead or Like based on campaign type */}
              {isLeadCampaign && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Lead</label>
                  <input
                    type="number"
                    value={formData.lead}
                    onChange={(e) => setFormData(prev => ({ ...prev, lead: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Numero di lead generati"
                  />
                </div>
              )}

              {isLikeCampaign && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Like</label>
                  <input
                    type="number"
                    value={formData.like}
                    onChange={(e) => setFormData(prev => ({ ...prev, like: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Numero di like ottenuti"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Copertura</label>
                <input
                  type="number"
                  value={formData.copertura}
                  onChange={(e) => setFormData(prev => ({ ...prev, copertura: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Copertura raggiunti"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Impression</label>
                <input
                  type="number"
                  value={formData.impression}
                  onChange={(e) => setFormData(prev => ({ ...prev, impression: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Numero di impression"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Click</label>
                <input
                  type="number"
                  value={formData.click}
                  onChange={(e) => setFormData(prev => ({ ...prev, click: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Numero di click"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Spending (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.spending}
                  onChange={(e) => setFormData(prev => ({ ...prev, spending: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Budget speso in euro"
                />
              </div>
            </div>

            {/* CPL Preview */}
            {formData.spending && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>CPL Calcolato:</strong> €
                  {(() => {
                    const spending = parseFloat(formData.spending) || 0;
                    let metric = 0;
                    if (isLeadCampaign) {
                      metric = parseInt(formData.lead) || 0;
                    } else if (isLikeCampaign) {
                      metric = parseInt(formData.like) || 0;
                    }
                    return metric > 0 ? (spending / metric).toFixed(2) : '0.00';
                  })()}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={submitDailyData}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-slate-400 transition-colors"
              >
                {loading ? 'Salvando...' : 'Salva Dati'}
              </button>
              <button
                onClick={() => setShowDataForm(false)}
                className="px-4 py-2 bg-slate-400 text-white rounded-lg hover:bg-slate-500 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple Calendar Component
const SimpleCalendar = ({ dailyData, onDateClick, selectedDate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  // Get first day of month and number of days
  const firstDay = new Date(currentYear, currentMonth, 1);
  const lastDay = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  
  // Create array of days
  const days = [];
  
  // Add empty cells for days before the first day of month
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }
  
  // Add days of month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }
  
  const getDateString = (day) => {
    return `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  
  const hasData = (day) => {
    if (!day) return false;
    const dateString = getDateString(day);
    return dailyData.some(data => data.date === dateString);
  };
  
  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];
  
  const weekDays = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  
  return (
    <div className="bg-slate-50 rounded-lg p-4">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrentDate(new Date(currentYear, currentMonth - 1, 1))}
          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
        >
          <ChevronDown className="h-4 w-4 transform rotate-90" />
        </button>
        
        <h3 className="text-lg font-semibold text-slate-800">
          {monthNames[currentMonth]} {currentYear}
        </h3>
        
        <button
          onClick={() => setCurrentDate(new Date(currentYear, currentMonth + 1, 1))}
          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
        >
          <ChevronDown className="h-4 w-4 transform -rotate-90" />
        </button>
      </div>
      
      {/* Week Days Header */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-medium text-slate-600 p-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          if (!day) {
            return <div key={index} className="p-2"></div>;
          }
          
          const dateString = getDateString(day);
          const isSelected = dateString === selectedDate;
          const isToday = dateString === new Date().toISOString().split('T')[0];
          const hasDataForDay = hasData(day);
          
          return (
            <button
              key={day}
              onClick={() => onDateClick(dateString)}
              className={`p-2 text-center text-sm rounded-lg transition-colors relative ${
                isSelected 
                  ? 'bg-blue-600 text-white' 
                  : isToday
                  ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                  : hasDataForDay
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'hover:bg-slate-200 text-slate-700'
              }`}
            >
              {day}
              {hasDataForDay && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></span>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs">
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
          <span>Selezionato</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          <span>Con dati</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-blue-200 rounded-full"></span>
          <span>Oggi</span>
        </div>
      </div>
    </div>
  );
};

// Monthly Reports Component with Excel Export
const MonthlyReports = ({ clients, setSuccess, setError }) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().substring(0, 7));
  const [campaigns, setCampaigns] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCampaigns = async (clientId) => {
    if (!clientId) {
      setCampaigns([]);
      return;
    }

    try {
      // Per la sezione Dati Giornalieri, carica solo le campagne attive
      const response = await axios.get(`${API}/digital/campaigns/${clientId}?active_only=true`);
      setCampaigns(response.data);
    } catch (error) {
      setError('Errore nel caricamento delle campagne');
    }
  };

  const generateReport = async () => {
    if (!selectedClient || !selectedMonth) {
      setError('Seleziona cliente e mese');
      return;
    }

    try {
      setLoading(true);
      
      // Get ALL ACTIVE campaigns for the client (non più filtrate per mese)
      // Il filtraggio per mese avviene sui daily_data, non sulle campagne!
      const activeCampaigns = campaigns.filter(c => c.is_active !== false);
      
      if (activeCampaigns.length === 0) {
        setError('Nessuna campagna attiva per questo cliente');
        return;
      }

      // Load data for each campaign (il backend filtrerà per data automaticamente)
      const campaignData = await Promise.all(
        activeCampaigns.map(async (campaign) => {
          try {
            const response = await axios.get(`${API}/digital/daily-data/${campaign.id}`);
            return {
              campaign,
              dailyData: response.data
            };
          } catch (error) {
            return {
              campaign,
              dailyData: []
            };
          }
        })
      );

      // Calculate totals by campaign type
      const typeTotals = {
        'Lead Brand': { lead: 0, copertura: 0, impression: 0, click: 0, spending: 0, cpl: 0, campaigns: 0 },
        'Lead White': { lead: 0, copertura: 0, impression: 0, click: 0, spending: 0, cpl: 0, campaigns: 0 },
        'Like Brand': { like: 0, copertura: 0, impression: 0, click: 0, spending: 0, cpl: 0, campaigns: 0 },
        'Like White': { like: 0, copertura: 0, impression: 0, click: 0, spending: 0, cpl: 0, campaigns: 0 }
      };

      campaignData.forEach(({ campaign, dailyData }) => {
        const type = campaign.campaign_type;
        if (!typeTotals[type]) return;

        typeTotals[type].campaigns++;
        
        // FILTRO CRITICO: Somma SOLO i dati del mese selezionato!
        const [year, month] = selectedMonth.split('-');
        const monthPrefix = `${year}-${month}`;  // es: "2025-09"
        
        dailyData.forEach(data => {
          // Verifica che la data appartenga al mese selezionato
          if (!data.date || !data.date.startsWith(monthPrefix)) {
            return;  // Skip questo record se non è del mese corretto
          }
          
          typeTotals[type].copertura += data.copertura;
          typeTotals[type].impression += data.impression;
          typeTotals[type].click += data.click;
          typeTotals[type].spending += data.spending;
          
          if (type.includes('Lead')) {
            typeTotals[type].lead += data.lead || 0;
          } else if (type.includes('Like')) {
            typeTotals[type].like += data.like || 0;
          }
        });
      });

      // Calculate CPL for each type
      Object.keys(typeTotals).forEach(type => {
        const total = typeTotals[type];
        if (type.includes('Lead') && total.lead > 0) {
          total.cpl = total.spending / total.lead;
        } else if (type.includes('Like') && total.like > 0) {
          total.cpl = total.spending / total.like;
        }
      });

      setReportData({
        client: clients.find(c => c.id === selectedClient),
        month: selectedMonth,
        campaigns: campaignData,
        typeTotals
      });
      
      setSuccess('Report generato con successo');
    } catch (error) {
      setError('Errore nella generazione del report');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData) return;
    
    try {
      // Create new workbook
      const workbook = XLSX.utils.book_new();
      
      // Create sheet data exactly like the example file
      const sheetData = [];
      
      // Main title (row 1) - merged across columns
      sheetData.push(['📊 REPORT CAMPAGNE DIGITALI', '', '', '', '', '', '']);
      
      // Client name (row 2) - merged across columns  
      sheetData.push([reportData.client.name.toUpperCase(), '', '', '', '', '', '']);
      
      // Month and date (row 3)
      sheetData.push([
        new Date(reportData.month + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }),
        '', '', '', 
        'Generato il: ' + new Date().toLocaleDateString('it-IT'),
        '', ''
      ]);
      
      // Empty row (row 4)
      sheetData.push(['', '', '', '', '', '', '']);
      
      // Lead boxes row headers (row 5)
      sheetData.push(['🔷 LEAD BRAND', '', '🔶 LEAD WHITE', '', '', '', '']);
      
      // Lead Brand and Lead White data (rows 6-11)
      const leadBrand = reportData.typeTotals['Lead Brand'];
      const leadWhite = reportData.typeTotals['Lead White'];
      
      sheetData.push(['Lead:', leadBrand.lead.toLocaleString(), 'Lead:', leadWhite.lead.toLocaleString(), '', '', '']);
      sheetData.push(['Copertura:', leadBrand.copertura.toLocaleString(), 'Copertura:', leadWhite.copertura.toLocaleString(), '', '', '']);
      sheetData.push(['Impression:', leadBrand.impression.toLocaleString(), 'Impression:', leadWhite.impression.toLocaleString(), '', '', '']);
      sheetData.push(['Click:', leadBrand.click.toLocaleString(), 'Click:', leadWhite.click.toLocaleString(), '', '', '']);
      sheetData.push(['Spending:', '€' + leadBrand.spending.toFixed(2), 'Spending:', '€' + leadWhite.spending.toFixed(2), '', '', '']);
      sheetData.push(['CPL:', '€' + leadBrand.cpl.toFixed(2), 'CPL:', '€' + leadWhite.cpl.toFixed(2), '', '', '']);
      
      // Empty row (row 12)
      sheetData.push(['', '', '', '', '', '', '']);
      
      // Like boxes row headers (row 13)  
      sheetData.push(['💚 LIKE BRAND', '', '🤍 LIKE WHITE', '', '', '', '']);
      
      // Like Brand and Like White data (rows 14-19)
      const likeBrand = reportData.typeTotals['Like Brand'];
      const likeWhite = reportData.typeTotals['Like White'];
      
      sheetData.push(['Like:', likeBrand.like.toLocaleString(), 'Like:', likeWhite.like.toLocaleString(), '', '', '']);
      sheetData.push(['Copertura:', likeBrand.copertura.toLocaleString(), 'Copertura:', likeWhite.copertura.toLocaleString(), '', '', '']);
      sheetData.push(['Impression:', likeBrand.impression.toLocaleString(), 'Impression:', likeWhite.impression.toLocaleString(), '', '', '']);
      sheetData.push(['Click:', likeBrand.click.toLocaleString(), 'Click:', likeWhite.click.toLocaleString(), '', '', '']);
      sheetData.push(['Spending:', '€' + likeBrand.spending.toFixed(2), 'Spending:', '€' + likeWhite.spending.toFixed(2), '', '', '']);
      sheetData.push(['CPL:', '€' + likeBrand.cpl.toFixed(2), 'CPL:', '€' + likeWhite.cpl.toFixed(2), '', '', '']);
      
      // Empty rows (rows 20-21)
      sheetData.push(['', '', '', '', '', '', '']);
      sheetData.push(['', '', '', '', '', '', '']);
      
      // Totali Generali header (row 22)
      sheetData.push(['🎯 TOTALI GENERALI', '', '', '', '', '', '']);
      
      // Totali Generali data (rows 23-25)
      sheetData.push([
        'Lead Totali:',
        (reportData.typeTotals['Lead Brand'].lead + reportData.typeTotals['Lead White'].lead).toLocaleString(),
        'Like Totali:',
        (reportData.typeTotals['Like Brand'].like + reportData.typeTotals['Like White'].like).toLocaleString(),
        '', '', ''
      ]);
      sheetData.push([
        'Copertura Totale:',
        Object.values(reportData.typeTotals).reduce((sum, total) => sum + total.copertura, 0).toLocaleString(),
        'Impression Totali:',
        Object.values(reportData.typeTotals).reduce((sum, total) => sum + total.impression, 0).toLocaleString(),
        '', '', ''
      ]);
      sheetData.push([
        'Click Totali:',
        Object.values(reportData.typeTotals).reduce((sum, total) => sum + total.click, 0).toLocaleString(),
        'Spending Totale:',
        '€' + Object.values(reportData.typeTotals).reduce((sum, total) => sum + total.spending, 0).toLocaleString('it-IT', { minimumFractionDigits: 2 }),
        '', '', ''
      ]);
      
      // Create the worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      
      // Set column widths to match example
      worksheet['!cols'] = [
        { wch: 18 }, // Column A - Labels
        { wch: 16 }, // Column B - Values  
        { wch: 18 }, // Column C - Labels
        { wch: 16 }, // Column D - Values
        { wch: 5 },  // Column E - Spacer
        { wch: 5 },  // Column F - Spacer
        { wch: 5 }   // Column G - Spacer
      ];
      
      // Apply styling to exactly match the example file
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      
      // Style the title (row 1) - Dark background, white text, bold, large
      for (let col = 0; col < 7; col++) {
        const titleCell = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[titleCell]) {
          worksheet[titleCell].s = {
            fill: { fgColor: { rgb: '1F2937' } },
            font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 14 },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'medium', color: { rgb: '000000' } },
              bottom: { style: 'medium', color: { rgb: '000000' } },
              left: { style: 'medium', color: { rgb: '000000' } },
              right: { style: 'medium', color: { rgb: '000000' } }
            }
          };
        }
      }
      
      // Style client name (row 2) - Centered, bold
      for (let col = 0; col < 7; col++) {
        const clientCell = XLSX.utils.encode_cell({ r: 1, c: col });
        if (worksheet[clientCell]) {
          worksheet[clientCell].s = {
            font: { bold: true, sz: 12, color: { rgb: '1F2937' } },
            alignment: { horizontal: 'center', vertical: 'center' }
          };
        }
      }
      
      // Define the colored boxes with exact styling
      const boxes = [
        // Lead Brand box (rows 4-10, cols 0-1) - Blue
        { 
          startRow: 4, endRow: 10, startCol: 0, endCol: 1, 
          bgColor: 'E3F2FD', borderColor: '1976D2', headerBg: '1976D2', headerText: 'FFFFFF'
        },
        // Lead White box (rows 4-10, cols 2-3) - Cyan  
        { 
          startRow: 4, endRow: 10, startCol: 2, endCol: 3, 
          bgColor: 'E0F7FA', borderColor: '00ACC1', headerBg: '00ACC1', headerText: 'FFFFFF'
        },
        // Like Brand box (rows 12-18, cols 0-1) - Green
        { 
          startRow: 12, endRow: 18, startCol: 0, endCol: 1, 
          bgColor: 'E8F5E8', borderColor: '388E3C', headerBg: '388E3C', headerText: 'FFFFFF'
        },
        // Like White box (rows 12-18, cols 2-3) - Emerald
        { 
          startRow: 12, endRow: 18, startCol: 2, endCol: 3, 
          bgColor: 'E0F2F1', borderColor: '00695C', headerBg: '00695C', headerText: 'FFFFFF'
        },
        // Totali box (rows 21-24, cols 0-3) - Orange
        { 
          startRow: 21, endRow: 24, startCol: 0, endCol: 3, 
          bgColor: 'FFF3E0', borderColor: 'F57C00', headerBg: 'F57C00', headerText: 'FFFFFF'
        }
      ];
      
      // Apply box styling
      boxes.forEach(box => {
        for (let row = box.startRow; row <= box.endRow; row++) {
          for (let col = box.startCol; col <= box.endCol; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            
            if (!worksheet[cellAddress]) {
              worksheet[cellAddress] = { t: 's', v: '' };
            }
            
            const isHeader = (row === box.startRow) && worksheet[cellAddress].v && (
              worksheet[cellAddress].v.toString().includes('🔷') ||
              worksheet[cellAddress].v.toString().includes('🔶') ||
              worksheet[cellAddress].v.toString().includes('💚') ||
              worksheet[cellAddress].v.toString().includes('🤍') ||
              worksheet[cellAddress].v.toString().includes('🎯')
            );
            
            worksheet[cellAddress].s = {
              fill: { fgColor: { rgb: isHeader ? box.headerBg : box.bgColor } },
              font: { 
                color: { rgb: isHeader ? box.headerText : '1F2937' },
                bold: isHeader || (worksheet[cellAddress].v && worksheet[cellAddress].v.toString().includes(':')),
                sz: isHeader ? 11 : 10
              },
              border: {
                top: { style: 'medium', color: { rgb: box.borderColor } },
                bottom: { style: 'medium', color: { rgb: box.borderColor } },
                left: { style: 'medium', color: { rgb: box.borderColor } },
                right: { style: 'medium', color: { rgb: box.borderColor } }
              },
              alignment: { 
                horizontal: isHeader ? 'center' : 'left', 
                vertical: 'center' 
              }
            };
          }
        }
      });
      
      // Set row heights for better appearance
      worksheet['!rows'] = sheetData.map((_, index) => {
        if (index === 0) return { hpt: 25 }; // Title
        if (index === 1) return { hpt: 20 }; // Client
        if ([4, 12, 21].includes(index)) return { hpt: 18 }; // Headers
        return { hpt: 16 }; // Regular rows
      });
      
      // Merge cells for title and client name
      worksheet['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, // Title
        { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }  // Client name
      ];
      
      // Add the sheet
      XLSX.utils.book_append_sheet(workbook, worksheet, '📊 Report Totali');
      
      // Generate filename matching the example
      const fileName = `📊_Report_Totali_${reportData.client.name.replace(/[^a-zA-Z0-9]/g, '_')}_${reportData.month.replace('-', '_')}.xlsx`;
      
      // Write and download file
      XLSX.writeFile(workbook, fileName);
      
      setSuccess(`🎉 Excel con layout identico generato: "${fileName}"!\n📊 Stile grafico replicato dal file di esempio`);
      
    } catch (error) {
      console.error('Error generating Excel with example layout:', error);
      setError('Errore durante la generazione dell\'Excel: ' + error.message);
    }
  };

  useEffect(() => {
    if (selectedClient) {
      loadCampaigns(selectedClient);
    }
  }, [selectedClient]);

  return (
    <div className="space-y-6">
      {/* Report Generation Controls */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Export Report Mensile</h3>
        
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Cliente</label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Seleziona Cliente --</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Mese</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={generateReport}
              disabled={loading || !selectedClient}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 transition-colors"
            >
              {loading ? 'Generando...' : 'Genera Report'}
            </button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-800">
              Report {reportData.client.name} - {new Date(reportData.month + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
            </h3>
            <button
              onClick={exportToExcel}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Export Excel</span>
            </button>
          </div>

          {/* Campaign Type Totals */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            {Object.entries(reportData.typeTotals).map(([type, totals]) => (
              <div key={type} className="border border-slate-200 rounded-lg p-4">
                <h4 className="font-medium text-slate-800 mb-2">{type}</h4>
                <div className="space-y-1 text-sm">
                  {type.includes('Lead') && <div>Lead: {totals.lead.toLocaleString()}</div>}
                  {type.includes('Like') && <div>Like: {totals.like.toLocaleString()}</div>}
                  <div>Copertura: {totals.copertura.toLocaleString()}</div>
                  <div>Impression: {totals.impression.toLocaleString()}</div>
                  <div>Click: {totals.click.toLocaleString()}</div>
                  <div>Spending: €{totals.spending.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                  <div className="font-medium text-blue-600">CPL: €{totals.cpl.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Overall Totals */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-800 mb-2">Totali Generali</h4>
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6 text-sm">
              <div>
                <span className="font-medium">Lead Totali:</span> {
                  (reportData.typeTotals['Lead Brand'].lead + reportData.typeTotals['Lead White'].lead).toLocaleString()
                }
              </div>
              <div>
                <span className="font-medium">Like Totali:</span> {
                  (reportData.typeTotals['Like Brand'].like + reportData.typeTotals['Like White'].like).toLocaleString()
                }
              </div>
              <div>
                <span className="font-medium">Copertura Totale:</span> {
                  Object.values(reportData.typeTotals).reduce((sum, total) => sum + total.copertura, 0).toLocaleString()
                }
              </div>
              <div>
                <span className="font-medium">Impression Totali:</span> {
                  Object.values(reportData.typeTotals).reduce((sum, total) => sum + total.impression, 0).toLocaleString()
                }
              </div>
              <div>
                <span className="font-medium">Click Totali:</span> {
                  Object.values(reportData.typeTotals).reduce((sum, total) => sum + total.click, 0).toLocaleString()
                }
              </div>
              <div>
                <span className="font-medium">Spending Totale:</span> €{
                  Object.values(reportData.typeTotals).reduce((sum, total) => sum + total.spending, 0)
                    .toLocaleString('it-IT', { minimumFractionDigits: 2 })
                }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Password Settings Component - Change Password for all users
const PasswordSettings = ({ user }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    clearMessages();

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Tutti i campi sono obbligatori');
      return;
    }

    if (newPassword.length < 6) {
      setError('La nuova password deve essere di almeno 6 caratteri');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('La nuova password e la conferma non coincidono');
      return;
    }

    if (currentPassword === newPassword) {
      setError('La nuova password deve essere diversa da quella attuale');
      return;
    }

    try {
      setLoading(true);

      await axios.put(`${API}/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });

      setSuccess('Password cambiata con successo!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

    } catch (error) {
      setError(error.response?.data?.detail || 'Errore durante il cambio password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 rounded-xl text-white">
        <div className="flex items-center space-x-3">
          <Settings className="h-8 w-8" />
          <div>
            <h2 className="text-xl font-semibold">Impostazioni Account</h2>
            <p className="text-blue-100">Gestisci le impostazioni del tuo account</p>
          </div>
        </div>
      </div>

      {/* Change Password Form */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Cambia Password</h3>
        
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Password Attuale
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full p-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Inserisci la password attuale"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-5 w-5 text-slate-400" />
                ) : (
                  <Eye className="h-5 w-5 text-slate-400" />
                )}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Nuova Password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Inserisci la nuova password (min. 6 caratteri)"
                minLength="6"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <EyeOff className="h-5 w-5 text-slate-400" />
                ) : (
                  <Eye className="h-5 w-5 text-slate-400" />
                )}
              </button>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              La password deve essere di almeno 6 caratteri
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Conferma Nuova Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Conferma la nuova password"
              required
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                clearMessages();
              }}
              className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              disabled={loading}
            >
              Reset
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
              <Save className="h-4 w-4" />
              <span>{loading ? 'Salvando...' : 'Salva Password'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* User Info */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Informazioni Account</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-500">Username</label>
            <p className="text-slate-800">{user?.username}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-500">Email</label>
            <p className="text-slate-800">{user?.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-500">Ruolo</label>
            <p className="text-slate-800 capitalize">{user?.role}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-500">Unità</label>
            <p className="text-slate-800">
              {user?.units && user.units.length > 0 
                ? user.units.join(', ') 
                : (user?.unit || 'Non assegnata')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar View Component for Campaign Reports - Daily Data Visualization
const CalendarView = ({ clients, user, setSuccess, setError }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Check user permissions - same as other Campaign Reports components
  const canView = user && (
    user.unit === 'Digital' || 
    user.role === 'admin' || 
    (user.role === 'responsabile_unit' && user.unit !== 'Agenti')
  );

  const loadCalendarData = async () => {
    if (!selectedClient) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/digital/calendar-data/${selectedClient.id}/${selectedMonth}/${selectedYear}`);
      setCalendarData(response.data.calendar_data || []);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nel caricamento dei dati calendario');
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarData();
  }, [selectedClient, selectedMonth, selectedYear]);

  const getDayData = (day) => {
    const dateStr = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return calendarData.find(data => data.date === dateStr);
  };

  const renderCalendar = () => {
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-20 border border-slate-200"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = getDayData(day);
      const hasData = dayData && dayData.daily_total.total_spending > 0;
      
      days.push(
        <div
          key={day}
          className={`h-20 border border-slate-200 p-1 cursor-pointer transition-colors relative ${
            hasData 
              ? 'bg-blue-50 hover:bg-blue-100' 
              : 'bg-white hover:bg-slate-50'
          }`}
          onClick={() => hasData && setSelectedDay({day, data: dayData})}
        >
          <div className="font-medium text-xs">{day}</div>
          {hasData && (
            <div className="absolute bottom-1 right-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            </div>
          )}
          {hasData && (
            <div className="text-xs text-slate-600 mt-1">
              €{dayData.daily_total.total_spending.toFixed(0)}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  if (!canView) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-800">Accesso Negato</h3>
        <p className="text-slate-600">Non hai l'autorizzazione per visualizzare i report di campagne.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-xl text-white">
        <div className="flex items-center space-x-3">
          <Calendar className="h-8 w-8" />
          <div>
            <h2 className="text-xl font-semibold">Calendario Dati Giornalieri</h2>
            <p className="text-indigo-100">Visualizzazione calendario con totali giornalieri per tipologia</p>
          </div>
        </div>
      </div>

      {/* Client Selection */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Selezione Cliente e Periodo</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Client Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Cliente</label>
            <select
              value={selectedClient?.id || ''}
              onChange={(e) => {
                const client = clients.find(c => c.id === e.target.value);
                setSelectedClient(client);
              }}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Seleziona un cliente</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          {/* Month Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Mese</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Array.from({length: 12}, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2024, i, 1).toLocaleDateString('it-IT', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          {/* Year Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Anno</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={2024}>2024</option>
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
            </select>
          </div>
        </div>
      </div>

      {/* Calendar */}
      {selectedClient && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-800">
              Calendario {selectedClient.name} - {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
            </h3>
            {loading && (
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>

          {/* Days of week header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(day => (
              <div key={day} className="p-2 text-center font-medium text-slate-600 text-sm bg-slate-100 rounded">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {renderCalendar()}
          </div>

          <div className="mt-4 text-sm text-slate-600">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span>Giorni con dati</span>
              </div>
              <span>Clicca su un giorno per vedere i dettagli</span>
            </div>
          </div>
        </div>
      )}

      {/* Day Details Modal */}
      {selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-slate-800">
                  Dettagli Giorno {selectedDay.day} {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  onClick={() => setSelectedDay(null)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Daily Totals */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg mb-6">
                <h4 className="font-semibold text-slate-800 mb-3">Totali Giornalieri</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm text-slate-600">Total Lead</span>
                    <p className="text-lg font-semibold text-blue-600">{selectedDay.data.daily_total.total_lead}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-600">Total Like</span>
                    <p className="text-lg font-semibold text-purple-600">{selectedDay.data.daily_total.total_like}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-600">Spending</span>
                    <p className="text-lg font-semibold text-green-600">€{selectedDay.data.daily_total.total_spending.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-600">Copertura</span>
                    <p className="text-lg font-semibold text-orange-600">{selectedDay.data.daily_total.total_copertura.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-600">Impression</span>
                    <p className="text-lg font-semibold text-teal-600">{selectedDay.data.daily_total.total_impression.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-600">Click</span>
                    <p className="text-lg font-semibold text-red-600">{selectedDay.data.daily_total.total_click.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Campaign Types Breakdown */}
              <div>
                <h4 className="font-semibold text-slate-800 mb-3">Dettaglio per Tipologia Campagna</h4>
                <div className="space-y-4">
                  {Object.entries(selectedDay.data.campaign_types).map(([type, data]) => {
                    const hasData = (data.lead > 0 || data.like > 0) && data.spending > 0;
                    if (!hasData) return null;

                    return (
                      <div key={type} className="border border-slate-200 rounded-lg p-4">
                        <h5 className="font-medium text-slate-700 mb-2">{type}</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <span className="text-xs text-slate-500">{type.includes('Lead') ? 'Lead' : 'Like'}</span>
                            <p className="font-semibold text-blue-600">{type.includes('Lead') ? data.lead : data.like}</p>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500">Spending</span>
                            <p className="font-semibold text-green-600">€{data.spending.toFixed(2)}</p>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500">CPL</span>
                            <p className="font-semibold text-purple-600">€{data.cpl.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// CEO Appointments System Component (CEO Unit Only)
const CEOAppointments = ({ user }) => {
  const [appointments, setAppointments] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewAppointmentForm, setShowNewAppointmentForm] = useState(false);
  
  // New appointment form state
  const [newAppointment, setNewAppointment] = useState({
    name: '',
    location: '',
    date: '',
    time: ''
  });

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ceo/appointments`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      setAppointments(response.data.appointments || []);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nel caricamento degli appuntamenti');
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const createAppointment = async () => {
    if (!newAppointment.name || !newAppointment.location || !newAppointment.date || !newAppointment.time) {
      setError('Tutti i campi sono obbligatori');
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      // Combine date and time
      const appointmentDatetime = `${newAppointment.date}T${newAppointment.time}:00`;

      await axios.post(`${API}/ceo/appointments`, {
        name: newAppointment.name,
        location: newAppointment.location,
        appointment_datetime: appointmentDatetime
      });

      setSuccess('Appuntamento creato con successo');
      setNewAppointment({ name: '', location: '', date: '', time: '' });
      setShowNewAppointmentForm(false);
      await loadAppointments();

    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nella creazione dell\'appuntamento');
    } finally {
      setLoading(false);
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo appuntamento?')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/ceo/appointments/${appointmentId}`);
      setSuccess('Appuntamento eliminato con successo');
      await loadAppointments();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'appuntamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
  }, [selectedMonth, selectedYear]);

  const formatAppointmentDateTime = (dateTimeStr) => {
    try {
      const dt = new Date(dateTimeStr);
      return dt.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateTimeStr;
    }
  };

  const generateCalendarDays = () => {
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 border border-slate-200"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayAppointments = appointments.filter(app => {
        const appDate = new Date(app.appointment_datetime);
        return appDate.getDate() === day && appDate.getMonth() === selectedMonth - 1 && appDate.getFullYear() === selectedYear;
      });

      days.push(
        <div key={day} className="h-24 border border-slate-200 p-1">
          <div className="font-medium text-sm text-slate-600 mb-1">{day}</div>
          <div className="space-y-1">
            {dayAppointments.map(appointment => (
              <div 
                key={appointment.id} 
                className="bg-blue-100 text-blue-800 text-xs p-1 rounded cursor-pointer hover:bg-blue-200"
                title={`${appointment.name} - ${appointment.location}`}
                onClick={() => {
                  alert(`${appointment.name}\nLuogo: ${appointment.location}\nData: ${formatAppointmentDateTime(appointment.appointment_datetime)}`);
                }}
              >
                {appointment.name.substring(0, 15)}
                {appointment.name.length > 15 && '...'}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return days;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-xl text-white">
        <div className="flex items-center space-x-3">
          <Calendar className="h-8 w-8" />
          <div>
            <h2 className="text-xl font-semibold">Calendario Appuntamenti CEO</h2>
            <p className="text-purple-100">Gestione appuntamenti condivisa con alert email automatici</p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-green-800">{success}</span>
            </div>
            <button onClick={clearMessages} className="text-green-600 hover:text-green-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-red-800">{error}</span>
            </div>
            <button onClick={clearMessages} className="text-red-600 hover:text-red-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* New Appointment Form */}
      {showNewAppointmentForm && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Nuovo Appuntamento</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nome Appuntamento</label>
              <input
                type="text"
                value={newAppointment.name}
                onChange={(e) => setNewAppointment({...newAppointment, name: e.target.value})}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Es: Riunione con cliente"
                maxLength="200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Luogo Appuntamento</label>
              <input
                type="text"
                value={newAppointment.location}
                onChange={(e) => setNewAppointment({...newAppointment, location: e.target.value})}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Es: Ufficio principale"
                maxLength="200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Data</label>
              <input
                type="date"
                value={newAppointment.date}
                onChange={(e) => setNewAppointment({...newAppointment, date: e.target.value})}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Ora</label>
              <input
                type="time"
                value={newAppointment.time}
                onChange={(e) => setNewAppointment({...newAppointment, time: e.target.value})}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setShowNewAppointmentForm(false);
                setNewAppointment({ name: '', location: '', date: '', time: '' });
                clearMessages();
              }}
              className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              onClick={createAppointment}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
              <Save className="h-4 w-4" />
              <span>{loading ? 'Salvando...' : 'Crea Appuntamento'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Calendar Controls */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mese</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Array.from({length: 12}, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2024, i, 1).toLocaleDateString('it-IT', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Anno</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>
          </div>

          <button
            onClick={() => setShowNewAppointmentForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            disabled={loading}
          >
            <Plus className="h-4 w-4" />
            <span>Nuovo Appuntamento</span>
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-800">
            {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
          </h3>
          {loading && (
            <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(day => (
            <div key={day} className="p-2 text-center font-medium text-slate-600 text-sm bg-slate-100 rounded">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {generateCalendarDays()}
        </div>

        <div className="mt-4 text-sm text-slate-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-100 border border-blue-200 rounded"></div>
              <span>Appuntamenti programmati</span>
            </div>
            <span>Clicca su un appuntamento per vedere i dettagli</span>
          </div>
        </div>
      </div>

      {/* Appointments List */}
      {appointments.length > 0 && (
        <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            Appuntamenti per {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
          </h3>

          <div className="space-y-3">
            {appointments
              .sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime))
              .map((appointment) => (
                <div key={appointment.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-800">{appointment.name}</h4>
                      <div className="mt-2 space-y-1 text-sm text-slate-600">
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4" />
                          <span>{appointment.location}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4" />
                          <span>{formatAppointmentDateTime(appointment.appointment_datetime)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => deleteAppointment(appointment.id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                      title="Elimina appuntamento"
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            }
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-800">
                <strong>Alert Automatici:</strong> Riceverai email di promemoria 24 ore e 4 ore prima di ogni appuntamento.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Solar Installations System Component (Admin, CEO, ResponsabileUnit Fotovoltaico)
const SolarInstallations = ({ user }) => {
  const [installations, setInstallations] = useState([]);
  const [solarEmployees, setSolarEmployees] = useState([]);
  const [solarTeams, setSolarTeams] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentView, setCurrentView] = useState('calendar'); // calendar, teams, installations, planning
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Enriched data state
  const [enrichedData, setEnrichedData] = useState({});
  const [loadingEnriched, setLoadingEnriched] = useState({});
  
  // AI Planning state
  const [planningCities, setPlanningCities] = useState('');
  const [planningResult, setPlanningResult] = useState(null);
  const [loadingPlanning, setLoadingPlanning] = useState(false);
  
  // Team management state
  const [showNewTeamForm, setShowNewTeamForm] = useState(false);
  const [newTeam, setNewTeam] = useState({
    name: '',
    leader_id: '',
    members: []
  });
  
  // Installation form state  
  const [showNewInstallationForm, setShowNewInstallationForm] = useState(false);
  const [newInstallation, setNewInstallation] = useState({
    client_first_name: '',
    client_last_name: '',
    installation_type: '',
    address: '',
    city: '',
    phone: '',
    time_slot: '09:00',
    installation_date: '',
    team_id: '',
    notes: ''
  });

  // Edit installation state
  const [showEditInstallationForm, setShowEditInstallationForm] = useState(false);
  const [editingInstallation, setEditingInstallation] = useState(null);
  const [editInstallation, setEditInstallation] = useState({
    client_first_name: '',
    client_last_name: '',
    installation_type: '',
    address: '',
    city: '',
    phone: '',
    time_slot: '09:00',
    installation_date: '',
    team_id: '',
    notes: ''
  });

  // Edit team state
  const [showEditTeamForm, setShowEditTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeam, setEditTeam] = useState({
    name: '',
    leader_id: '',
    members: []
  });

  // Check user permissions
  const canManage = user && (
    user.role === 'admin' || 
    (user.role === 'responsabile_unit' && user.unit === 'CEO') ||
    (user.role === 'responsabile_unit' && (user.unit === 'Fotovoltaico' || user.unit === 'Fibra'))
  );

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const loadSolarEmployees = async () => {
    try {
      const response = await axios.get(`${API}/solar/employees`);
      setSolarEmployees(response.data.employees || []);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nel caricamento dipendenti');
      console.error('Error loading solar employees:', error);
    }
  };

  const loadSolarTeams = async () => {
    try {
      const response = await axios.get(`${API}/solar/teams`);
      setSolarTeams(response.data.teams || []);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nel caricamento squadre');
      console.error('Error loading solar teams:', error);
    }
  };

  const loadInstallations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/solar/installations`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      setInstallations(response.data.installations || []);
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nel caricamento delle installazioni');
      console.error('Error loading installations:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTeam = async () => {
    if (!newTeam.name || !newTeam.leader_id || !newTeam.members.length) {
      setError('Nome squadra, capo squadra e membri sono obbligatori');
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      await axios.post(`${API}/solar/teams`, {
        name: newTeam.name,
        leader_id: newTeam.leader_id,
        members: newTeam.members
      });

      setSuccess('Squadra creata con successo');
      setNewTeam({ name: '', leader_id: '', members: [] });
      setShowNewTeamForm(false);
      await loadSolarTeams();

    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa squadra?')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/solar/teams/${teamId}`);
      setSuccess('Squadra eliminata con successo');
      await loadSolarTeams();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nell\'eliminazione della squadra');
    } finally {
      setLoading(false);
    }
  };

  const openEditTeam = (team) => {
    setEditingTeam(team);
    setEditTeam({
      name: team.name,
      leader_id: team.leader_id,
      members: team.members.map(member => ({
        employee_id: member.employee_id,
        employee_name: member.employee_name,
        qualification: member.qualification
      }))
    });
    setShowEditTeamForm(true);
    clearMessages();
  };

  const updateTeam = async () => {
    if (!editTeam.name || !editTeam.leader_id || editTeam.members.length === 0) {
      setError('Tutti i campi obbligatori devono essere compilati e almeno un membro deve essere presente');
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      await axios.put(`${API}/solar/teams/${editingTeam.id}`, editTeam);

      setSuccess('Squadra aggiornata con successo');
      setEditingTeam(null);
      setEditTeam({
        name: '',
        leader_id: '',
        members: []
      });
      setShowEditTeamForm(false);
      await loadSolarTeams();

    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const cancelEditTeam = () => {
    setEditingTeam(null);
    setEditTeam({
      name: '',
      leader_id: '',
      members: []
    });
    setShowEditTeamForm(false);
    clearMessages();
  };

  const addEditTeamMember = () => {
    setEditTeam(prev => ({
      ...prev,
      members: [...prev.members, { employee_id: '', employee_name: '', qualification: 'Installatore' }]
    }));
  };

  const updateEditTeamMember = (index, field, value) => {
    const updatedMembers = [...editTeam.members];
    updatedMembers[index][field] = value;
    
    if (field === 'employee_id') {
      const employee = solarEmployees.find(emp => emp.id === value);
      if (employee) {
        updatedMembers[index].employee_name = employee.username;
      }
    }
    
    setEditTeam(prev => ({
      ...prev,
      members: updatedMembers
    }));
  };

  const removeEditTeamMember = (index) => {
    setEditTeam(prev => ({
      ...prev,
      members: prev.members.filter((_, i) => i !== index)
    }));
  };

  const createInstallation = async () => {
    if (!newInstallation.client_first_name || !newInstallation.client_last_name || 
        !newInstallation.installation_type || !newInstallation.address || !newInstallation.city ||
        !newInstallation.phone || !newInstallation.installation_date || !newInstallation.team_id) {
      setError('Tutti i campi obbligatori devono essere compilati');
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      await axios.post(`${API}/solar/installations`, newInstallation);

      setSuccess('Installazione creata con successo');
      setNewInstallation({
        client_first_name: '', client_last_name: '', installation_type: '',
        address: '', city: '', phone: '', time_slot: 'Mattina',
        installation_date: '', team_id: '', notes: ''
      });
      setShowNewInstallationForm(false);
      await loadInstallations();

    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const deleteInstallation = async (installationId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa installazione?')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/solar/installations/${installationId}`);
      setSuccess('Installazione eliminata con successo');
      await loadInstallations();
    } catch (error) {
      setError(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'installazione');
    } finally {
      setLoading(false);
    }
  };

  const openEditInstallation = (installation) => {
    setEditingInstallation(installation);
    setEditInstallation({
      client_first_name: installation.client_first_name,
      client_last_name: installation.client_last_name,
      installation_type: installation.installation_type,
      address: installation.address,
      city: installation.city,
      phone: installation.phone,
      time_slot: installation.time_slot,
      installation_date: installation.installation_date,
      team_id: installation.team_id,
      notes: installation.notes || ''
    });
    setShowEditInstallationForm(true);
    clearMessages();
  };

  const updateInstallation = async () => {
    if (!editInstallation.client_first_name || !editInstallation.client_last_name || 
        !editInstallation.installation_type || !editInstallation.address || !editInstallation.city ||
        !editInstallation.phone || !editInstallation.installation_date || !editInstallation.team_id) {
      setError('Tutti i campi obbligatori devono essere compilati');
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      await axios.put(`${API}/solar/installations/${editingInstallation.id}`, editInstallation);

      setSuccess('Installazione aggiornata con successo');
      setEditingInstallation(null);
      setEditInstallation({
        client_first_name: '', client_last_name: '', installation_type: '',
        address: '', city: '', phone: '', time_slot: '09:00',
        installation_date: '', team_id: '', notes: ''
      });
      setShowEditInstallationForm(false);
      await loadInstallations();

    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Load enriched data for installation
  const loadEnrichedData = async (installationId) => {
    if (loadingEnriched[installationId]) return;
    
    try {
      setLoadingEnriched(prev => ({ ...prev, [installationId]: true }));
      const response = await axios.post(`${API}/solar/installations/${installationId}/enrich-data`);
      setEnrichedData(prev => ({ ...prev, [installationId]: response.data }));
    } catch (error) {
      console.error('Errore caricamento dati arricchiti:', error);
    } finally {
      setLoadingEnriched(prev => ({ ...prev, [installationId]: false }));
    }
  };

  // AI Planning
  const runAIPlanning = async () => {
    if (!planningCities.trim()) {
      setError('Inserisci almeno una città');
      return;
    }

    try {
      setLoadingPlanning(true);
      clearMessages();
      
      const citiesList = planningCities.split(',').map(c => c.trim()).filter(c => c);
      
      const response = await axios.post(`${API}/solar/installations/ai-planning`, {
        cities: citiesList,
        optimize_for: 'distance'
      });
      
      setPlanningResult(response.data);
      setSuccess('Pianificazione AI completata!');
      
    } catch (error) {
      setError(formatErrorMessage(error));
    } finally {
      setLoadingPlanning(false);
    }
  };

  const cancelEditInstallation = () => {
    setEditingInstallation(null);
    setEditInstallation({
      client_first_name: '', client_last_name: '', installation_type: '',
      address: '', city: '', phone: '', time_slot: '09:00',
      installation_date: '', team_id: '', notes: ''
    });
    setShowEditInstallationForm(false);
    clearMessages();
  };

  // Team management functions
  const addTeamMember = () => {
    setNewTeam(prev => ({
      ...prev,
      members: [
        ...prev.members,
        { employee_id: '', employee_name: '', qualification: 'Installatore' }
      ]
    }));
  };

  const updateTeamMember = (index, field, value) => {
    const updatedMembers = [...newTeam.members];
    updatedMembers[index][field] = value;
    
    if (field === 'employee_id') {
      const employee = solarEmployees.find(emp => emp.id === value);
      if (employee) {
        updatedMembers[index].employee_name = employee.username;
      }
    }
    
    setNewTeam(prev => ({
      ...prev,
      members: updatedMembers
    }));
  };

  const removeTeamMember = (index) => {
    setNewTeam(prev => ({
      ...prev,
      members: prev.members.filter((_, i) => i !== index)
    }));
  };

  const generateCalendarDays = () => {
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 border border-slate-200"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayInstallations = installations.filter(inst => {
        const instDate = new Date(inst.installation_date);
        return instDate.getDate() === day && instDate.getMonth() === selectedMonth - 1 && instDate.getFullYear() === selectedYear;
      });

      days.push(
        <div 
          key={day} 
          className="h-24 border border-slate-200 p-1 cursor-pointer hover:bg-orange-50 transition-colors"
          onClick={() => handleDayClick(day)}
        >
          <div className="font-medium text-sm text-slate-600 mb-1">{day}</div>
          <div className="space-y-1">
            {dayInstallations.slice(0, 2).map(installation => (
              <div 
                key={installation.id} 
                className="bg-orange-100 text-orange-800 text-xs p-1 rounded"
                title={`${installation.client_first_name} ${installation.client_last_name} - ${installation.city}`}
              >
                {installation.client_first_name} {installation.client_last_name.substring(0, 1)}.
                <br />
                {installation.city.substring(0, 8)}
                {installation.city.length > 8 && '...'}
              </div>
            ))}
            {dayInstallations.length > 2 && (
              <div className="text-xs text-slate-500">
                +{dayInstallations.length - 2} altre
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const handleDayClick = (day) => {
    const clickedDate = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    setSelectedDate(clickedDate);
    // Pop-up opens immediately in calendar view - no view change needed
  };

  const getDayInstallations = () => {
    if (!selectedDate) return [];
    return installations.filter(inst => inst.installation_date === selectedDate);
  };

  useEffect(() => {
    if (canManage) {
      loadSolarEmployees();
      loadSolarTeams();
      loadInstallations();
    }
  }, [selectedMonth, selectedYear, canManage]);

  const formatInstallationDateTime = (date, timeSlot) => {
    try {
      const dt = new Date(date + "T00:00:00");
      const formattedDate = dt.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
      return `${formattedDate} - ${timeSlot}`;
    } catch {
      return `${date} - ${timeSlot}`;
    }
  };

  if (!canManage) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-800">Accesso Negato</h3>
        <p className="text-slate-600">Non hai l'autorizzazione per visualizzare le installazioni fotovoltaico.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-6 rounded-xl text-white">
        <div className="flex items-center space-x-3">
          <Settings className="h-8 w-8" />
          <div>
            <h2 className="text-xl font-semibold">Installazioni Fotovoltaico</h2>
            <p className="text-yellow-100">Gestione squadre e programmazione installazioni con alert automatici</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCurrentView('calendar')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              currentView === 'calendar' 
                ? 'bg-orange-600 text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Calendar className="h-4 w-4" />
            <span>Calendario</span>
          </button>
          
          <button
            onClick={() => setCurrentView('teams')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              currentView === 'teams' 
                ? 'bg-orange-600 text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Users className="h-4 w-4" />
            <span>Gestione Squadre</span>
          </button>
          
          <button
            onClick={() => setCurrentView('installations')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              currentView === 'installations' 
                ? 'bg-orange-600 text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Settings className="h-4 w-4" />
            <span>Lista Installazioni</span>
          </button>

          <button
            onClick={() => setCurrentView('planning')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              currentView === 'planning' 
                ? 'bg-orange-600 text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <MapPin className="h-4 w-4" />
            <span>🤖 Pianificazione AI</span>
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-green-800">{success}</span>
            </div>
            <button onClick={clearMessages} className="text-green-600 hover:text-green-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-red-800">{error}</span>
            </div>
            <button onClick={clearMessages} className="text-red-600 hover:text-red-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Calendar View */}
      {currentView === 'calendar' && (
        <div className="space-y-4">
          {/* Calendar Controls */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mese</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {Array.from({length: 12}, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2024, i, 1).toLocaleDateString('it-IT', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Anno</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value={2024}>2024</option>
                    <option value={2025}>2025</option>
                    <option value={2026}>2026</option>
                  </select>
                </div>
              </div>

              <button
                onClick={() => setShowNewInstallationForm(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                disabled={loading}
              >
                <Plus className="h-4 w-4" />
                <span>Nuova Installazione</span>
              </button>
            </div>
          </div>

          {/* Calendar */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">
                {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
              </h3>
              {loading && (
                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>

            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(day => (
                <div key={day} className="p-2 text-center font-medium text-slate-600 text-sm bg-slate-100 rounded">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays()}
            </div>

            <div className="mt-4 text-sm text-slate-600">
              <p><strong>Clicca su un giorno</strong> per vedere le installazioni programmate o aggiungerne una nuova.</p>
            </div>
          </div>
        </div>
      )}


      {/* Teams Management View */}
      {currentView === 'teams' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowNewTeamForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              disabled={loading}
            >
              <Plus className="h-4 w-4" />
              <span>Nuova Squadra</span>
            </button>
          </div>

          {/* Teams List */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
            <h3 className="text-lg font-semibold text-slate-800 mb-6">Squadre Fotovoltaico</h3>
            
            {solarTeams && Array.isArray(solarTeams) && solarTeams.length > 0 ? (
              <div className="space-y-4">
                {solarTeams.map((team) => (
                  <div key={team.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h4 className="font-medium text-slate-800">{team.name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            team.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {team.active ? 'Attiva' : 'Disattivata'}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="text-sm">
                            <span className="font-medium text-slate-700">Capo Squadra:</span>
                            <span className="ml-2 text-slate-600">{team.leader_name}</span>
                          </div>
                          
                          <div>
                            <span className="text-sm font-medium text-slate-700 mb-2 block">Membri:</span>
                            <div className="flex flex-wrap gap-2">
                              {team.members && team.members.map((member, index) => (
                                <div key={index} className={`px-3 py-1 rounded-full text-xs font-medium border ${
                                  member.qualification === 'Capo Squadra'
                                    ? 'bg-orange-100 text-orange-800 border-orange-200'
                                    : 'bg-slate-100 text-slate-700 border-slate-200'
                                }`}>
                                  {member.employee_name} ({member.qualification})
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => openEditTeam(team)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          title="Modifica squadra"
                          disabled={loading}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteTeam(team.id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Elimina squadra"
                          disabled={loading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-slate-600 mb-2">Nessuna Squadra</h4>
                <p className="text-slate-500">Crea la prima squadra per iniziare a programmare installazioni.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Installations List View */}
      {currentView === 'installations' && (
        <div className="space-y-4">
          <div className="bg-white/80 backdrop-blur-md rounded-xl p-6 shadow-sm border border-slate-200/50">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">Lista Installazioni</h3>
              {loading && (
                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>

            {installations && installations.length > 0 ? (
              <div className="space-y-4">
                {installations
                  .sort((a, b) => new Date(a.installation_date) - new Date(b.installation_date))
                  .map((installation) => (
                    <div key={installation.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <h4 className="font-medium text-slate-800">
                              {installation.client_first_name} {installation.client_last_name}
                            </h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              installation.status === 'programmata' 
                                ? 'bg-blue-100 text-blue-800' 
                                : installation.status === 'in_corso'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {installation.status === 'programmata' ? 'Programmata' : 
                               installation.status === 'in_corso' ? 'In Corso' : 'Completata'}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <Settings className="h-4 w-4" />
                                <span><strong>Tipologia:</strong> {installation.installation_type}</span>
                              </div>
                              <div className="flex items-center space-x-2 mb-1">
                                <MapPin className="h-4 w-4" />
                                <span><strong>Indirizzo:</strong> {installation.address}, {installation.city}</span>
                              </div>
                              <div className="flex items-center space-x-2 mb-1">
                                <User className="h-4 w-4" />
                                <span><strong>Telefono:</strong> {installation.phone}</span>
                              </div>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <Clock className="h-4 w-4" />
                                <span><strong>Data:</strong> {formatInstallationDateTime(installation.installation_date, installation.time_slot)}</span>
                              </div>
                              <div className="flex items-center space-x-2 mb-1">
                                <Users className="h-4 w-4" />
                                <span><strong>Squadra:</strong> {installation.team_name} ({installation.team_leader})</span>
                              </div>
                              {installation.notes && (
                                <div className="flex items-start space-x-2">
                                  <FileText className="h-4 w-4 mt-0.5" />
                                  <span><strong>Note:</strong> {installation.notes}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => openEditInstallation(installation)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Modifica installazione"
                            disabled={loading}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteInstallation(installation.id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Elimina installazione"
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-slate-600 mb-2">Nessuna Installazione</h4>
                <p className="text-slate-500">Non ci sono installazioni programmate.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Planning View */}
      {currentView === 'planning' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-slate-800 mb-2">🤖 Pianificazione Intelligente Installazioni</h3>
              <p className="text-slate-600">Inserisci una lista di città e il sistema calcolerà l'ordine ottimale partendo dalla più lontana</p>
            </div>

            {/* Input Form */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                🏙️ Lista Città (separate da virgola)
              </label>
              <textarea
                value={planningCities}
                onChange={(e) => setPlanningCities(e.target.value)}
                placeholder="Es: Milano, Napoli, Firenze, Bologna, Torino, Venezia"
                className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
                rows="3"
              />
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={runAIPlanning}
                  disabled={loadingPlanning || !planningCities.trim()}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    loadingPlanning || !planningCities.trim()
                      ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {loadingPlanning ? '⏳ Ottimizzazione in corso...' : '🚀 Ottimizza con AI'}
                </button>
                
                {planningResult && (
                  <button
                    onClick={() => {
                      setPlanningResult(null);
                      setPlanningCities('');
                    }}
                    className="px-4 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    Nuova Pianificazione
                  </button>
                )}
              </div>
              
              <div className="mt-4 text-xs text-slate-600 bg-white/50 rounded p-3">
                <strong>💡 Suggerimento:</strong> Inserisci qualsiasi città italiana! Il sistema usa geocoding automatico per trovare le coordinate.
                <br />
                <span className="text-slate-500">Esempi: Milano, Roma, Napoli, Torino, Bologna, Padova, Verona, Brescia, Modena, Parma...</span>
              </div>
            </div>

            {/* Results */}
            {planningResult && (
              <div className="space-y-4">
                {/* Explanation */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5">
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">🎯</div>
                    <div className="flex-1">
                      <h4 className="font-bold text-green-900 mb-2">Logica di Ottimizzazione</h4>
                      <p className="text-green-800">{planningResult.explanation}</p>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="text-blue-600 text-sm font-semibold mb-1">Città Totali</div>
                    <div className="text-2xl font-bold text-blue-900">{planningResult.total_cities}</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                    <div className="text-purple-600 text-sm font-semibold mb-1">Distanza Totale</div>
                    <div className="text-2xl font-bold text-purple-900">{planningResult.total_distance_km} km</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                    <div className="text-orange-600 text-sm font-semibold mb-1">Costo Totale</div>
                    <div className="text-2xl font-bold text-orange-900">€{planningResult.total_cost_estimate}</div>
                  </div>
                </div>

                {/* Teams Routes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(planningResult.teams).map(([teamName, teamData], teamIndex) => (
                    teamData.num_cities > 0 && (
                      <div key={teamIndex} className="bg-white border-2 border-slate-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-bold text-slate-800 flex items-center">
                            <span className="mr-2">👥</span> {teamName}
                          </h4>
                          <div className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
                            {teamData.num_cities} città
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                          <div className="bg-slate-50 p-2 rounded">
                            <div className="text-slate-600 text-xs">Distanza</div>
                            <div className="font-bold text-slate-800">{teamData.total_distance_km} km</div>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <div className="text-slate-600 text-xs">Costo</div>
                            <div className="font-bold text-slate-800">€{teamData.total_cost}</div>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          {teamData.cities.map((city, cityIndex) => (
                            <div key={cityIndex} className="flex items-start space-x-3 p-3 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                                {cityIndex + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-800 flex items-center gap-2">
                                  {city.city}
                                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">{city.zone}</span>
                                </div>
                                <div className="text-xs text-slate-600 mt-1">
                                  {cityIndex === 0 ? '🏢 Da Base' : `⬅️ Da ${teamData.cities[cityIndex-1].city}`}: 
                                  <span className="font-semibold ml-1">{city.distance_from_previous} km • €{city.cost_from_previous}</span>
                                </div>
                              </div>
                              {cityIndex === 0 && (
                                <div className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded">
                                  INIZIO
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* No Results State */}
            {!planningResult && !loadingPlanning && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🗺️</div>
                <h4 className="text-lg font-semibold text-slate-600 mb-2">Nessuna pianificazione</h4>
                <p className="text-slate-500">Inserisci le città sopra e clicca "Ottimizza con AI"</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Day Popup for Calendar */}
      {selectedDate && currentView === 'calendar' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-slate-800">
                  Installazioni del {new Date(selectedDate).toLocaleDateString('it-IT', { 
                    day: '2-digit', month: 'long', year: 'numeric' 
                  })}
                </h3>
                <button
                  onClick={() => setSelectedDate(null)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Day Installations */}
              <div className="space-y-3 mb-6">
                {getDayInstallations().length > 0 ? (
                  getDayInstallations().map((installation) => (
                    <div key={installation.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-800 mb-2">
                            {installation.client_first_name} {installation.client_last_name}
                          </h4>
                          <div className="text-sm text-slate-600 space-y-1">
                            <div><strong>Tipologia:</strong> {installation.installation_type}</div>
                            <div><strong>Città:</strong> {installation.city}</div>
                            <div><strong>Orario:</strong> {installation.time_slot}</div>
                            <div><strong>Squadra:</strong> {installation.team_name}</div>
                          </div>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            installation.status === 'programmata' 
                              ? 'bg-blue-100 text-blue-800' 
                              : installation.status === 'in_corso'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {installation.status === 'programmata' ? 'Programmata' : 
                             installation.status === 'in_corso' ? 'In Corso' : 'Completata'}
                          </span>
                          <div className="flex items-center space-x-1 ml-2">
                            <button
                              onClick={() => {
                                setSelectedDate(null);
                                openEditInstallation(installation);
                              }}
                              className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                              title="Modifica installazione"
                              disabled={loading}
                            >
                              <Edit className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedDate(null);
                                deleteInstallation(installation.id);
                              }}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                              title="Elimina installazione"
                              disabled={loading}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6">
                    <Calendar className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-500">Nessuna installazione programmata per questo giorno.</p>
                  </div>
                )}
              </div>

              {/* New Installation Button */}
              <div className="border-t pt-4">
                <button
                  onClick={() => {
                    setNewInstallation(prev => ({ ...prev, installation_date: selectedDate }));
                    setShowNewInstallationForm(true);
                    setSelectedDate(null);
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  disabled={loading}
                >
                  <Plus className="h-4 w-4" />
                  <span>Nuova Installazione per questo giorno</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Team Form */}
      {showNewTeamForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-slate-800">Nuova Squadra Fotovoltaico</h3>
                <button
                  onClick={() => {
                    setShowNewTeamForm(false);
                    setNewTeam({ name: '', leader_id: '', members: [] });
                    clearMessages();
                  }}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Team Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nome Squadra*</label>
                  <input
                    type="text"
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({...newTeam, name: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Es: Squadra 1"
                    maxLength="100"
                    required
                  />
                </div>

                {/* Team Leader */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Capo Squadra*</label>
                  <select
                    value={newTeam.leader_id}
                    onChange={(e) => setNewTeam({...newTeam, leader_id: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  >
                    <option value="">Seleziona capo squadra</option>
                    {solarEmployees && solarEmployees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.username}</option>
                    ))}
                  </select>
                </div>

                {/* Team Members */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-slate-700">Membri Squadra*</label>
                    <button
                      type="button"
                      onClick={addTeamMember}
                      className="flex items-center space-x-2 px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Aggiungi Membro</span>
                    </button>
                  </div>

                  <div className="space-y-3">
                    {newTeam.members && newTeam.members.map((member, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <select
                            value={member.employee_id}
                            onChange={(e) => updateTeamMember(index, 'employee_id', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            required
                          >
                            <option value="">Seleziona dipendente</option>
                            {solarEmployees && solarEmployees.map(emp => (
                              <option key={emp.id} value={emp.id}>{emp.username}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="w-40">
                          <select
                            value={member.qualification}
                            onChange={(e) => updateTeamMember(index, 'qualification', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            required
                          >
                            <option value="Installatore">Installatore</option>
                            <option value="Capo Squadra">Capo Squadra</option>
                          </select>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => removeTeamMember(index)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Rimuovi membro"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    
                    {(!newTeam.members || newTeam.members.length === 0) && (
                      <div className="text-center py-4 text-slate-500">
                        Nessun membro della squadra. Clicca "Aggiungi Membro" per iniziare.
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6">
                  <button
                    onClick={() => {
                      setShowNewTeamForm(false);
                      setNewTeam({ name: '', leader_id: '', members: [] });
                      clearMessages();
                    }}
                    className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                    disabled={loading}
                  >
                    Annulla
                  </button>
                  <button
                    onClick={createTeam}
                    className="flex items-center space-x-2 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                    <Save className="h-4 w-4" />
                    <span>{loading ? 'Salvando...' : 'Crea Squadra'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Form */}
      {showEditTeamForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-slate-800">Modifica Squadra Fotovoltaico</h3>
                <button
                  onClick={cancelEditTeam}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Team Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nome Squadra*</label>
                  <input
                    type="text"
                    placeholder="Es: Squadra Alpha"
                    value={editTeam.name}
                    onChange={(e) => setEditTeam({...editTeam, name: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Team Leader */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Capo Squadra*</label>
                  <select
                    value={editTeam.leader_id}
                    onChange={(e) => setEditTeam({...editTeam, leader_id: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  >
                    <option value="">Seleziona capo squadra</option>
                    {solarEmployees && solarEmployees.map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employee.username}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Team Members */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-slate-700">Membri Squadra*</label>
                    <button
                      type="button"
                      onClick={addEditTeamMember}
                      className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Aggiungi Membro</span>
                    </button>
                  </div>

                  <div className="space-y-3">
                    {editTeam.members.map((member, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg">
                        <div className="flex-1">
                          <select
                            value={member.employee_id}
                            onChange={(e) => updateEditTeamMember(index, 'employee_id', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            required
                          >
                            <option value="">Seleziona dipendente</option>
                            {solarEmployees && solarEmployees.map(employee => (
                              <option key={employee.id} value={employee.id}>
                                {employee.username}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="w-40">
                          <select
                            value={member.qualification}
                            onChange={(e) => updateEditTeamMember(index, 'qualification', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            required
                          >
                            <option value="Capo Squadra">Capo Squadra</option>
                            <option value="Installatore">Installatore</option>
                          </select>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => removeEditTeamMember(index)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          disabled={editTeam.members.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={cancelEditTeam}
                  className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  disabled={loading}
                >
                  Annulla
                </button>
                <button
                  onClick={updateTeam}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <Save className="h-4 w-4" />
                  <span>{loading ? 'Salvando...' : 'Aggiorna Squadra'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Installation Form */}
      {showNewInstallationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-slate-800">Nuova Installazione Fotovoltaico</h3>
                <button
                  onClick={() => {
                    setShowNewInstallationForm(false);
                    setNewInstallation({
                      client_first_name: '', client_last_name: '', installation_type: '',
                      address: '', city: '', phone: '', time_slot: 'Mattina',
                      installation_date: '', team_id: '', notes: ''
                    });
                    clearMessages();
                  }}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Cliente */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nome Cliente*</label>
                  <input
                    type="text"
                    value={newInstallation.client_first_name}
                    onChange={(e) => setNewInstallation({...newInstallation, client_first_name: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Nome"
                    maxLength="100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Cognome Cliente*</label>
                  <input
                    type="text"
                    value={newInstallation.client_last_name}
                    onChange={(e) => setNewInstallation({...newInstallation, client_last_name: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Cognome"
                    maxLength="100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tipologia Impianto*</label>
                  <input
                    type="text"
                    value={newInstallation.installation_type}
                    onChange={(e) => setNewInstallation({...newInstallation, installation_type: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Es: Residenziale 6kW"
                    maxLength="100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Recapito Telefonico*</label>
                  <input
                    type="tel"
                    value={newInstallation.phone}
                    onChange={(e) => setNewInstallation({...newInstallation, phone: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Es: +39 123 456 7890"
                    maxLength="20"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Indirizzo*</label>
                  <input
                    type="text"
                    value={newInstallation.address}
                    onChange={(e) => setNewInstallation({...newInstallation, address: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Via Roma 123"
                    maxLength="300"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Città*</label>
                  <input
                    type="text"
                    value={newInstallation.city}
                    onChange={(e) => setNewInstallation({...newInstallation, city: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Milano"
                    maxLength="100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Orario Installazione*</label>
                  <input
                    type="time"
                    value={newInstallation.time_slot}
                    onChange={(e) => setNewInstallation({...newInstallation, time_slot: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Data Installazione*</label>
                  <input
                    type="date"
                    value={newInstallation.installation_date}
                    onChange={(e) => setNewInstallation({...newInstallation, installation_date: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Squadra*</label>
                  <select
                    value={newInstallation.team_id}
                    onChange={(e) => setNewInstallation({...newInstallation, team_id: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  >
                    <option value="">Seleziona squadra</option>
                    {solarTeams && Array.isArray(solarTeams) && solarTeams.filter(team => team.active).map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.leader_name})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Note</label>
                  <textarea
                    value={newInstallation.notes}
                    onChange={(e) => setNewInstallation({...newInstallation, notes: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Note aggiuntive sull'installazione"
                    rows="3"
                    maxLength="500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => {
                    setShowNewInstallationForm(false);
                    setNewInstallation({
                      client_first_name: '', client_last_name: '', installation_type: '',
                      address: '', city: '', phone: '', time_slot: 'Mattina',
                      installation_date: '', team_id: '', notes: ''
                    });
                    clearMessages();
                  }}
                  className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  disabled={loading}
                >
                  Annulla
                </button>
                <button
                  onClick={createInstallation}
                  className="flex items-center space-x-2 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <Save className="h-4 w-4" />
                  <span>{loading ? 'Salvando...' : 'Crea Installazione'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Installation Form */}
      {showEditInstallationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-slate-800">Modifica Installazione Fotovoltaico</h3>
                <button
                  onClick={cancelEditInstallation}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome Cliente */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nome Cliente*</label>
                  <input
                    type="text"
                    placeholder="Nome"
                    value={editInstallation.client_first_name}
                    onChange={(e) => setEditInstallation({...editInstallation, client_first_name: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Cognome Cliente */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Cognome Cliente*</label>
                  <input
                    type="text"
                    placeholder="Cognome"
                    value={editInstallation.client_last_name}
                    onChange={(e) => setEditInstallation({...editInstallation, client_last_name: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Tipologia Impianto */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tipologia Impianto*</label>
                  <input
                    type="text"
                    placeholder="Es: Residenziale 6kW"
                    value={editInstallation.installation_type}
                    onChange={(e) => setEditInstallation({...editInstallation, installation_type: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Recapito Telefonico */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Recapito Telefonico*</label>
                  <input
                    type="tel"
                    placeholder="Es: +39 123 456 7890"
                    value={editInstallation.phone}
                    onChange={(e) => setEditInstallation({...editInstallation, phone: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Indirizzo */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Indirizzo*</label>
                  <input
                    type="text"
                    placeholder="Via Roma 123"
                    value={editInstallation.address}
                    onChange={(e) => setEditInstallation({...editInstallation, address: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Città */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Città*</label>
                  <input
                    type="text"
                    placeholder="Milano"
                    value={editInstallation.city}
                    onChange={(e) => setEditInstallation({...editInstallation, city: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Orario Installazione */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Orario Installazione*</label>
                  <input
                    type="time"
                    value={editInstallation.time_slot}
                    onChange={(e) => setEditInstallation({...editInstallation, time_slot: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Data Installazione */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Data Installazione*</label>
                  <input
                    type="date"
                    value={editInstallation.installation_date}
                    onChange={(e) => setEditInstallation({...editInstallation, installation_date: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Squadra */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Squadra*</label>
                  <select
                    value={editInstallation.team_id}
                    onChange={(e) => setEditInstallation({...editInstallation, team_id: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  >
                    <option value="">Seleziona squadra</option>
                    {solarTeams && Array.isArray(solarTeams) && solarTeams.filter(team => team.active).map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.leader_name})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Note</label>
                  <textarea
                    value={editInstallation.notes}
                    onChange={(e) => setEditInstallation({...editInstallation, notes: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Note aggiuntive sull'installazione"
                    rows="3"
                    maxLength="500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={cancelEditInstallation}
                  className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  disabled={loading}
                >
                  Annulla
                </button>
                <button
                  onClick={updateInstallation}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <Save className="h-4 w-4" />
                  <span>{loading ? 'Salvando...' : 'Aggiorna Installazione'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Info */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <Mail className="h-5 w-5 text-orange-600" />
          <span className="text-sm text-orange-800">
            <strong>Alert Automatici:</strong> I Capi Squadra riceveranno email di promemoria 24 ore prima e la mattina alle 06:00 (fuso Roma) del giorno dell'installazione.
          </span>
        </div>
      </div>
    </div>
  );
};

export default App;